import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Clock, Edit3, Save, BarChart3, FileText, Plus, Search, Filter, Download,
  Eye, CheckCircle2, XCircle, User, Building2, ChevronLeft,
  X, FileSpreadsheet, Trash2, Image, ArrowLeft, Upload, AlertCircle, Briefcase,
  DollarSign, Edit2, Calendar, UserCheck, ChevronDown, ChevronUp,
  ArrowUpDown, ArrowUp, ArrowDown, ChevronRight
} from 'lucide-react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import BASE_URL from "../../url";
import Swal from 'sweetalert2';
import '../styles/EmployeeDetails.css';

// Helper function to safely format dates for payload (accepts YYYY-MM-DD, MM/DD/YYYY, etc.)
const parseDateForPayload = (val) => {
  if (!val || String(val).trim() === '') return null;
  const str = String(val).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const mdy = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (mdy) {
    return `${mdy[3]}-${mdy[1].padStart(2, '0')}-${mdy[2].padStart(2, '0')}`;
  }
  const d = new Date(str);
  if (!isNaN(d.getTime())) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
  return str;
};

// Helper function to format date consistently as MM/DD/YYYY (month/date/year)
const formatToMonthDateYear = (dateVal) => {
  if (!dateVal) return '';
  const str = String(dateVal).trim();
  const isoMatch = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (isoMatch) {
    const [, yyyy, mm, dd] = isoMatch;
    return `${mm.padStart(2, '0')}/${dd.padStart(2, '0')}/${yyyy}`;
  }
  const mdyMatch = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
  if (mdyMatch) {
    const [, mm, dd, yyyy] = mdyMatch;
    return `${mm.padStart(2, '0')}/${dd.padStart(2, '0')}/${yyyy}`;
  }
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return str;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

// Typable and Selectable DatePicker component
const TypableDatePicker = ({ value, onChange, disabled, placeholder = "YYYY-MM-DD or MM/DD/YYYY" }) => {
  const hiddenDateRef = useRef(null);

  const handlePickerChange = (e) => {
    if (e.target.value) {
      onChange(e.target.value);
    }
  };

  const openCalendar = () => {
    if (disabled) return;
    if (hiddenDateRef.current) {
      if (typeof hiddenDateRef.current.showPicker === 'function') {
        hiddenDateRef.current.showPicker();
      } else {
        hiddenDateRef.current.focus();
      }
    }
  };

  const getPickerValue = (val) => {
    if (!val) return '';
    const str = String(val).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    const mdy = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (mdy) {
      return `${mdy[3]}-${mdy[1].padStart(2, '0')}-${mdy[2].padStart(2, '0')}`;
    }
    const d = new Date(str);
    if (!isNaN(d.getTime())) {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }
    return '';
  };

  return (
    <div style={{
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      width: '100%'
    }}>
      <input
        type="text"
        disabled={disabled}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          padding: '6px 32px 6px 8px',
          borderRadius: '6px',
          border: '1px solid #cbd5e1',
          fontSize: '0.82rem',
          color: '#0f172a',
          outline: 'none',
          background: disabled ? '#f1f5f9' : '#ffffff',
          cursor: disabled ? 'not-allowed' : 'text',
          width: '100%',
          boxSizing: 'border-box'
        }}
      />
      <input
        ref={hiddenDateRef}
        type="date"
        disabled={disabled}
        tabIndex={-1}
        value={getPickerValue(value)}
        onChange={handlePickerChange}
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          width: '32px',
          height: '100%',
          opacity: 0,
          cursor: disabled ? 'not-allowed' : 'pointer',
          zIndex: 1
        }}
      />
      <button
        type="button"
        disabled={disabled}
        onClick={openCalendar}
        tabIndex={-1}
        style={{
          position: 'absolute',
          right: '8px',
          background: 'none',
          border: 'none',
          padding: 0,
          color: disabled ? '#94a3b8' : '#3b5bfd',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2,
          pointerEvents: 'none'
        }}
        title="Select date from calendar"
      >
        <Calendar size={15} />
      </button>
    </div>
  );
};

// Helper function to format date as DD MMM YYYY (e.g. 25 Aug 2026 or 15 Sep 2026)
const formatDeploymentDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

// Helper function to format month and year
const formatMonthYear = (dateString) => {
  if (!dateString) return '';
  return formatDeploymentDate(dateString);
};

// Helper function to get file icon
const getFileIcon = (fileName) => {
  const extension = fileName.split('.').pop().toLowerCase();
  switch (extension) {
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
    case 'svg':
      return Image;
    case 'xlsx':
    case 'xls':
    case 'csv':
      return FileSpreadsheet;
    default:
      return FileText;
  }
};


const STATES_BY_COUNTRY = {
  'United States': [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ],
  'Canada': [
    'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador',
    'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island',
    'Quebec', 'Saskatchewan', 'Yukon'
  ],
  'Australia': [
    'New South Wales', 'Queensland', 'South Australia', 'Tasmania', 'Victoria',
    'Western Australia', 'Australian Capital Territory', 'Northern Territory'
  ],
  'India': [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
  ],
  'United Kingdom': [
    'England', 'Scotland', 'Wales', 'Northern Ireland'
  ],
  'Germany': [
    'Baden-Württemberg', 'Bavaria', 'Berlin', 'Brandenburg', 'Bremen',
    'Hamburg', 'Hesse', 'Lower Saxony', 'Mecklenburg-Vorpommern', 'North Rhine-Westphalia',
    'Rhineland-Palatinate', 'Saarland', 'Saxony', 'Saxony-Anhalt', 'Schleswig-Holstein', 'Thuringia'
  ],
  'France': [
    'Île-de-France', 'Provence-Alpes-Côte d\'Azur', 'Auvergne-Rhône-Alpes',
    'Nouvelle-Aquitaine', 'Occitanie', 'Bourgogne-Franche-Comté', 'Brittany',
    'Normandy', 'Hauts-de-France', 'Grand Est', 'Centre-Val de Loire', 'Pays de la Loire'
  ],
  'Japan': [
    'Aichi', 'Akita', 'Aomori', 'Chiba', 'Ehime', 'Fukui', 'Fukuoka', 'Fukushima',
    'Gifu', 'Gunma', 'Hiroshima', 'Hokkaido', 'Hyogo', 'Ibaraki', 'Ishikawa', 'Iwate',
    'Kagawa', 'Kagoshima', 'Kanagawa', 'Kochi', 'Kumamoto', 'Kyoto', 'Mie', 'Miyagi',
    'Miyazaki', 'Nagano', 'Nagasaki', 'Nara', 'Niigata', 'Okinawa', 'Osaka', 'Saga',
    'Saitama', 'Shiga', 'Shimane', 'Shizuoka', 'Tochigi', 'Tokushima', 'Tokyo', 'Tottori',
    'Toyama', 'Wakayama', 'Yamagata', 'Yamaguchi', 'Yamanashi'
  ]
};

const CountryStateFieldForDetails = ({ value, onChange, isEditing }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Create a flat list of all states with their countries
  const allStatesWithCountry = [];
  Object.entries(STATES_BY_COUNTRY).forEach(([country, states]) => {
    states.forEach(state => {
      allStatesWithCountry.push({
        label: `${state} (${country})`,
        value: state,
        country: country,
        state: state
      });
    });
  });

  // Filter based on search term
  const filteredStates = allStatesWithCountry.filter(item =>
    item.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleStateSelect = (item) => {
    onChange(item.value);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Get the country for the selected state
  const getCountryForState = (stateValue) => {
    if (!stateValue) return '';
    const item = allStatesWithCountry.find(s => s.value === stateValue);
    return item ? item.country : '';
  };

  const selectedDisplay = value ? `${value} (${getCountryForState(value)})` : '';

  if (!isEditing) {
    return (
      <span>{value || 'N/A'}</span>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        value={searchTerm || selectedDisplay}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={() => setIsOpen(true)}
        placeholder="Search states/provinces..."
        style={{
          width: '100%',
          padding: '8px 12px',
          border: isOpen ? '2px solid #10b981' : '1px solid #d1d5db',
          borderRadius: '4px',
          fontSize: '14px',
          boxSizing: 'border-box',
          fontFamily: 'inherit'
        }}
      />

      {/* Dropdown List */}
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: '#fff',
          border: '1px solid #d1d5db',
          borderTop: 'none',
          borderRadius: '0 0 4px 4px',
          maxHeight: '250px',
          overflowY: 'auto',
          zIndex: 1000,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          {filteredStates.length > 0 ? (
            filteredStates.map((item, index) => (
              <div
                key={index}
                onClick={() => handleStateSelect(item)}
                style={{
                  padding: '10px 12px',
                  cursor: 'pointer',
                  backgroundColor: value === item.value ? '#e0f2fe' : '#fff',
                  borderBottom: '1px solid #f3f4f6',
                  fontSize: '14px',
                  color: value === item.value ? '#0369a1' : '#1f2937',
                  fontWeight: value === item.value ? '600' : '400',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (value !== item.value) {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (value !== item.value) {
                    e.currentTarget.style.backgroundColor = '#fff';
                  }
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{item.state}</span>
                  <span style={{
                    fontSize: '12px',
                    color: '#9ca3af',
                    marginLeft: '12px'
                  }}>
                    {item.country}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div style={{
              padding: '10px 12px',
              color: '#9ca3af',
              fontSize: '14px',
              textAlign: 'center'
            }}>
              No states found
            </div>
          )}
        </div>
      )}

      {/* Close dropdown when clicking outside */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999
          }}
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

const EmployeeDetailsPage = ({
  employeeId: propEmployeeId,
  passedEmployee: propPassedEmployee,
  passedCompany: propPassedCompany,
  isModal = false,
  onClose
} = {}) => {
  const { employeeId: paramEmployeeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const employeeId = propEmployeeId || paramEmployeeId;

  // Get employee and company from props or navigation state
  const passedEmployee = propPassedEmployee || location.state?.employee;
  const passedCompany = propPassedCompany || location.state?.company;

  const shouldHideTabs = useCallback((companyData) => {
    if (!companyData) return false;

    // Check if company name is "Prophecy Offshore"
    // Hide only externalTimesheets and statement, NOT report
    if (companyData.name === 'Prophecy Offshore' ||
      companyData.clientId === '25642740') {
      return true; // This still hides the tabs
    }
    return false;
  }, []);

  // View states
  const [activeView, setActiveView] = useState('employeeDetails');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [allEmployees, setAllEmployees] = useState([]);
  const [useMailingAddress, setUseMailingAddress] = useState(false);

  // Data states
  const [employee, setEmployee] = useState(passedEmployee || null);
  const [company, setCompany] = useState(passedCompany || null);
  const [editedEmployee, setEditedEmployee] = useState({});
  const [timesheets, setTimesheets] = useState([]);
  const [externalTimesheets, setExternalTimesheets] = useState([]);
  const [statements, setStatements] = useState([]);
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState({});

  const [editingStatementId, setEditingStatementId] = useState(null);
  const [editingStatement, setEditingStatement] = useState({});
  const [externalSearchTerm, setExternalSearchTerm] = useState('');
  const [externalFilterStatus, setExternalFilterStatus] = useState('all');
  const [externalFilterPeriod, setExternalFilterPeriod] = useState('all');

  const [permissionStatus, setPermissionStatus] = useState({
    hasPermission: false,
    allowEditing: false
  });

  const [statementFileUpload, setStatementFileUpload] = useState({
    isUploading: false,
    progress: 0,
    selectedFile: null
  });

  const [statementSearchTerm, setStatementSearchTerm] = useState('');
  const [statementFilterStartDate, setStatementFilterStartDate] = useState('');
  const [statementFilterEndDate, setStatementFilterEndDate] = useState('');

  // Payroll Statement sorting, bulk selection, and pagination states
  const [statementSortConfig, setStatementSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  const [selectedStatementIds, setSelectedStatementIds] = useState([]);
  const [statementCurrentPage, setStatementCurrentPage] = useState(1);
  const [statementRowsPerPage, setStatementRowsPerPage] = useState(10);


  // Pay Structure & Statement Placement states
  const [statementPlacement, setStatementPlacement] = useState(null);
  const [showPayStructureModal, setShowPayStructureModal] = useState(false);
  const [payStructureData, setPayStructureData] = useState({
    lca: 113776,
    vendorRate: 80,
    consultantRate: 80,
    adpRate: 57.46,
    biweeklyHours: 80,
    perdiemAmount: 1600,
    payrollMode: 'Payroll & Perdiem mode',
    percentageSplit: 80
  });
  const [isEditingPayStructure, setIsEditingPayStructure] = useState(false);
  const [payrollInfo, setPayrollInfo] = useState({
    payType: 'Hourly',
    basisOfPay: 'Same as Pay Type',
    paySchedule: 'Monthly',
    employmentType: 'Full-time',  // 👈 ADD THIS LINE
    seasonalEmployee: 'No',
    ownerOfficer: 'No',
    semiMonthlySalary: 0,
    hourlyRate: 0
  });
  const [isEditingPayroll, setIsEditingPayroll] = useState(false);
  const [payrollLoading, setPayrollLoading] = useState(false);

  // Work Deployment History states
  const [workExperience, setWorkExperience] = useState([]);
  const [deployments, setDeployments] = useState([]);
  const [isEditingWork, setIsEditingWork] = useState(false);
  const [workFormData, setWorkFormData] = useState([]);
  const [loadingWork, setLoadingWork] = useState(false);
  const [savingWork, setSavingWork] = useState(false);

  // Rate & Referral Inline Edit States (matching OnboardingDetailDrawer / Image 1)
  const [editingRateDepId, setEditingRateDepId] = useState(null);
  const [savingRates, setSavingRates] = useState(false);
  const [crmCompanies, setCrmCompanies] = useState([]);
  const [showReferralDropdown, setShowReferralDropdown] = useState(false);
  const [rateReferralSearchText, setRateReferralSearchText] = useState('');
  const [showRateReferralDropdown, setShowRateReferralDropdown] = useState(false);
  const [expandedDescDepIds, setExpandedDescDepIds] = useState([]);

  const toggleExpandDesc = (depId) => {
    setExpandedDescDepIds(prev =>
      prev.includes(depId) ? prev.filter(id => id !== depId) : [...prev, depId]
    );
  };

  const isC2CType = (empType) => {
    if (!empType) return false;
    const upper = String(empType).toUpperCase();
    return upper.includes('C2C') || upper.includes('CORP');
  };

  const calculateMargin = (rateAfterVms, supplierRateAfterVms, actualPayRate, empType, referrals, totalReferralFee, expensesVal) => {
    const isW2 = !isC2CType(empType);
    const rAfter = parseFloat(rateAfterVms) || 0;

    let totalRef = 0;
    if (Array.isArray(referrals) && referrals.length > 0) {
      totalRef = referrals.reduce((sum, r) => sum + (parseFloat(r.ReferralFee ?? r.referralFee) || 0), 0);
    } else if (totalReferralFee) {
      totalRef = parseFloat(totalReferralFee) || 0;
    }

    const exp = parseFloat(expensesVal) || 0;

    if (isW2) {
      const pay = parseFloat(actualPayRate) || 0;
      if (rAfter > 0) {
        return (rAfter - pay - exp - totalRef).toFixed(2);
      }
    } else {
      const supAfter = parseFloat(supplierRateAfterVms) || 0;
      if (rAfter > 0) {
        return (rAfter - supAfter - exp - totalRef).toFixed(2);
      }
    }
    return null;
  };

  const [rateFormData, setRateFormData] = useState({
    clientRate: '',
    vmsPct: '',
    rateAfterVms: '',
    supplierRate: '',
    supplierVmsPct: '',
    supplierRateAfterVms: '',
    supplierNetTermsDays: '',
    compliancePayRate: '',
    actualPayRate: '',
    expenses: '',
    margin: '',
    referralSource: '',
    referralSources: [],
    referrals: [],
    referralFee: '',
    notes: ''
  });

  // Fetch CRM Companies (Clients and Suppliers) for Referral Source dropdown
  useEffect(() => {
    const fetchCrmCompanies = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${BASE_URL}/api/crm/companies`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (res.data?.success && Array.isArray(res.data.data)) {
          setCrmCompanies(res.data.data);
        }
      } catch (err) {
        console.error('Error fetching CRM companies for referral source:', err);
      }
    };
    fetchCrmCompanies();
  }, []);

  const getFilteredCrmCompanies = (searchTerm = '') => {
    if (!crmCompanies || crmCompanies.length === 0) return [];
    const query = String(searchTerm || '').trim().toLowerCase();

    const mapped = crmCompanies.map(c => {
      const name = c.DisplayName || c.CompanyName || c.CompanyCode || '';
      const sub = (c.CompanyName && c.CompanyName !== name) ? c.CompanyName : (c.IsClient ? 'Client' : (c.IsSupplier ? 'Supplier' : ''));
      return {
        id: c.CompanyId,
        displayName: name,
        companyName: sub,
        isClient: c.IsClient,
        isSupplier: c.IsSupplier
      };
    }).filter(c => c.displayName);

    if (!query) {
      return mapped.slice(0, 35);
    }

    return mapped
      .filter(c =>
        c.displayName.toLowerCase().includes(query) ||
        (c.companyName && c.companyName.toLowerCase().includes(query))
      )
      .slice(0, 35);
  };

  const handleStartEditRates = (dep) => {
    const depId = dep.deploymentId || dep.id;
    setEditingRateDepId(depId);
    setRateReferralSearchText('');
    setShowRateReferralDropdown(false);

    const empType = (dep.employmentType || dep.metadata?.employmentType || employee?.EmployeeType || 'W2').toUpperCase();
    const isW2 = !isC2CType(empType);

    const rawRef = dep.referralSource || '';
    const activeRefs = Array.isArray(dep.referrals) && dep.referrals.length > 0
      ? dep.referrals.map(r => ({
          submissionReferralId: r.SubmissionReferralId,
          referralCompanyId: r.ReferralCompanyId,
          displayName: r.DisplayName || r.CompanyName,
          companyName: r.CompanyName,
          referralFee: r.ReferralFee != null ? r.ReferralFee : '',
          referralNetTermsDays: r.ReferralNetTermsDays != null ? r.ReferralNetTermsDays : '',
          notes: r.Notes || ''
        }))
      : (rawRef ? rawRef.split(',').map(s => ({
          referralCompanyId: null,
          displayName: s.trim(),
          companyName: '',
          referralFee: dep.referralFee || '',
          referralNetTermsDays: '',
          notes: ''
        })).filter(r => r.displayName) : []);

    const totalRefFee = activeRefs.length > 0
      ? activeRefs.reduce((acc, r) => acc + (parseFloat(r.referralFee) || 0), 0)
      : (parseFloat(dep.referralFee) || 0);

    const sRateInit = dep.supplierRate != null ? dep.supplierRate : (dep.payRate != null ? dep.payRate : '');
    const svPctInit = dep.supplierVmsPct != null ? dep.supplierVmsPct : '';
    const sRateAfterInit = dep.supplierRateAfterVms != null
      ? dep.supplierRateAfterVms
      : (sRateInit && svPctInit ? (sRateInit * (1 - svPctInit / 100)).toFixed(2) : '');

    const computedM = dep.margin ?? calculateMargin(
      dep.rateAfterVms,
      sRateAfterInit,
      dep.actualPayRate ?? dep.payRate,
      empType,
      activeRefs,
      totalRefFee,
      dep.expenses != null ? dep.expenses : (dep.Expenses != null ? dep.Expenses : '')
    );

    setRateFormData({
      clientRate: dep.clientRate != null ? dep.clientRate : (dep.billRate != null ? dep.billRate : ''),
      vmsPct: dep.vmsPct != null ? dep.vmsPct : '',
      rateAfterVms: dep.rateAfterVms != null ? dep.rateAfterVms : '',
      supplierRate: sRateInit,
      supplierVmsPct: svPctInit,
      supplierRateAfterVms: sRateAfterInit,
      supplierNetTermsDays: dep.supplierNetTermsDays || '',
      compliancePayRate: dep.compliancePayRate != null ? dep.compliancePayRate : '',
      actualPayRate: dep.actualPayRate != null ? dep.actualPayRate : (isW2 ? (dep.payRate || '') : '0.00'),
      expenses: dep.expenses != null ? dep.expenses : (dep.Expenses != null ? dep.Expenses : ''),
      margin: computedM ?? '',
      referralSource: rawRef,
      referralSources: activeRefs.map(r => r.displayName),
      referrals: activeRefs,
      referralFee: totalRefFee > 0 ? totalRefFee.toFixed(2) : (dep.referralFee || ''),
      notes: dep.rateNotes || dep.notes || ''
    });
  };

  const handleCancelEditRates = () => {
    setEditingRateDepId(null);
    setRateReferralSearchText('');
    setShowRateReferralDropdown(false);
  };

  const handleAddReferralSource = (sourceItem) => {
    if (!sourceItem) return;
    const isObj = typeof sourceItem === 'object';
    const compName = isObj ? (sourceItem.displayName || sourceItem.companyName) : String(sourceItem).trim();
    if (!compName) return;

    setRateFormData(prev => {
      const currentList = prev.referrals || [];
      if (currentList.some(r => (r.displayName || '').toLowerCase() === compName.toLowerCase())) {
        return prev;
      }
      const newItem = {
        referralCompanyId: isObj ? sourceItem.id : null,
        displayName: compName,
        companyName: isObj ? sourceItem.companyName : '',
        referralFee: '',
        referralNetTermsDays: '',
        notes: ''
      };
      const updatedList = [...currentList, newItem];
      const totalRefFee = updatedList.reduce((acc, r) => acc + (parseFloat(r.referralFee) || 0), 0);
      return {
        ...prev,
        referrals: updatedList,
        referralSources: updatedList.map(r => r.displayName),
        referralSource: updatedList.map(r => r.displayName).join(', '),
        referralFee: totalRefFee > 0 ? totalRefFee.toFixed(2) : prev.referralFee
      };
    });
    setRateReferralSearchText('');
    setShowRateReferralDropdown(false);
  };

  const handleRemoveReferralSource = (indexToRemove) => {
    setRateFormData(prev => {
      const currentList = prev.referrals || [];
      const updatedList = currentList.filter((_, idx) => idx !== indexToRemove);
      const totalRefFee = updatedList.reduce((acc, r) => acc + (parseFloat(r.referralFee) || 0), 0);
      return {
        ...prev,
        referrals: updatedList,
        referralSources: updatedList.map(r => r.displayName),
        referralSource: updatedList.map(r => r.displayName).join(', '),
        referralFee: totalRefFee > 0 ? totalRefFee.toFixed(2) : ''
      };
    });
  };

  const handleReferralItemChange = (index, field, value) => {
    setRateFormData(prev => {
      const currentList = [...(prev.referrals || [])];
      if (!currentList[index]) return prev;
      currentList[index] = { ...currentList[index], [field]: value };
      const totalRefFee = currentList.reduce((acc, r) => acc + (parseFloat(r.referralFee) || 0), 0);
      return {
        ...prev,
        referrals: currentList,
        referralFee: totalRefFee > 0 ? totalRefFee.toFixed(2) : ''
      };
    });
  };

  const handleRateFieldChange = (field, value, dep) => {
    setRateFormData(prev => {
      const updated = { ...prev, [field]: value };
      const empTypeRaw = dep?.employmentType || dep?.metadata?.employmentType || employee?.EmployeeType || 'W2';
      const empType = empTypeRaw.toUpperCase();

      // 1. Client Rate & VMS calculations
      if (field === 'clientRate' || field === 'vmsPct') {
        const cr = parseFloat(field === 'clientRate' ? value : updated.clientRate);
        const vms = parseFloat(field === 'vmsPct' ? value : updated.vmsPct);
        if (!isNaN(cr) && cr > 0) {
          if (!isNaN(vms) && vms > 0) {
            updated.rateAfterVms = (cr * (1 - vms / 100)).toFixed(2);
          } else if (vms === 0 || value === '' || updated.vmsPct === '') {
            updated.rateAfterVms = cr.toFixed(2);
          }
        } else if (value === '' && field === 'clientRate') {
          updated.rateAfterVms = '';
        }
      } else if (field === 'rateAfterVms') {
        const cr = parseFloat(updated.clientRate);
        const rAfter = parseFloat(value);
        if (!isNaN(cr) && cr > 0 && !isNaN(rAfter) && rAfter >= 0 && rAfter <= cr) {
          const computedVms = ((cr - rAfter) / cr) * 100;
          updated.vmsPct = parseFloat(computedVms.toFixed(2)).toString();
        }
      }

      // 2. Supplier Rate & Supplier VMS calculations
      if (field === 'supplierRate' || field === 'supplierVmsPct') {
        const sr = parseFloat(field === 'supplierRate' ? value : updated.supplierRate);
        const sv = parseFloat(field === 'supplierVmsPct' ? value : updated.supplierVmsPct);
        if (!isNaN(sr) && sr > 0) {
          if (!isNaN(sv) && sv > 0) {
            updated.supplierRateAfterVms = (sr * (1 - sv / 100)).toFixed(2);
          } else if (sv === 0 || value === '' || updated.supplierVmsPct === '') {
            updated.supplierRateAfterVms = sr.toFixed(2);
          }
        } else if (value === '' && field === 'supplierRate') {
          updated.supplierRateAfterVms = '';
        }
      } else if (field === 'supplierRateAfterVms') {
        const sr = parseFloat(updated.supplierRate);
        const sAfter = parseFloat(value);
        if (!isNaN(sr) && sr > 0 && !isNaN(sAfter) && sAfter >= 0 && sAfter <= sr) {
          const computedSv = ((sr - sAfter) / sr) * 100;
          updated.supplierVmsPct = parseFloat(computedSv.toFixed(2)).toString();
        }
      }

      // Auto compute margin
      const totalRefFee = Array.isArray(updated.referrals) && updated.referrals.length > 0
        ? updated.referrals.reduce((sum, r) => sum + (parseFloat(r.referralFee) || 0), 0)
        : (parseFloat(updated.referralFee) || 0);

      const m = calculateMargin(
        updated.rateAfterVms,
        updated.supplierRateAfterVms,
        updated.actualPayRate,
        empType,
        updated.referrals,
        totalRefFee,
        updated.expenses
      );
      if (m != null) updated.margin = m;

      return updated;
    });
  };

  const handleSaveRates = async (dep) => {
    const depId = dep.deploymentId || dep.id;
    const targetEmpId = employee?.EmployeeId || employee?.EmployeeCode || employeeId;
    const cid = company?.id || passedCompany?.id || 1;

    setSavingRates(true);
    try {
      const token = localStorage.getItem('token');
      const refSourceString = (rateFormData.referralSources && rateFormData.referralSources.length > 0)
        ? rateFormData.referralSources.join(', ')
        : (rateFormData.referralSource || null);

      const payload = {
        placedJobTitle: dep.jobTitle || dep.placedJobTitle,
        startDate: dep.startDate ? String(dep.startDate).substring(0, 10) : null,
        endDate: dep.endDate ? String(dep.endDate).substring(0, 10) : null,
        status: dep.status || 'ACTIVE',
        clientRate: rateFormData.clientRate,
        vmsPct: rateFormData.vmsPct,
        rateAfterVms: rateFormData.rateAfterVms,
        supplierRate: rateFormData.supplierRate,
        supplierVmsPct: rateFormData.supplierVmsPct,
        supplierRateAfterVms: rateFormData.supplierRateAfterVms,
        supplierNetTermsDays: rateFormData.supplierNetTermsDays,
        compliancePayRate: rateFormData.compliancePayRate,
        actualPayRate: rateFormData.actualPayRate,
        expenses: rateFormData.expenses,
        margin: rateFormData.margin,
        referralSource: refSourceString,
        referralFee: rateFormData.referralFee,
        referrals: rateFormData.referrals,
        rateNotes: rateFormData.notes || '',
        notes: rateFormData.notes || '',
        description: dep.description || ''
      };

      const res = await axios.put(
        `${BASE_URL}/api/employees/company/${cid}/${targetEmpId}/deployments/${depId}`,
        payload,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (res.data?.success) {
        setEditingRateDepId(null);
        setRateReferralSearchText('');
        setShowRateReferralDropdown(false);
        Swal.fire({
          icon: 'success',
          title: 'Rates Saved!',
          text: 'Rate & Referral details updated successfully.',
          timer: 1600,
          showConfirmButton: false
        });
        await fetchWorkExperience();
      } else {
        throw new Error(res.data?.message || 'Failed to update rate details');
      }
    } catch (err) {
      console.error('Error saving rate details:', err);
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: err.response?.data?.message || err.message || 'Failed to save rate details.'
      });
    } finally {
      setSavingRates(false);
    }
  };

  // Left Side Deployment Inline Edit States (Title, Dates, Status, Description)
  const [editingLeftDepId, setEditingLeftDepId] = useState(null);
  const [savingLeft, setSavingLeft] = useState(false);
  const [leftFormData, setLeftFormData] = useState({
    placedJobTitle: '',
    startDate: '',
    endDate: '',
    isCurrent: true,
    status: 'ACTIVE',
    notes: ''
  });

  const stripHtmlToPlainText = (html) => {
    if (!html) return '';
    let text = String(html)
      .replace(/<br\s*[\/]?>/gi, '\n')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<\/li>/gi, '\n')
      .replace(/<[^>]+>/g, '');

    text = text
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'");

    return text.replace(/\n{3,}/g, '\n\n').trim();
  };

  const handleStartEditLeft = (dep) => {
    const depId = dep.deploymentId || dep.id;
    setEditingLeftDepId(depId);
    const resolvedStartDate = dep.startDate ? String(dep.startDate).substring(0, 10) : '';
    const resolvedEndDate = dep.endDate ? String(dep.endDate).substring(0, 10) : '';
    setLeftFormData({
      placedJobTitle: dep.jobTitle || dep.placedJobTitle || '',
      startDate: resolvedStartDate,
      endDate: resolvedEndDate,
      isCurrent: Boolean(dep.isCurrent || !dep.endDate),
      status: dep.status || 'ACTIVE',
      notes: stripHtmlToPlainText(dep.description || dep.notes || '')
    });
  };

  const handleCancelEditLeft = () => {
    setEditingLeftDepId(null);
  };

  const handleLeftFieldChange = (field, value) => {
    setLeftFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveLeft = async (dep) => {
    const depId = dep.deploymentId || dep.id;
    const targetEmpId = employee?.EmployeeId || employee?.EmployeeCode || employeeId;
    const cid = company?.id || passedCompany?.id || 1;

    setSavingLeft(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        placedJobTitle: leftFormData.placedJobTitle,
        startDate: parseDateForPayload(leftFormData.startDate),
        endDate: leftFormData.isCurrent ? null : parseDateForPayload(leftFormData.endDate),
        status: leftFormData.status,
        clientRate: dep.clientRate != null ? dep.clientRate : dep.billRate,
        vmsPct: dep.vmsPct,
        rateAfterVms: dep.rateAfterVms,
        supplierRate: dep.supplierRate != null ? dep.supplierRate : dep.payRate,
        supplierVmsPct: dep.supplierVmsPct,
        compliancePayRate: dep.compliancePayRate,
        actualPayRate: dep.actualPayRate != null ? dep.actualPayRate : dep.payRate,
        referralSource: dep.referralSource,
        referralFee: dep.referralFee,
        description: leftFormData.notes,
        rateNotes: dep.rateNotes || dep.notes || '',
        notes: dep.rateNotes || dep.notes || ''
      };

      const res = await axios.put(
        `${BASE_URL}/api/employees/company/${cid}/${targetEmpId}/deployments/${depId}`,
        payload,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (res.data?.success) {
        setEditingLeftDepId(null);
        Swal.fire({
          icon: 'success',
          title: 'Deployment Saved!',
          text: 'Deployment details updated successfully.',
          timer: 1600,
          showConfirmButton: false
        });
        await fetchWorkExperience();
      } else {
        throw new Error(res.data?.message || 'Failed to update deployment');
      }
    } catch (err) {
      console.error('Error saving deployment details:', err);
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: err.response?.data?.message || err.message || 'Failed to save deployment details.'
      });
    } finally {
      setSavingLeft(false);
    }
  };

  // Full Deployment Modal States (Add & Edit Deployments)
  const [showDeploymentModal, setShowDeploymentModal] = useState(false);
  const [isEditingDeployment, setIsEditingDeployment] = useState(false);
  const [savingDeploymentModal, setSavingDeploymentModal] = useState(false);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showEndClientDropdown, setShowEndClientDropdown] = useState(false);
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [showModalReferralDropdown, setShowModalReferralDropdown] = useState(false);
  const [deploymentModalData, setDeploymentModalData] = useState({
    deploymentId: null,
    placedJobTitle: '',
    clientId: null,
    clientName: '',
    endClientId: null,
    endClientName: '',
    supplierId: null,
    supplierName: '',
    startDate: '',
    endDate: '',
    isCurrent: true,
    status: 'ACTIVE',
    clientRate: '',
    vmsPct: '',
    rateAfterVms: '',
    supplierRate: '',
    supplierVmsPct: '',
    compliancePayRate: '',
    actualPayRate: '',
    referralSource: '',
    referralFee: '',
    rateNotes: '',
    notes: ''
  });

  const handleOpenAddDeploymentModal = () => {
    setIsEditingDeployment(false);
    setDeploymentModalData({
      deploymentId: null,
      placedJobTitle: '',
      clientId: null,
      clientName: '',
      endClientId: null,
      endClientName: '',
      supplierId: null,
      supplierName: '',
      startDate: new Date().toISOString().substring(0, 10),
      endDate: '',
      isCurrent: true,
      status: 'ACTIVE',
      clientRate: '',
      vmsPct: '',
      rateAfterVms: '',
      supplierRate: '',
      supplierVmsPct: '',
      compliancePayRate: '',
      actualPayRate: '',
      referralSource: '',
      referralFee: '',
      rateNotes: '',
      notes: ''
    });
    setShowDeploymentModal(true);
  };

  const handleOpenEditDeploymentModal = (dep) => {
    setIsEditingDeployment(true);
    const endClientNode = dep.clientSide?.find(c => c.IsEndClient);
    const directClientNode = dep.clientSide?.find(c => !c.IsEndClient) || dep.clientSide?.[0];
    const supplierNode = dep.supplierSide?.[0];

    const resolvedStartDate = dep.startDate ? String(dep.startDate).substring(0, 10) : '';
    const resolvedEndDate = dep.endDate ? String(dep.endDate).substring(0, 10) : '';

    setDeploymentModalData({
      deploymentId: dep.deploymentId || dep.id,
      placedJobTitle: dep.jobTitle || dep.placedJobTitle || '',
      clientId: directClientNode?.CompanyId || dep.clientId || null,
      clientName: directClientNode?.DisplayName || directClientNode?.CompanyName || dep.clientName || dep.company || '',
      endClientId: endClientNode?.CompanyId || null,
      endClientName: endClientNode ? (endClientNode.DisplayName || endClientNode.CompanyName || '') : '',
      supplierId: supplierNode?.CompanyId || null,
      supplierName: supplierNode ? (supplierNode.DisplayName || supplierNode.CompanyName || '') : '',
      startDate: resolvedStartDate,
      endDate: resolvedEndDate,
      isCurrent: Boolean(dep.isCurrent || !dep.endDate),
      status: dep.status || 'ACTIVE',
      clientRate: dep.clientRate != null ? dep.clientRate : (dep.billRate != null ? dep.billRate : ''),
      vmsPct: dep.vmsPct != null ? dep.vmsPct : '',
      rateAfterVms: dep.rateAfterVms != null ? dep.rateAfterVms : '',
      supplierRate: dep.supplierRate != null ? dep.supplierRate : (dep.payRate != null ? dep.payRate : ''),
      supplierVmsPct: dep.supplierVmsPct != null ? dep.supplierVmsPct : '',
      compliancePayRate: dep.compliancePayRate != null ? dep.compliancePayRate : '',
      actualPayRate: dep.actualPayRate != null ? dep.actualPayRate : (dep.payRate != null ? dep.payRate : ''),
      referralSource: dep.referralSource || '',
      referralFee: dep.referralFee != null ? dep.referralFee : '',
      rateNotes: dep.rateNotes || '',
      notes: stripHtmlToPlainText(dep.description || dep.notes || '')
    });
    setShowDeploymentModal(true);
  };

  const handleDeploymentModalChange = (field, value) => {
    setDeploymentModalData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'clientRate' || field === 'vmsPct') {
        const cr = parseFloat(field === 'clientRate' ? value : updated.clientRate);
        const vms = parseFloat(field === 'vmsPct' ? value : updated.vmsPct);
        if (!isNaN(cr)) {
          const discount = !isNaN(vms) ? (cr * (vms / 100)) : 0;
          updated.rateAfterVms = (cr - discount).toFixed(2);
        }
      }
      return updated;
    });
  };

  const handleSaveDeploymentModal = async (e) => {
    if (e) e.preventDefault();
    if (!deploymentModalData.placedJobTitle || !deploymentModalData.placedJobTitle.trim()) {
      Swal.fire({ icon: 'warning', title: 'Job Title Required', text: 'Please provide a job title / role name for this deployment.' });
      return;
    }
    if (!deploymentModalData.clientName || !deploymentModalData.clientName.trim()) {
      Swal.fire({ icon: 'warning', title: 'Client Required', text: 'Please select or enter the Direct Client name.' });
      return;
    }
    if (!deploymentModalData.startDate) {
      Swal.fire({ icon: 'warning', title: 'Start Date Required', text: 'Please select or enter a valid start date.' });
      return;
    }

    const targetEmpId = employee?.EmployeeId || employee?.EmployeeCode || employeeId;
    const cid = company?.id || passedCompany?.id || 1;
    setSavingDeploymentModal(true);

    try {
      const token = localStorage.getItem('token');
      const payload = {
        placedJobTitle: deploymentModalData.placedJobTitle.trim(),
        clientId: deploymentModalData.clientId,
        clientName: deploymentModalData.clientName.trim(),
        endClientId: deploymentModalData.endClientId,
        endClientName: deploymentModalData.endClientName ? deploymentModalData.endClientName.trim() : null,
        supplierId: deploymentModalData.supplierId,
        supplierName: deploymentModalData.supplierName ? deploymentModalData.supplierName.trim() : null,
        startDate: parseDateForPayload(deploymentModalData.startDate),
        endDate: deploymentModalData.isCurrent ? null : parseDateForPayload(deploymentModalData.endDate),
        status: deploymentModalData.status || 'ACTIVE',
        clientRate: deploymentModalData.clientRate,
        vmsPct: deploymentModalData.vmsPct,
        rateAfterVms: deploymentModalData.rateAfterVms,
        supplierRate: deploymentModalData.supplierRate,
        supplierVmsPct: deploymentModalData.supplierVmsPct,
        compliancePayRate: deploymentModalData.compliancePayRate,
        actualPayRate: deploymentModalData.actualPayRate,
        referralSource: deploymentModalData.referralSource ? deploymentModalData.referralSource.trim() : null,
        referralFee: deploymentModalData.referralFee,
        rateNotes: deploymentModalData.rateNotes || '',
        description: deploymentModalData.notes || '',
        notes: deploymentModalData.notes || ''
      };

      let res;
      if (isEditingDeployment && deploymentModalData.deploymentId) {
        res = await axios.put(
          `${BASE_URL}/api/employees/company/${cid}/${targetEmpId}/deployments/${deploymentModalData.deploymentId}`,
          payload,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } else {
        res = await axios.post(
          `${BASE_URL}/api/employees/company/${cid}/${targetEmpId}/deployments`,
          payload,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }

      if (res.data?.success) {
        setShowDeploymentModal(false);
        Swal.fire({
          icon: 'success',
          title: isEditingDeployment ? 'Deployment Updated!' : 'Deployment Created!',
          text: isEditingDeployment ? 'Deployment details updated successfully.' : 'New deployment added successfully.',
          timer: 1600,
          showConfirmButton: false
        });
        await fetchWorkExperience();
      } else {
        throw new Error(res.data?.message || 'Failed to save deployment');
      }
    } catch (err) {
      console.error('Error saving deployment:', err);
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: err.response?.data?.message || err.message || 'Failed to save deployment.'
      });
    } finally {
      setSavingDeploymentModal(false);
    }
  };

  const handleDeleteDeployment = async (dep) => {
    const depId = dep.deploymentId || dep.id;
    const targetEmpId = employee?.EmployeeId || employee?.EmployeeCode || employeeId;
    const cid = company?.id || passedCompany?.id || 1;

    const result = await Swal.fire({
      title: 'Delete Deployment?',
      text: `Are you sure you want to delete the deployment "${dep.jobTitle || 'Deployed Consultant'}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.delete(
          `${BASE_URL}/api/employees/company/${cid}/${targetEmpId}/deployments/${depId}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (res.data?.success) {
          Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: 'Deployment removed successfully.',
            timer: 1500,
            showConfirmButton: false
          });
          await fetchWorkExperience();
        } else {
          throw new Error(res.data?.message || 'Failed to delete deployment');
        }
      } catch (err) {
        console.error('Error deleting deployment:', err);
        Swal.fire({
          icon: 'error',
          title: 'Delete Failed',
          text: err.response?.data?.message || err.message || 'Failed to delete deployment.'
        });
      }
    }
  };

  const fetchWorkExperience = useCallback(async () => {
    const targetEmpId = employee?.EmployeeId || employee?.EmployeeCode || employeeId;
    const cid = company?.id || passedCompany?.id || 1;
    if (!targetEmpId) return;

    setLoadingWork(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${BASE_URL}/api/employees/company/${cid}/${targetEmpId}/deployments`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (res.data?.success) {
        const liveDeployments = res.data.data || [];
        const priorExp = res.data.priorExperience || [];
        setDeployments(liveDeployments);

        // Convert prior experience into editable format
        const formattedExp = priorExp.map(p => ({
          JobTitle: p.jobTitle || '',
          Company: p.company || '',
          StartDate: p.startDate ? p.startDate.substring(0, 10) : '',
          EndDate: p.endDate ? p.endDate.substring(0, 10) : '',
          IsCurrent: Boolean(p.isCurrent),
          Description: p.description || ''
        }));
        setWorkExperience(formattedExp);
        setWorkFormData(formattedExp);
      } else {
        setDeployments([]);
        setWorkExperience([]);
        setWorkFormData([]);
      }
    } catch (err) {
      console.error('Error loading deployments from backend:', err);
      // Fallback to localStorage if offline/network error
      try {
        const stored = localStorage.getItem(`work_deployment_history_${targetEmpId}`);
        if (stored) {
          const parsed = JSON.parse(stored);
          setWorkExperience(parsed);
          setWorkFormData(parsed);
        }
      } catch (e) { }
    } finally {
      setLoadingWork(false);
    }
  }, [employee, employeeId, company, passedCompany]);

  useEffect(() => {
    if (activeView === 'workDeploymentHistory') {
      fetchWorkExperience();
    }
  }, [activeView, fetchWorkExperience]);

  const handleSaveWorkExperience = async () => {
    const targetEmpId = employee?.EmployeeId || employee?.EmployeeCode || employeeId;
    const cid = company?.id || passedCompany?.id || 1;
    if (!targetEmpId) return;

    setSavingWork(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BASE_URL}/api/employees/company/${cid}/${targetEmpId}/work-experience`,
        { experiences: workFormData },
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      if (response.data?.success) {
        setWorkExperience(workFormData);
        setIsEditingWork(false);
        try {
          localStorage.setItem(`work_deployment_history_${targetEmpId}`, JSON.stringify(workFormData));
        } catch (e) { }
        Swal.fire({
          icon: 'success',
          title: 'Saved!',
          text: 'Work Deployment History updated successfully.',
          timer: 1800,
          showConfirmButton: false
        });
        fetchWorkExperience();
      } else {
        throw new Error(response.data?.message || 'Failed to save');
      }
    } catch (err) {
      console.error('Error saving work experience to database:', err);
      // Fallback local save
      try {
        localStorage.setItem(`work_deployment_history_${targetEmpId}`, JSON.stringify(workFormData));
        setWorkExperience(workFormData);
        setIsEditingWork(false);
      } catch (e) { }
      Swal.fire({
        icon: 'success',
        title: 'Saved',
        text: 'Work Deployment History updated.',
        timer: 1800,
        showConfirmButton: false
      });
    } finally {
      setSavingWork(false);
    }
  };


  useEffect(() => {
    if (showPayStructureModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showPayStructureModal]);

  useEffect(() => {
    // If user tries to access hidden tabs for Prophecy Offshore, redirect to employeeDetails
    if (shouldHideTabs(company) && ['externalTimesheets', 'statement'].includes(activeView)) {
      setActiveView('employeeDetails');
    }
  }, [company, activeView]);

  // Statement helper functions
  const handleEditStatement = (statement) => {
    setEditingStatementId(statement.Id);
    setEditingStatement({
      ...statement,
      CheckDate: statement.CheckDate ? new Date(statement.CheckDate).toISOString().split('T')[0] : '',
      Credit: parseFloat(statement.Credit) || 0,
      Debit: parseFloat(statement.Debit) || 0,
      Balance: parseFloat(statement.Balance) || 0,
      Hours: parseFloat(statement.Hours) || 0,
      PayRate: parseFloat(statement.PayRate) || 0
    });
  };

  const handleStatementFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      // Validate file type
      const allowedExtensions = ['.csv', '.xlsx', '.xls'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

      if (!allowedExtensions.includes(fileExtension)) {
        Swal.fire({
          title: 'Invalid File Type',
          text: 'Please upload a CSV or Excel file (.csv, .xlsx, .xls)',
          icon: 'error'
        });
        e.target.value = '';
        return;
      }

      // Validate file size (50MB limit)
      const MAX_FILE_SIZE = 50 * 1024 * 1024;
      if (file.size > MAX_FILE_SIZE) {
        Swal.fire({
          title: 'File Too Large',
          text: 'File size must be less than 50MB',
          icon: 'error'
        });
        e.target.value = '';
        return;
      }

      setStatementFileUpload({
        isUploading: true,
        progress: 0,
        selectedFile: file
      });

      // Create FormData
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');

      console.log('📤 Uploading payroll statement file:', file.name);
      console.log('Company ID:', company.id);
      console.log('Employee ID (numeric):', employee.Id);

      // Upload file
      const response = await axios.post(
        `${BASE_URL}/api/employees/company/${company.id}/${employee.Id}/statements/upload-file`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setStatementFileUpload(prev => ({
              ...prev,
              progress: percentCompleted
            }));
          }
        }
      );

      console.log('✅ File upload response:', response.data);

      if (response.data.success) {
        if (response.data.data?.payStructure) {
          setPayStructureData(prev => ({
            ...prev,
            ...response.data.data.payStructure
          }));
        }

        Swal.fire({
          title: 'Success!',
          html: `
          <div style="text-align: left;">
            <p><strong>File:</strong> ${file.name}</p>
            <p><strong>Total Rows:</strong> ${response.data.data.totalStatements}</p>
            <p><strong>Successfully Imported:</strong> <span style="color: #10b981;">${response.data.data.importedStatements}</span></p>
            ${response.data.data.payStructure ? `
              <p><strong>Pay Structure:</strong> <span style="color: #059669; font-weight: 600;">Imported from Sheet 2 (LCA: $${response.data.data.payStructure.lca.toLocaleString()}, Vendor Rate: $${response.data.data.payStructure.vendorRate}/hr)</span></p>
            ` : ''}
            ${response.data.data.failedStatements > 0 ? `
              <p><strong>Failed:</strong> <span style="color: #ef4444;">${response.data.data.failedStatements}</span></p>
            ` : ''}
            ${response.data.data.warnings && response.data.data.warnings.length > 0 ? `
              <div style="background: #fff3cd; padding: 12px; border-radius: 4px; margin-top: 12px; text-align: left;">
                <p style="margin: 0 0 8px 0; font-weight: bold; color: #856404;">⚠️ Warnings:</p>
                <ul style="margin: 0; padding-left: 20px; color: #856404;">
                  ${response.data.data.warnings.slice(0, 5).map(w => `<li>${w}</li>`).join('')}
                  ${response.data.data.warnings.length > 5 ? `<li>... and ${response.data.data.warnings.length - 5} more</li>` : ''}
                </ul>
              </div>
            ` : ''}
          </div>
        `,
          icon: 'success',
          timer: 5000,
          showConfirmButton: true,
          confirmButtonText: 'OK',
          confirmButtonColor: '#10b981'
        });

        // Refresh statements
        const statementsResponse = await axios.get(
          `${BASE_URL}/api/employees/company/${company.id}/${employee.Id}/statements`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (statementsResponse.data.success) {
          setStatements(statementsResponse.data.data);
          if (statementsResponse.data.placement) {
            setStatementPlacement(statementsResponse.data.placement);
          }
        }
      } else {
        Swal.fire({
          title: 'Error',
          text: response.data.message || 'Failed to import statements',
          icon: 'error'
        });
      }

      // Reset upload state
      setStatementFileUpload({
        isUploading: false,
        progress: 0,
        selectedFile: null
      });
      e.target.value = '';

    } catch (error) {
      console.error('Error uploading statement file:', error);

      Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to upload payroll statement file',
        icon: 'error'
      });

      setStatementFileUpload({
        isUploading: false,
        progress: 0,
        selectedFile: null
      });
      e.target.value = '';
    }
  };

  const fetchPermissionStatus = async () => {
    if (!employee?.EmployeeId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/timesheets/employee-permission/${employee.EmployeeId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.data.success) {
        setPermissionStatus({
          hasPermission: response.data.hasPermission,
          allowEditing: response.data.allowEditing
        });
      }
    } catch (error) {
      console.error('Error fetching permission status:', error);
    }
  };

  useEffect(() => {
    if (employee?.EmployeeId) {
      fetchPermissionStatus();

      // Add 5-second polling as requested
      const interval = setInterval(() => {
        fetchPermissionStatus();
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [employee]);


  const handleEditStatementInputChange = (e) => {
    const { name, value } = e.target;

    let updatedStatement = {
      ...editingStatement,
      [name]: value
    };

    // Auto-calculate credit if both hours and payRate are entered
    if ((name === 'Hours' || name === 'PayRate') && updatedStatement.Hours && updatedStatement.PayRate) {
      const calculatedCredit = parseFloat(updatedStatement.Hours) * parseFloat(updatedStatement.PayRate);
      updatedStatement.Credit = calculatedCredit.toFixed(2);

      // Auto-calculate balance: previousBalance + credit - debit
      const debit = parseFloat(updatedStatement.Debit) || 0;
      const newBalance = parseFloat(editingStatement.previousBalance || 0) + calculatedCredit - debit;
      updatedStatement.Balance = newBalance.toFixed(2);
    }

    // Auto-calculate balance if debit changes
    if (name === 'Debit' && updatedStatement.Credit) {
      const credit = parseFloat(updatedStatement.Credit) || 0;
      const debit = parseFloat(value) || 0;
      const newBalance = parseFloat(editingStatement.previousBalance || 0) + credit - debit;
      updatedStatement.Balance = newBalance.toFixed(2);
    }

    setEditingStatement(updatedStatement);
  };

  const handleSaveEditStatement = async (statementId) => {
    try {
      const token = localStorage.getItem('token');

      const response = await axios.put(
        `${BASE_URL}/api/employees/company/${company.id}/${employee.Id}/statements/${statementId}`,
        {
          checkDate: editingStatement.CheckDate,
          period: editingStatement.Period,
          description: editingStatement.Description,
          hours: editingStatement.Hours || 0,
          payRate: editingStatement.PayRate || 0,
          credit: editingStatement.Credit || 0,
          debit: editingStatement.Debit || 0,
          balance: editingStatement.Balance || 0
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setStatements(statements.map(s =>
          s.Id === statementId ? response.data.data : s
        ));

        setEditingStatementId(null);
        setEditingStatement({});

        Swal.fire({
          title: 'Success!',
          text: 'Statement updated successfully',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Error updating statement:', error);
      Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to update statement',
        icon: 'error'
      });
    }
  };

  const handleCancelEditStatement = () => {
    setEditingStatementId(null);
    setEditingStatement({});
  };

  // Statement-related states
  const [isAddingNewRow, setIsAddingNewRow] = useState(false);
  const [newStatementRow, setNewStatementRow] = useState({
    date: '',
    retention: '',
    description: '',
    hours: '',
    payRate: '',
    credit: '',
    debit: '',
    balance: ''
  });

  // Calculate pay structure based on Excel formulas (Anice_Acct Stmt.xlsx Sheet 2)
  const calculatePayStructureValues = () => {
    const lcaNum = parseFloat(payStructureData.lca) || 0;
    const vendorRateNum = parseFloat(payStructureData.vendorRate) || 80;
    const hoursNum = parseFloat(payStructureData.biweeklyHours) || 80;
    const perdiemNum = parseFloat(payStructureData.perdiemAmount !== undefined ? payStructureData.perdiemAmount : 1600) || 0;

    // Customizable percentage split (e.g. 80, 85, 86.25, 70, or custom)
    let pctSplit = parseFloat(payStructureData.percentageSplit);
    if (isNaN(pctSplit) || pctSplit <= 0) {
      if (payStructureData.payrollMode?.includes('86.25')) pctSplit = 86.25;
      else if (payStructureData.payrollMode?.includes('85')) pctSplit = 85;
      else if (payStructureData.payrollMode?.includes('70')) pctSplit = 70;
      else pctSplit = 80;
    }

    // As per LCA Rate (LCA / 1980 hours standard full-time annual hours benchmark from Excel: =I1/1980)
    const asPerLCARate = lcaNum > 0 ? (lcaNum / 1980) : 57.46;

    // Perdiem Rate (= Perdiem / biweeklyHours from Excel: =D13/80)
    const perdiemRate = hoursNum > 0 ? (perdiemNum / hoursNum) : 20.00;

    // Mode 1: Customizable Percentage Split Mode (e.g. 80/20, 85/15, 70/30, Custom %)
    const m1Rate = (vendorRateNum === 80) ? pctSplit : (vendorRateNum * (pctSplit / 80));
    const m1Gross = m1Rate * hoursNum;
    const m1Adp = m1Gross;
    const m1TaxRate = 0.0765 + (0.14518 * (pctSplit / 80));
    const m1Net = m1Gross * (1 - m1TaxRate);
    const m1Perdiem = 0;
    const m1TotalNet = m1Net;

    // Mode 2: Payroll & Perdiem mode (Column D in Excel)
    const m2Rate = asPerLCARate; // e.g. 57.46
    const m2Gross = m2Rate * hoursNum; // e.g. 4,596.80
    const m2Adp = m2Gross;
    const m2Net = m2Gross * 0.80668726; // e.g. 3,708.18 (taxable base net)
    const m2Perdiem = perdiemNum; // e.g. 1,600.00
    const m2TotalRate = m2Rate + perdiemRate; // e.g. 57.46 + 20.00 = 77.46
    const m2TotalNet = m2Net + m2Perdiem; // e.g. 3,708.18 + 1,600.00 = 5,308.18

    // Mode 3: Equals to 86.25% (Column E in Excel)
    const m3Rate = 86.25;
    const m3Gross = m3Rate * hoursNum; // e.g. 6,900.00
    const m3Adp = m3Gross;
    const m3Net = 5308.04;
    const m3Perdiem = 0;
    const m3TotalNet = m3Net;

    const companySplit = Math.max(0, 100 - pctSplit).toFixed(1).replace(/\.0$/, '');

    return {
      pctSplit,
      asPerLCARate: asPerLCARate.toFixed(2),
      perdiemRate: perdiemRate.toFixed(2),
      payingRate: m2TotalRate.toFixed(2),
      totalRate: m2TotalRate.toFixed(2),
      biweeklyHours: hoursNum.toFixed(0),
      mode1: {
        name: `Mode 1: ${pctSplit}% Split (${pctSplit}% / ${companySplit}%)`,
        rate: m1Rate.toFixed(2),
        biweeklyGross: m1Gross.toFixed(2),
        adpPayroll: m1Adp.toFixed(2),
        netPay: m1Net.toFixed(2),
        perdiem: '0.00',
        perdiemRate: '0.00',
        totalRate: m1Rate.toFixed(2),
        totalNetPay: m1TotalNet.toFixed(2)
      },
      mode2: {
        name: 'Payroll & Perdiem mode',
        rate: m2Rate.toFixed(2),
        biweeklyGross: m2Gross.toFixed(2),
        adpPayroll: m2Adp.toFixed(2),
        netPay: m2Net.toFixed(2),
        perdiem: m2Perdiem.toFixed(2),
        perdiemRate: perdiemRate.toFixed(2),
        totalRate: m2TotalRate.toFixed(2),
        totalNetPay: m2TotalNet.toFixed(2)
      },
      mode3: {
        name: 'Equals to 86.25%',
        rate: m3Rate.toFixed(2),
        biweeklyGross: m3Gross.toFixed(2),
        adpPayroll: m3Adp.toFixed(2),
        netPay: m3Net.toFixed(2),
        perdiem: '0.00',
        perdiemRate: '0.00',
        totalRate: m3Rate.toFixed(2),
        totalNetPay: m3TotalNet.toFixed(2)
      },
      // Backward compatibility fields
      biweeklyConsultant: m1Gross.toFixed(2),
      biweeklyAdp: m2Gross.toFixed(2),
      netPayConsultant: m1Net.toFixed(2),
      netPayAdp: m2Net.toFixed(2),
      perdiemAmount: m2Perdiem.toFixed(2),
      grossPayConsultant: m1Gross.toFixed(2),
      grossPayAdp: m2Gross.toFixed(2)
    };
  };

  const handleOpenPayStructure = () => {
    setShowPayStructureModal(true);
  };

  const handleClosePayStructure = () => {
    setShowPayStructureModal(false);
    setIsEditingPayStructure(false);
  };

  const handleEditPayStructure = () => {
    setIsEditingPayStructure(true);
  };

  const handleSavePayStructure = () => {
    setIsEditingPayStructure(false);
    Swal.fire({
      title: 'Success!',
      text: 'Pay structure updated successfully',
      icon: 'success',
      timer: 2000,
      showConfirmButton: false
    });
  };

  const handleCancelEditPayStructure = () => {
    setIsEditingPayStructure(false);
  };

  const handlePayStructureInputChange = (e) => {
    const { name, value } = e.target;
    setPayStructureData(prev => {
      const updated = {
        ...prev,
        [name]: (name === 'payrollMode') ? value : (parseFloat(value) || value)
      };

      // Synchronize percentage when changing payroll mode
      if (name === 'payrollMode') {
        if (value.includes('80/20')) {
          updated.percentageSplit = 80;
        } else if (value.includes('85/15')) {
          updated.percentageSplit = 85;
        } else if (value.includes('86.25')) {
          updated.percentageSplit = 86.25;
        } else if (value.includes('70/30')) {
          updated.percentageSplit = 70;
        }
      }

      // Synchronize percentageSplit input
      if (name === 'percentageSplit') {
        const num = parseFloat(value);
        if (!isNaN(num) && num > 0) {
          updated.percentageSplit = num;
        }
      }

      return updated;
    });
  };

  const filteredStatements = statements.filter(statement => {
    // Search filter
    const matchesSearch =
      statement.Description?.toLowerCase().includes(statementSearchTerm.toLowerCase()) ||
      statement.Period?.toLowerCase().includes(statementSearchTerm.toLowerCase());

    // Date range filter
    let matchesDateRange = true;
    if (statementFilterStartDate || statementFilterEndDate) {
      const statementDate = new Date(statement.CheckDate);

      if (statementFilterStartDate) {
        const startDate = new Date(statementFilterStartDate);
        matchesDateRange = matchesDateRange && statementDate >= startDate;
      }

      if (statementFilterEndDate) {
        const endDate = new Date(statementFilterEndDate);
        // Set end date to end of day
        endDate.setHours(23, 59, 59, 999);
        matchesDateRange = matchesDateRange && statementDate <= endDate;
      }
    }

    return matchesSearch && matchesDateRange;
  });

  const handleStatementSort = (columnKey) => {
    setStatementSortConfig(prev => {
      if (prev.key === columnKey) {
        if (prev.direction === 'asc') return { key: columnKey, direction: 'desc' };
        if (prev.direction === 'desc') return { key: null, direction: 'asc' };
      }
      return { key: columnKey, direction: 'asc' };
    });
  };

  const sortedFilteredStatements = useMemo(() => {
    if (!statementSortConfig.key) return filteredStatements;

    return [...filteredStatements].sort((a, b) => {
      const key = statementSortConfig.key;
      let aVal = a[key];
      let bVal = b[key];

      if (key === 'CheckDate') {
        const aTime = aVal ? new Date(aVal).getTime() : 0;
        const bTime = bVal ? new Date(bVal).getTime() : 0;
        return statementSortConfig.direction === 'asc' ? aTime - bTime : bTime - aTime;
      }

      if (['Hours', 'PayRate', 'Credit', 'Debit', 'Balance'].includes(key)) {
        const aNum = parseFloat(aVal) || 0;
        const bNum = parseFloat(bVal) || 0;
        return statementSortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      const aStr = String(aVal || '').toLowerCase();
      const bStr = String(bVal || '').toLowerCase();
      const comp = aStr.localeCompare(bStr);
      return statementSortConfig.direction === 'asc' ? comp : -comp;
    });
  }, [filteredStatements, statementSortConfig]);

  const renderStatementSortIcon = (columnKey) => {
    if (statementSortConfig.key !== columnKey) {
      return <ArrowUpDown size={13} style={{ opacity: 0.35, flexShrink: 0 }} />;
    }
    return statementSortConfig.direction === 'asc' ? (
      <ArrowUp size={13} style={{ color: '#2563eb', flexShrink: 0 }} />
    ) : (
      <ArrowDown size={13} style={{ color: '#2563eb', flexShrink: 0 }} />
    );
  };

  // Statement pagination logic
  useEffect(() => {
    setStatementCurrentPage(1);
  }, [statementSearchTerm, statementFilterStartDate, statementFilterEndDate]);

  const totalStatementPages = Math.ceil(sortedFilteredStatements.length / statementRowsPerPage) || 1;
  const statementIndexOfLastRow = statementCurrentPage * statementRowsPerPage;
  const statementIndexOfFirstRow = (statementCurrentPage - 1) * statementRowsPerPage;

  const paginatedStatements = useMemo(() => {
    return sortedFilteredStatements.slice(statementIndexOfFirstRow, statementIndexOfLastRow);
  }, [sortedFilteredStatements, statementIndexOfFirstRow, statementIndexOfLastRow]);

  useEffect(() => {
    if (statementCurrentPage > totalStatementPages) {
      setStatementCurrentPage(Math.max(1, totalStatementPages));
    }
  }, [totalStatementPages, statementCurrentPage]);




  // Fetch employee details and all related data
  useEffect(() => {
    const fetchEmployeeData = async () => {
      if (!employeeId && !passedEmployee) {
        if (isModal) {
          if (onClose) onClose();
          return;
        }
        Swal.fire({
          title: 'Error',
          text: 'Missing employee information',
          icon: 'error'
        });
        navigate(-1);
        return;
      }

      // If employee data was passed via state or props with full details, use it directly
      if (passedEmployee && (passedEmployee.Name || passedEmployee.FirstName)) {
        console.log('Using passed employee data:', passedEmployee);
        const resolvedCompany = passedCompany || { id: passedEmployee.EntityId || 1, name: 'Prophecy Consulting INC' };
        console.log('Company:', resolvedCompany);

        setEmployee(passedEmployee);
        setCompany(resolvedCompany);
        setEditedEmployee(passedEmployee);

        // Fetch only stats using the employee's database Id
        try {
          setLoading(true);
          const token = localStorage.getItem('token');
          const headers = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          };

          const empDbId = passedEmployee.Id || passedEmployee.EmployeeId || passedEmployee.id || employeeId;
          const cid = resolvedCompany.id || 1;

          // Use the database Id for stats
          const statsResponse = await axios.get(
            `${BASE_URL}/api/employees/company/${cid}/${empDbId}/details`,
            { headers }
          );

          if (statsResponse.data.success) {
            setStats(statsResponse.data.data.stats || {});
          }
        } catch (error) {
          console.error('Error fetching employee stats:', error);
        } finally {
          setLoading(false);
        }
        return;
      }

      // If no employee data passed, fetch by employeeId from backend
      if (employeeId) {
        try {
          setLoading(true);
          const token = localStorage.getItem('token');
          const cid = passedCompany?.id || 1;
          const empRes = await axios.get(`${BASE_URL}/api/employees/company/${cid}/${employeeId}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });

          if (empRes.data?.success && empRes.data.data) {
            const fetchedEmp = empRes.data.data;
            const fetchedComp = passedCompany || { id: cid, name: 'Prophecy Consulting INC' };
            setEmployee(fetchedEmp);
            setCompany(fetchedComp);
            setEditedEmployee(fetchedEmp);

            // Fetch stats
            try {
              const statsRes = await axios.get(`${BASE_URL}/api/employees/company/${cid}/${fetchedEmp.Id || employeeId}/details`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
              });
              if (statsRes.data?.success) {
                setStats(statsRes.data.data.stats || {});
              }
            } catch (statsErr) {
              console.error('Error fetching stats:', statsErr);
            }
            return;
          }
        } catch (fetchErr) {
          console.error('Error fetching employee by ID:', fetchErr);
        } finally {
          setLoading(false);
        }
      }

      // If no employee data could be loaded
      if (isModal) {
        if (onClose) onClose();
      } else {
        Swal.fire({
          title: 'Error',
          text: 'No employee data available. Please navigate from the employee list.',
          icon: 'error'
        });
        navigate(-1);
      }
    };

    fetchEmployeeData();

    // Fetch all employees for supervisor selection
    const fetchAllEmployees = async () => {
      if (passedCompany?.id || company?.id) {
        try {
          const token = localStorage.getItem('token');
          const cid = passedCompany?.id || company?.id;
          const response = await axios.get(`${BASE_URL}/api/employees/company/${cid}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.data.success) {
            setAllEmployees(response.data.data);
          }
        } catch (error) {
          console.error('Error fetching employees:', error);
        }
      }
    };
    fetchAllEmployees();
  }, [employeeId, navigate, passedEmployee, passedCompany, company?.id]);

  // Fetch data based on active view
  useEffect(() => {
    const fetchViewData = async () => {
      if (!company?.id || !employee?.EmployeeId) return;

      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      try {
        switch (activeView) {
          case 'internalTimesheets':
            console.log('Fetching internal timesheets for employee ID (database):', employee.Id);
            const timesheetsResponse = await axios.get(
              `${BASE_URL}/api/employees/company/${company.id}/${employee.Id}/timesheets`,
              { headers, params: { status: filterStatus !== 'all' ? filterStatus : undefined } }
            );
            if (timesheetsResponse.data.success) {
              setTimesheets(timesheetsResponse.data.data);
            }
            break;
          case 'externalTimesheets':
            console.log('Fetching external timesheets for EmployeeId:', employee.EmployeeId);
            try {
              const externalResponse = await axios.get(
                `${BASE_URL}/api/employees/company/${company.id}/${employee.EmployeeId}/external-timesheets`,
                { headers }
              );
              console.log('External timesheets response:', externalResponse.data);

              if (externalResponse.data.success) {
                console.log('External timesheets data:', externalResponse.data.data);
                setExternalTimesheets(externalResponse.data.data || []);
              } else {
                console.warn('External timesheets API returned success: false');
                setExternalTimesheets([]);
              }
            } catch (error) {
              console.error('Error fetching external timesheets:', error);
              setExternalTimesheets([]);
            }
            break;

          case 'statement':
            const statementsResponse = await axios.get(
              `${BASE_URL}/api/employees/company/${company.id}/${employee.Id}/statements`,
              { headers }
            );
            if (statementsResponse.data.success) {
              setStatements(statementsResponse.data.data);
              if (statementsResponse.data.placement) {
                setStatementPlacement(statementsResponse.data.placement);
              }
            }
            break;

          case 'report':
            const reportsResponse = await axios.get(
              `${BASE_URL}/api/employees/company/${company.id}/${employee.Id}/reports`,
              { headers }
            );
            if (reportsResponse.data.success) {
              setReports(reportsResponse.data.data);
            }
            break;
        }
      } catch (error) {
        console.error(`Error fetching ${activeView} data:`, error);
        Swal.fire({
          title: 'Error',
          text: `Failed to load ${activeView}: ${error.response?.data?.message || error.message}`,
          icon: 'error'
        });
      }
    };

    fetchViewData();
  }, [activeView, company, employee, filterStatus]);

  // Add this new useEffect after your employee data fetch useEffect and BEFORE the fetchViewData useEffect:
  useEffect(() => {
    if (!company?.id || !employee?.EmployeeId) return;

    const token = localStorage.getItem('token');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    const fetchExternalTimesheetsForBadge = async () => {
      try {
        // FIX: Use employee.EmployeeId
        const externalResponse = await axios.get(
          `${BASE_URL}/api/employees/company/${company.id}/${employee.EmployeeId}/external-timesheets`,
          { headers }
        );
        if (externalResponse.data.success) {
          setExternalTimesheets(externalResponse.data.data);
        }
      } catch (error) {
        console.error('Error fetching external timesheets for badge:', error);
      }
    };

    fetchExternalTimesheetsForBadge();
  }, [company, employee]);

  // Timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchPayrollInfo = async () => {
      if (!company?.id || !employee?.Id) return;

      try {
        setPayrollLoading(true);
        const token = localStorage.getItem('token');

        const response = await axios.get(
          `${BASE_URL}/api/employees/company/${company.id}/${employee.Id}/payroll-info`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          setPayrollInfo(prev => ({
            ...prev,
            ...response.data.data.payrollInfo,
            employmentType: employee.EmploymentType || 'Full-time'  // 👈 ADD THIS
          }));
        }
      } catch (error) {
        console.error('Error fetching payroll info:', error);
      } finally {
        setPayrollLoading(false);
      }
    };

    fetchPayrollInfo();
  }, [company?.id, employee?.Id]);

  // Statement helper functions
  const handleAddNewRow = async () => {
    if (isAddingNewRow) {
      if (!newStatementRow.date || !newStatementRow.description) {
        Swal.fire({
          title: 'Validation Error',
          text: 'Please fill in Date and Description fields',
          icon: 'warning'
        });
        return;
      }

      // Calculate Credit from Hours * Pay Rate if provided
      let credit = 0;
      if (newStatementRow.hours && newStatementRow.payRate) {
        credit = parseFloat(newStatementRow.hours) * parseFloat(newStatementRow.payRate);
      } else if (newStatementRow.credit) {
        credit = parseFloat(newStatementRow.credit.replace(/,/g, ''));
      }

      // Get the last statement's balance to calculate new balance
      let previousBalance = 0;
      if (statements.length > 0) {
        const lastStatement = statements[statements.length - 1];
        previousBalance = parseFloat(lastStatement.Balance || 0);
      }

      // Calculate new balance: previousBalance + credit - debit
      const debit = parseFloat(String(newStatementRow.debit || '0').replace(/,/g, '')) || 0;
      const newBalance = previousBalance + credit - debit;

      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${BASE_URL}/api/employees/company/${company.id}/${employee.Id}/statements`,
          {
            checkDate: newStatementRow.date,
            period: newStatementRow.retention,
            description: newStatementRow.description,
            hours: newStatementRow.hours || 0,
            payRate: newStatementRow.payRate || 0,
            credit: credit,
            debit: debit,
            balance: newBalance
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          Swal.fire({
            title: 'Success!',
            text: 'Statement row added successfully',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          });

          const statementsResponse = await axios.get(
            `${BASE_URL}/api/employees/company/${company.id}/${employee.Id}/statements`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );

          if (statementsResponse.data.success) {
            setStatements(statementsResponse.data.data);
            if (statementsResponse.data.placement) {
              setStatementPlacement(statementsResponse.data.placement);
            }
          }

          setIsAddingNewRow(false);
          setNewStatementRow({
            date: '',
            retention: '',
            description: '',
            hours: '',
            payRate: '',
            credit: '',
            debit: '',
            balance: ''
          });
        }
      } catch (error) {
        console.error('Error adding statement:', error);
        Swal.fire({
          title: 'Error',
          text: error.response?.data?.message || 'Failed to add statement',
          icon: 'error'
        });
      }
    } else {
      setIsAddingNewRow(true);
    }
  };

  const handleNewRowInputChange = (e) => {
    const { name, value } = e.target;

    let updatedRow = {
      ...newStatementRow,
      [name]: value
    };

    // Auto-calculate credit if both hours and payRate are entered
    if ((name === 'hours' || name === 'payRate') && updatedRow.hours && updatedRow.payRate) {
      const calculatedCredit = (parseFloat(updatedRow.hours) || 0) * (parseFloat(updatedRow.payRate) || 0);
      updatedRow.credit = calculatedCredit.toFixed(2);
    }

    // Auto-calculate balance: previousBalance + credit - debit
    let previousBalance = 0;
    if (statements.length > 0) {
      const lastStatement = statements[statements.length - 1];
      previousBalance = parseFloat(lastStatement.Balance || 0);
    }

    const creditVal = parseFloat(String(updatedRow.credit || '0').replace(/,/g, '')) || 0;
    const debitVal = parseFloat(String(updatedRow.debit || '0').replace(/,/g, '')) || 0;
    const newBalance = previousBalance + creditVal - debitVal;
    updatedRow.balance = newBalance.toFixed(2);

    setNewStatementRow(updatedRow);
  };

  const handleCancelNewRow = () => {
    setIsAddingNewRow(false);
    setNewStatementRow({
      date: '',
      retention: '',
      description: '',
      hours: '',
      payRate: '',
      credit: '',
      debit: '',
      balance: ''
    });
  };

  const formatCurrency = (value) => {
    if (!value) return '0.00';
    return parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Employee edit functions
  const handleEdit = () => setIsEditing(true);

  const handleSave = async () => {
    // Validation
    if (!editedEmployee.Name?.trim() && !editedEmployee.Email?.trim()) {
      // Only block if both are missing, or just allow saving everything?
      // User said "remove the mandatory fields", so I'll be very permissive.
    }

    try {
      const token = localStorage.getItem('token');

      // Construct the payload with all necessary fields
      const payload = {
        name: editedEmployee.Name?.trim(),
        email: editedEmployee.Email?.trim(),
        // department: editedEmployee.Department?.trim(), // Commented out as requested
        phone: editedEmployee.Phone?.trim(),
        position: editedEmployee.Position?.trim(),
        employmentType: editedEmployee.EmploymentType,
        status: editedEmployee.EmploymentStatus || editedEmployee.Status,
        hireDate: editedEmployee.HireDate,

        // Supervisor fields
        supervisorEmployeeId: editedEmployee.SupervisorEmployeeId,
        backupSupervisorEmployeeId: editedEmployee.BackupSupervisorEmployeeId,
        timesheetRequired: editedEmployee.TimesheetRequired,

        // Emergency contact
        emergencyContactName: editedEmployee.EmergencyContactName?.trim(),
        emergencyContactPhone: editedEmployee.EmergencyContactPhone?.trim(),

        // Address fields
        streetAddress: editedEmployee.StreetAddress?.trim(),
        apartmentSuite: editedEmployee.ApartmentSuite?.trim(),
        city: editedEmployee.City?.trim(),
        state: editedEmployee.State?.trim(),
        zipCode: editedEmployee.ZipCode?.trim()
      };

      const response = await axios.put(
        `${BASE_URL}/api/employees/company/${company.id}/${employee.EmployeeId}`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        // The backend should return the full updated employee object
        const updatedData = response.data.data;
        setEmployee(updatedData);
        setEditedEmployee(updatedData);
        setIsEditing(false);

        // Notify sidebar to refresh timesheet required status immediately
        window.dispatchEvent(new Event('timesheetRequiredUpdated'));

        Swal.fire({
          title: 'Success!',
          text: 'Employee updated successfully',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Error updating employee:', error);
      Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to update employee',
        icon: 'error'
      });
    }
  };
  const handleCancel = () => {
    setEditedEmployee(employee);
    setIsEditing(false);
  };

  // Add these functions to your EmployeeDetailsPage component
  const handleGrantEditingPermission = async (employeeId) => {
    const result = await Swal.fire({
      title: 'Grant Editing Permission?',
      text: 'This will allow the employee to edit their submitted timesheets. Are you sure?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, grant permission!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');

        const response = await axios.post(
          `${BASE_URL}/api/timesheets/grant-editing-permission`,
          {
            employeeId: employeeId,
            allowEditing: true
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          Swal.fire({
            title: 'Permission Granted!',
            text: 'Employee can now edit their submitted timesheets.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          });
          fetchPermissionStatus(); // Refresh status
        }
      } catch (error) {
        console.error('Error granting editing permission:', error);
        Swal.fire({
          title: 'Error',
          text: error.response?.data?.message || 'Failed to grant editing permission',
          icon: 'error'
        });
      }
    }
  };

  const handleRevokeEditingPermission = async (employeeId) => {
    const result = await Swal.fire({
      title: 'Revoke Editing Permission?',
      text: 'This will prevent the employee from editing their submitted timesheets. Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, revoke permission!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');

        const response = await axios.post(
          `${BASE_URL}/api/timesheets/grant-editing-permission`,
          {
            employeeId: employeeId,
            allowEditing: false
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          Swal.fire({
            title: 'Permission Revoked!',
            text: 'Employee can no longer edit submitted timesheets.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          });
          fetchPermissionStatus(); // Refresh status
        }
      } catch (error) {
        console.error('Error revoking editing permission:', error);
        Swal.fire({
          title: 'Error',
          text: error.response?.data?.message || 'Failed to revoke editing permission',
          icon: 'error'
        });
      }
    }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Delete Employee?',
      text: `Are you sure you want to delete ${employee.Name}? This will also delete all associated timesheets.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');

        const response = await axios.delete(
          `${BASE_URL}/api/employees/company/${company.id}/${employee.EmployeeId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          Swal.fire({
            title: 'Deleted!',
            text: response.data.message,
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          });
          navigate(-1);
        }
      } catch (error) {
        console.error('Error deleting employee:', error);
        Swal.fire({
          title: 'Error',
          text: error.response?.data?.message || 'Failed to delete employee',
          icon: 'error'
        });
      }
    }
  };

  // Export timesheet to CSV
  const exportTimesheetToCSV = (timesheet, entries) => {
    const csvRows = [];

    // Add header information
    csvRows.push(`Timesheet Tasks - ${employee.Name}`);
    csvRows.push(`Period:,${new Date(timesheet.PeriodStart).toLocaleDateString()} - ${new Date(timesheet.PeriodEnd).toLocaleDateString()}`);
    csvRows.push(`Total Hours:,${timesheet.totalHours || 0} hours`);
    csvRows.push(`Status:,${timesheet.status}`);
    if (timesheet.notes) {
      csvRows.push(`Notes:,${timesheet.notes}`);
    }
    csvRows.push(''); // Empty row

    // Add table headers
    csvRows.push('Date,Day,Hours,Type,Task');

    // Add entries
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    entries.forEach(entry => {
      const entryDate = new Date(entry.Date);
      const dayName = dayNames[entryDate.getDay()];
      const task = (!entry.Task || entry.Task === 'General Work' ? 'Task cannot be updated' : entry.Task).replace(/,/g, ';'); // Replace commas in task

      // Format date with tab character to force text format in Excel
      const formattedDate = `="${entryDate.toLocaleDateString()}"`;

      csvRows.push(`${formattedDate},${dayName},${entry.Hours || 0},${entry.DayType || 'Regular'},${task}`);
    });

    // Create CSV content
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `Timesheet_${employee.Name}_${new Date(timesheet.PeriodStart).toLocaleDateString().replace(/\//g, '-')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // View Timesheet Tasks - FIXED VERSION WITH CORRECT ID PROPERTY
  const handleViewTimesheetTasks = async (timesheetId) => {
    try {
      const token = localStorage.getItem('token');

      console.log('🔍 Fetching timesheet details for ID:', timesheetId);

      // First, get the timesheet basic info
      const timesheetResponse = await axios.get(
        `${BASE_URL}/api/employees/company/${company.id}/${employee.Id}/timesheets`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!timesheetResponse.data.success) {
        throw new Error('Failed to fetch timesheets');
      }

      // FIX: Check for both 'id' and 'Id' properties since API might return either
      const timesheet = timesheetResponse.data.data.find(ts => ts.id === timesheetId || ts.Id === timesheetId);

      if (!timesheet) {
        throw new Error('Timesheet not found');
      }

      console.log('✅ Found timesheet:', timesheet);

      // Now fetch the entries for this timesheet
      const entriesResponse = await axios.get(
        `${BASE_URL}/api/timesheets/${timesheetId}/entries`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('✅ Entries response:', entriesResponse.data);

      const entries = entriesResponse.data.entries || entriesResponse.data || [];

      const getTableHtml = (isEdit) => {
        if (entries.length === 0) return '<p style="text-align: center; padding: 20px; color: #6b7280;">No entries found for this timesheet.</p>';

        let html = '<table style="width: 100%; border-collapse: collapse; text-align: left;">';
        html += '<thead><tr style="background: #f3f4f6; border-bottom: 2px solid #e5e7eb;">';
        html += '<th style="padding: 12px;">Date</th>';
        html += '<th style="padding: 12px;">Day</th>';
        html += '<th style="padding: 12px;">Hours</th>';
        html += '<th style="padding: 12px;">Type</th>';
        html += '<th style="padding: 12px;">Task</th>';
        html += '</tr></thead><tbody>';

        entries.forEach(entry => {
          const entryDate = new Date(entry.Date);
          const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          const dayName = dayNames[entryDate.getDay()];

          const rawType = entry.DayType || entry.HourType || 'Regular';
          const isLeave = rawType.toLowerCase() === 'leave' || entry.IsLeave === true || entry.IsLeave === 1;
          const isHoliday = rawType.toLowerCase() === 'holiday' || entry.IsHoliday === true || entry.IsHoliday === 1;

          const displayHours = (isLeave || isHoliday) ? 0 : (entry.Hours || 0);
          let rawTask = entry.Task || entry.task || entry.Description || entry.description || entry.Notes;
          if (rawTask === 'General Work') rawTask = 'Task cannot be updated';
          const taskText = (isLeave || isHoliday) ? 'No task' : (rawTask || 'Task cannot be updated');

          html += `<tr style="border-bottom: 1px solid #e5e7eb;" class="mts-entry-row" data-id="${entry.TimesheetEntryId || entry.id || entry.Id}">`;
          html += `<td style="padding: 10px;">${entryDate.toLocaleDateString()}</td>`;
          html += `<td style="padding: 10px;">${dayName}</td>`;

          if (isEdit) {
            html += `<td style="padding: 10px;"><input type="number" step="0.5" class="mts-hours-input" value="${displayHours}" style="width: 60px; padding: 4px; border: 1px solid #d1d5db; border-radius: 4px;"></td>`;
            html += `<td style="padding: 10px;">
                <select class="mts-type-input" style="padding: 4px; border: 1px solid #d1d5db; border-radius: 4px;">
                    <option value="REGULAR" ${rawType.toUpperCase() === 'REGULAR' ? 'selected' : ''}>REGULAR</option>
                    <option value="LEAVE" ${rawType.toUpperCase() === 'LEAVE' ? 'selected' : ''}>LEAVE</option>
                    <option value="HOLIDAY" ${rawType.toUpperCase() === 'HOLIDAY' ? 'selected' : ''}>HOLIDAY</option>
                    <option value="PTO" ${rawType.toUpperCase() === 'PTO' ? 'selected' : ''}>PTO</option>
                    <option value="SICK" ${rawType.toUpperCase() === 'SICK' ? 'selected' : ''}>SICK</option>
                </select>
            </td>`;
            html += `<td style="padding: 10px;"><input type="text" class="mts-task-input" value="${taskText.replace(/"/g, '&quot;')}" style="width: 100%; padding: 4px; border: 1px solid #d1d5db; border-radius: 4px;"></td>`;
          } else {
            html += `<td style="padding: 10px; font-weight: 600;">${displayHours} hrs</td>`;
            html += `<td style="padding: 10px;">${rawType}</td>`;
            html += `<td style="padding: 10px;" class="mts-task-hover-cell" data-full-task="${taskText.replace(/"/g, '&quot;')}"><span class="mts-task-cell">${taskText}</span></td>`;
          }
          html += '</tr>';
        });
        html += '</tbody></table>';
        return html;
      };

      const handleSaveEntries = async (ts, updatedEntries) => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.put(
            `${BASE_URL}/api/timesheets/company/${company.id}/${ts.Id || ts.id}/entries`,
            { entries: updatedEntries },
            { headers: { 'Authorization': `Bearer ${token}` } }
          );

          if (response.data.success) {
            // Update local state by refreshing the list (standard pattern in UserDetails.js)
            const timesheetsResponse = await axios.get(
              `${BASE_URL}/api/employees/company/${company.id}/${employee.Id}/timesheets`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (timesheetsResponse.data.success) {
              setTimesheets(timesheetsResponse.data.data);
            }

            Swal.fire({
              title: 'Saved!',
              text: 'Timesheet entries updated successfully.',
              icon: 'success',
              timer: 1500,
              showConfirmButton: false
            });
            return true;
          }
          return false;
        } catch (error) {
          console.error('Error saving timesheet entries:', error);
          Swal.fire('Error', error.response?.data?.message || 'Failed to save entries', 'error');
          return false;
        }
      };

      Swal.fire({
        title: `Timesheet Tasks - ${employee.Name}`,
        html: `
          <div style="text-align: left; margin-bottom: 20px;">
            <p><strong>Period:</strong> ${new Date(timesheet.PeriodStart).toLocaleDateString()} - ${new Date(timesheet.PeriodEnd).toLocaleDateString()}</p>
            <p><strong>Total Hours:</strong> <span id="mts-modal-total-hours">${timesheet.totalHours || timesheet.TotalHours || 0}</span> hours</p>
            <p><strong>Status:</strong> <span style="padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 600; background: ${(timesheet.status || timesheet.Status) === 'Approved' ? '#d1fae5' :
            (timesheet.status || timesheet.Status) === 'Rejected' ? '#fee2e2' : '#fff3cd'
          }; color: ${(timesheet.status || timesheet.Status) === 'Approved' ? '#065f46' :
            (timesheet.status || timesheet.Status) === 'Rejected' ? '#991b1b' : '#856404'
          };">${timesheet.status || timesheet.Status}</span></p>
            ${timesheet.notes || timesheet.Notes ? `<p><strong>Notes:</strong> ${timesheet.notes || timesheet.Notes}</p>` : ''}
          </div>
          <div id="mts-table-container" style="max-height: 400px; overflow-y: auto; padding-bottom: 20px;">
            ${getTableHtml(false)}
          </div>
        `,
        width: '900px',
        showConfirmButton: (timesheet.status || timesheet.Status) !== 'Approved',
        showDenyButton: (timesheet.status || timesheet.Status) !== 'Approved',
        showCancelButton: true,
        confirmButtonText: 'Approve',
        denyButtonText: 'Reject',
        cancelButtonText: 'Close',
        confirmButtonColor: '#10b981',
        denyButtonColor: '#ef4444',
        cancelButtonColor: '#6b7280',
        didOpen: () => {
          const container = Swal.getHtmlContainer();
          const actions = container.closest('.swal2-popup').querySelector('.swal2-actions');
          let isEditMode = false;

          // Add Edit button if it's not approved
          if ((timesheet.status || timesheet.Status) !== 'Approved') {
            const editBtn = document.createElement('button');
            editBtn.type = 'button';
            editBtn.id = 'mts-edit-toggle-btn';
            editBtn.innerText = 'Edit';
            editBtn.className = 'swal2-confirm swal2-styled';
            editBtn.style.backgroundColor = '#3b82f6';
            editBtn.style.marginRight = '10px';

            const confirmBtn = actions.querySelector('.swal2-confirm');
            actions.insertBefore(editBtn, confirmBtn);

            editBtn.onclick = async () => {
              if (!isEditMode) {
                isEditMode = true;
                editBtn.innerText = 'Save Changes';
                editBtn.style.backgroundColor = '#10b981';
                const tableContainer = container.querySelector('#mts-table-container');
                if (tableContainer) tableContainer.innerHTML = getTableHtml(true);
              } else {
                const rows = Array.from(container.querySelectorAll('.mts-entry-row'));
                const updatedEntries = rows.map(row => {
                  const hoursInput = row.querySelector('.mts-hours-input');
                  const taskInput = row.querySelector('.mts-task-input');
                  const typeInput = row.querySelector('.mts-type-input');
                  if (hoursInput && taskInput && typeInput) {
                    return {
                      id: row.dataset.id,
                      hours: hoursInput.value,
                      task: taskInput.value,
                      hourType: typeInput.value
                    };
                  }
                  return null;
                }).filter(Boolean);

                if (updatedEntries.length > 0) {
                  editBtn.disabled = true;
                  editBtn.innerText = 'Saving...';
                  const success = await handleSaveEntries(timesheet, updatedEntries);
                  if (success) {
                    const newTotal = updatedEntries.reduce((sum, e) => sum + parseFloat(e.hours || 0), 0);
                    const totalDisplay = container.querySelector('#mts-modal-total-hours');
                    if (totalDisplay) totalDisplay.innerText = newTotal;

                    updatedEntries.forEach(upd => {
                      const entry = entries.find(e => String(e.TimesheetEntryId || e.id || e.Id) === String(upd.id));
                      if (entry) {
                        entry.Hours = parseFloat(upd.hours);
                        entry.Task = upd.task;
                        entry.HourType = upd.hourType;
                        entry.DayType = upd.hourType;
                      }
                    });

                    isEditMode = false;
                    editBtn.innerText = 'Edit';
                    editBtn.style.backgroundColor = '#3b82f6';
                    const tableContainer = container.querySelector('#mts-table-container');
                    if (tableContainer) tableContainer.innerHTML = getTableHtml(false);
                  }
                  editBtn.disabled = false;
                }
              }
            };
          }

          const attachTooltips = () => {
            const hoverCells = container.querySelectorAll('.mts-task-hover-cell');
            let tooltip = document.getElementById('mts-floating-tooltip-user');
            if (!tooltip) {
              tooltip = document.createElement('div');
              tooltip.id = 'mts-floating-tooltip-user';
              tooltip.style.position = 'fixed';
              tooltip.style.display = 'none';
              tooltip.style.padding = '12px 16px';
              tooltip.style.background = '#0f172a';
              tooltip.style.color = '#ffffff';
              tooltip.style.borderRadius = '8px';
              tooltip.style.zIndex = '9999999';
              tooltip.style.pointerEvents = 'none';
              tooltip.style.maxWidth = '350px';
              tooltip.style.fontSize = '13px';
              tooltip.style.lineHeight = '1.5';
              tooltip.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.4)';
              tooltip.style.border = '1px solid rgba(255, 255, 255, 0.2)';
              document.body.appendChild(tooltip);
            }

            hoverCells.forEach(cell => {
              cell.addEventListener('mousemove', (e) => {
                tooltip.innerText = cell.getAttribute('data-full-task');
                tooltip.style.display = 'block';
                tooltip.style.left = (e.clientX + 15) + 'px';
                tooltip.style.top = (e.clientY + 15) + 'px';

                const rect = tooltip.getBoundingClientRect();
                if (rect.right > window.innerWidth) {
                  tooltip.style.left = (e.clientX - rect.width - 15) + 'px';
                }
                if (rect.bottom > window.innerHeight) {
                  tooltip.style.top = (e.clientY - rect.height - 15) + 'px';
                }
              });
              cell.addEventListener('mouseleave', () => {
                tooltip.style.display = 'none';
              });
            });
          };

          attachTooltips();
          const observer = new MutationObserver(attachTooltips);
          const tableContainer = container.querySelector('#mts-table-container');
          if (tableContainer) observer.observe(tableContainer, { childList: true });
        },
        willClose: () => {
          const tooltip = document.getElementById('mts-floating-tooltip-user');
          if (tooltip) tooltip.remove();
        }
      }).then((result) => {
        if (result.isConfirmed) {
          handleApproveInternalTimesheet(timesheet.Id || timesheet.id);
        } else if (result.isDenied) {
          handleRejectInternalTimesheet(timesheet.Id || timesheet.id);
        }
      });
    } catch (error) {
      console.error('❌ Error fetching timesheet details:', error);
      console.error('Error response:', error.response?.data);

      Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to load timesheet details',
        icon: 'error'
      });
    }
  };

  // Approve Internal Timesheet
  const handleApproveInternalTimesheet = async (timesheetId) => {
    const result = await Swal.fire({
      title: 'Approve Timesheet?',
      text: 'Are you sure you want to approve this timesheet?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, approve it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');

        const response = await axios.post(
          `${BASE_URL}/api/timesheets/${timesheetId}/approve`,
          {},
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          // Refresh the timesheets list
          const timesheetsResponse = await axios.get(
            `${BASE_URL}/api/employees/company/${company.id}/${employee.Id}/timesheets`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              params: { status: filterStatus !== 'all' ? filterStatus : undefined }
            }
          );

          if (timesheetsResponse.data.success) {
            setTimesheets(timesheetsResponse.data.data);
          }

          // Refresh stats
          const statsResponse = await axios.get(
            `${BASE_URL}/api/employees/company/${company.id}/${employee.Id}/details`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );

          if (statsResponse.data.success) {
            setStats(statsResponse.data.data.stats || {});
          }

          Swal.fire({
            title: 'Approved!',
            text: 'Timesheet has been approved successfully.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          });
        }
      } catch (error) {
        console.error('Error approving timesheet:', error);
        Swal.fire({
          title: 'Error',
          text: error.response?.data?.message || 'Failed to approve timesheet',
          icon: 'error'
        });
      }
    }
  };

  // Reject Internal Timesheet
  const handleRejectInternalTimesheet = async (timesheetId) => {
    const { value: reason, isConfirmed } = await Swal.fire({
      title: 'Reject Timesheet?',
      input: 'textarea',
      inputLabel: 'Rejection Reason (optional)',
      inputPlaceholder: 'Enter reason for rejection...',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, reject it!',
      cancelButtonText: 'Cancel'
    });

    if (isConfirmed) {
      try {
        const token = localStorage.getItem('token');

        const response = await axios.post(
          `${BASE_URL}/api/timesheets/${timesheetId}/reject`,
          {
            reason: reason || 'No reason provided'
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          // Refresh the timesheets list
          const timesheetsResponse = await axios.get(
            `${BASE_URL}/api/employees/company/${company.id}/${employee.Id}/timesheets`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              params: { status: filterStatus !== 'all' ? filterStatus : undefined }
            }
          );

          if (timesheetsResponse.data.success) {
            setTimesheets(timesheetsResponse.data.data);
          }

          // Refresh stats
          const statsResponse = await axios.get(
            `${BASE_URL}/api/employees/company/${company.id}/${employee.Id}/details`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );

          if (statsResponse.data.success) {
            setStats(statsResponse.data.data.stats || {});
          }

          Swal.fire({
            title: 'Rejected!',
            text: 'Timesheet has been rejected.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          });
        }
      } catch (error) {
        console.error('Error rejecting timesheet:', error);
        Swal.fire({
          title: 'Error',
          text: error.response?.data?.message || 'Failed to reject timesheet',
          icon: 'error'
        });
      }
    }
  };

  // Delete Internal Timesheet
  const deleteInternalTimesheet = async (timesheetId) => {
    const result = await Swal.fire({
      title: 'Delete Timesheet?',
      text: 'Are you sure you want to delete this timesheet? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(
          `${BASE_URL}/api/timesheets/${timesheetId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        // Refresh the timesheets list - fix: use both Id and id
        setTimesheets(prev => prev.filter(ts => ts.Id !== timesheetId && ts.id !== timesheetId));

        // Refresh stats
        const statsResponse = await axios.get(
          `${BASE_URL}/api/employees/company/${company.id}/${employee.Id}/details`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (statsResponse.data.success) {
          setStats(statsResponse.data.data.stats || {});
        }

        Swal.fire({
          title: 'Deleted!',
          text: 'Timesheet has been deleted.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        console.error('Error deleting timesheet:', error);
        Swal.fire('Error', 'Failed to delete timesheet', 'error');
      }
    }
  };

  // Approve external timesheet
  const handleApproveExternalTimesheet = async (timesheetId) => {
    const result = await Swal.fire({
      title: 'Approve Timesheet?',
      text: 'Are you sure you want to approve this external timesheet?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, approve it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');

        // FIX: Pass companyId as param, use EmployeeId in route, timesheetId in path
        const response = await axios.post(
          `${BASE_URL}/api/employees/company/${company.id}/${employee.EmployeeId}/external-timesheets/${timesheetId}/approve`,
          {},
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          // Refresh the external timesheets list using EmployeeId
          const timesheetsResponse = await axios.get(
            `${BASE_URL}/api/employees/company/${company.id}/${employee.EmployeeId}/external-timesheets`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );

          if (timesheetsResponse.data.success) {
            setExternalTimesheets(timesheetsResponse.data.data);
          }

          Swal.fire({
            title: 'Approved!',
            text: 'External timesheet has been approved successfully.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          });
        }
      } catch (error) {
        console.error('Error approving timesheet:', error);
        console.error('Error details:', error.response?.data);

        Swal.fire({
          title: 'Error',
          text: error.response?.data?.message || 'Failed to approve timesheet',
          icon: 'error'
        });
      }
    }
  };

  // Reject external timesheet
  const handleRejectExternalTimesheet = async (timesheetId) => {
    const { value: reason, isConfirmed } = await Swal.fire({
      title: 'Reject Timesheet?',
      input: 'textarea',
      inputLabel: 'Rejection Reason (optional)',
      inputPlaceholder: 'Enter reason for rejection...',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, reject it!',
      cancelButtonText: 'Cancel'
    });

    if (isConfirmed) {
      try {
        const token = localStorage.getItem('token');

        // FIX: Pass companyId as param, use EmployeeId in route, timesheetId in path
        const response = await axios.post(
          `${BASE_URL}/api/employees/company/${company.id}/${employee.EmployeeId}/external-timesheets/${timesheetId}/reject`,
          {
            reason: reason || 'No reason provided'
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          // Refresh the external timesheets list using EmployeeId
          const timesheetsResponse = await axios.get(
            `${BASE_URL}/api/employees/company/${company.id}/${employee.EmployeeId}/external-timesheets`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );

          if (timesheetsResponse.data.success) {
            setExternalTimesheets(timesheetsResponse.data.data);
          }

          Swal.fire({
            title: 'Rejected!',
            text: 'External timesheet has been rejected.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          });
        }
      } catch (error) {
        console.error('Error rejecting timesheet:', error);
        console.error('Error details:', error.response?.data);

        Swal.fire({
          title: 'Error',
          text: error.response?.data?.message || 'Failed to reject timesheet',
          icon: 'error'
        });
      }
    }
  };

  // Download external timesheet
  const handleDownloadExternalTimesheet = async (timesheetId, fileName) => {
    try {
      const token = localStorage.getItem('token');
      // FIX: Use EmployeeId instead of Id
      const response = await axios.get(
        `${BASE_URL}/api/employees/company/${company.id}/${employee.EmployeeId}/external-timesheets/${timesheetId}/download`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to download file',
        icon: 'error'
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedEmployee(prev => ({ ...prev, [name]: value }));
  };
  const handlePayrollInputChange = (e) => {
    const { name, value } = e.target;
    setPayrollInfo(prev => ({
      ...prev,
      [name]: isNaN(value) ? value : parseFloat(value)
    }));
  };

  const handleSavePayrollInfo = async () => {
    try {
      const token = localStorage.getItem('token');

      const response = await axios.put(
        `${BASE_URL}/api/employees/company/${company.id}/${employee.Id}/payroll-info`,
        {
          payType: payrollInfo.payType,
          basisOfPay: payrollInfo.basisOfPay,
          paySchedule: payrollInfo.paySchedule,
          employmentType: payrollInfo.employmentType,  // 👈 ADD THIS
          seasonalEmployee: payrollInfo.seasonalEmployee,
          ownerOfficer: payrollInfo.ownerOfficer,
          semiMonthlySalary: parseFloat(payrollInfo.semiMonthlySalary) || 0,
          hourlyRate: parseFloat(payrollInfo.hourlyRate) || 0
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setPayrollInfo(response.data.data.payrollInfo);
        setIsEditingPayroll(false);

        Swal.fire({
          title: 'Success!',
          text: 'Payroll information updated successfully',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Error updating payroll info:', error);
      Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to update payroll information',
        icon: 'error'
      });
    }
  };

  const handleCancelPayrollEdit = () => {
    setIsEditingPayroll(false);
  };

  // File upload handlers
  const handleReportUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      // Validate file size (50MB limit)
      const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
      if (file.size > MAX_FILE_SIZE) {
        Swal.fire({
          title: 'File Too Large',
          text: 'File size must be less than 50MB',
          icon: 'error'
        });
        e.target.value = '';
        return;
      }

      // Create FormData object
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'Custom Report');
      formData.append('description', '');

      const token = localStorage.getItem('token');

      console.log('Uploading report for company:', company.id, 'employee:', employee.Id);
      console.log('File name:', file.name, 'Size:', file.size);

      const response = await axios.post(
        `${BASE_URL}/api/employees/company/${company.id}/${employee.Id}/reports`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      console.log('Upload response:', response.data);

      if (response.data.success) {
        Swal.fire({
          title: 'Success!',
          text: 'Report uploaded successfully',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });

        // Refresh reports list
        const reportsResponse = await axios.get(
          `${BASE_URL}/api/employees/company/${company.id}/${employee.Id}/reports`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (reportsResponse.data.success) {
          setReports(reportsResponse.data.data);
        }

        // Clear the input
        e.target.value = '';
      }
    } catch (error) {
      console.error('Error uploading report:', error);
      console.error('Error response:', error.response?.data);

      Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to upload report',
        icon: 'error'
      });

      // Clear the input
      e.target.value = '';
    }
  };

  // Delete handlers
  const deleteExternalTimesheet = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Timesheet?',
      text: 'Are you sure you want to delete this timesheet?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        // FIX: Use EmployeeId instead of Id
        await axios.delete(
          `${BASE_URL}/api/employees/company/${company.id}/${employee.EmployeeId}/external-timesheets/${id}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        setExternalTimesheets(prev => prev.filter(ts => ts.Id !== id));
        Swal.fire('Deleted!', 'Timesheet has been deleted.', 'success');
      } catch (error) {
        console.error('Error deleting timesheet:', error);
        Swal.fire('Error', 'Failed to delete timesheet', 'error');
      }
    }
  };

  const deleteReport = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Report?',
      text: 'Are you sure you want to delete this report?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(
          `${BASE_URL}/api/employees/company/${company.id}/${employee.Id}/reports/${id}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        setReports(prev => prev.filter(report => report.Id !== id));
        Swal.fire('Deleted!', 'Report has been deleted.', 'success');
      } catch (error) {
        console.error('Error deleting report:', error);
        Swal.fire('Error', 'Failed to delete report', 'error');
      }
    }
  };

  const deleteStatement = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Statement?',
      text: 'Are you sure you want to delete this statement?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(
          `${BASE_URL}/api/employees/company/${company.id}/${employee.Id}/statements/${id}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        setStatements(prev => prev.filter(statement => statement.Id !== id));
        setSelectedStatementIds(prev => prev.filter(statementId => statementId !== id));
        Swal.fire('Deleted!', 'Statement has been deleted.', 'success');
      } catch (error) {
        console.error('Error deleting statement:', error);
        Swal.fire('Error', 'Failed to delete statement', 'error');
      }
    }
  };

  const handleToggleSelectStatement = (id) => {
    setSelectedStatementIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleToggleSelectAllStatements = () => {
    const pageItems = paginatedStatements.map(stmt => stmt.Id);
    const allPageSelected = pageItems.length > 0 && pageItems.every(id => selectedStatementIds.includes(id));

    if (allPageSelected) {
      setSelectedStatementIds(prev => prev.filter(id => !pageItems.includes(id)));
    } else {
      setSelectedStatementIds(prev => Array.from(new Set([...prev, ...pageItems])));
    }
  };

  const handleBulkDeleteStatements = async () => {
    if (selectedStatementIds.length === 0) return;

    const result = await Swal.fire({
      title: 'Delete Selected Statements?',
      text: `Are you sure you want to delete ${selectedStatementIds.length} statement(s)? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6b7280',
      confirmButtonText: `Yes, delete ${selectedStatementIds.length} statement(s)!`,
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        Swal.fire({
          title: 'Deleting...',
          text: 'Deleting selected statements...',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        await Promise.all(
          selectedStatementIds.map(id =>
            axios.delete(
              `${BASE_URL}/api/employees/company/${company.id}/${employee.Id}/statements/${id}`,
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              }
            )
          )
        );

        setStatements(prev => prev.filter(statement => !selectedStatementIds.includes(statement.Id)));
        setSelectedStatementIds([]);
        Swal.fire('Deleted!', 'Selected statement(s) have been deleted.', 'success');
      } catch (error) {
        console.error('Error deleting statements:', error);
        Swal.fire('Error', 'Failed to delete some or all selected statements', 'error');
      }
    }
  };

  if (loading || !employee) {
    return (
      <div className="employee-details-main-container">
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  const filteredTimesheets = timesheets.filter(ts => {
    const matchesSearch = ts.EmployeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ts.Period?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Filter function for External Timesheets
  const filteredExternalTimesheets = externalTimesheets.filter(ts => {
    // Search filter
    const matchesSearch =
      ts.FileName?.toLowerCase().includes(externalSearchTerm.toLowerCase()) ||
      ts.Period?.toLowerCase().includes(externalSearchTerm.toLowerCase());

    // Status filter
    const matchesStatus = externalFilterStatus === 'all' ||
      (ts.Status || 'Pending') === externalFilterStatus;

    // Period filter
    let matchesPeriod = true;
    if (externalFilterPeriod !== 'all') {
      const uploadDate = new Date(ts.UploadDate);
      const currentDate = new Date();
      const monthsAgo = new Date(currentDate.getFullYear(), currentDate.getMonth() - parseInt(externalFilterPeriod), currentDate.getDate());
      matchesPeriod = uploadDate >= monthsAgo;
    }

    return matchesSearch && matchesStatus && matchesPeriod;
  });

  if (loading && !employee) {
    return (
      <div className="employee-details-main-container" style={{ padding: '60px 20px', textAlign: 'center', background: '#fff', borderRadius: '8px' }}>
        <div style={{ display: 'inline-block', width: '36px', height: '36px', border: '3px solid #e2e8f0', borderTopColor: '#3b5bfd', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        <p style={{ marginTop: '16px', color: '#64748b', fontSize: '14px' }}>Loading employee details...</p>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="employee-details-main-container" style={{ padding: '60px 20px', textAlign: 'center', background: '#fff', borderRadius: '8px' }}>
        <p style={{ color: '#ef4444', fontSize: '15px', fontWeight: 600 }}>Employee details could not be loaded.</p>
        {isModal && (
          <button 
            type="button"
            className="employee-details-back-button" 
            style={{ margin: '16px auto', display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#3b5bfd', color: '#fff', padding: '8px 20px', borderRadius: '6px' }} 
            onClick={onClose}
          >
            <X size={16} /> Close
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="employee-details-main-container">
      {/* Header */}
      <div className="employee-details-header">
        <div className="employee-details-header-content">
          <div className="employee-details-header-left">
            {!isModal && (
              <button 
                className="employee-details-back-button" 
                onClick={() => navigate(-1)}
              >
                <ArrowLeft size={20} />
                Back
              </button>
            )}
            <div>
              <h1>{employee?.Name || employee?.FirstName || 'Employee'} - Employee Details</h1>
              <p>Employee Code: {employee?.EmployeeCode || employee?.employeeCode || employeeId || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="employee-details-navigation-tabs">
          <button
            className={`employee-details-tab-button ${activeView === 'employeeDetails' ? 'active' : ''}`}
            onClick={() => setActiveView('employeeDetails')}
          >
            <User size={16} />
            Employee Details
          </button>
          <button
            className={`employee-details-tab-button ${activeView === 'internalTimesheets' ? 'active' : ''}`}
            onClick={() => setActiveView('internalTimesheets')}
          >
            <FileText size={16} />
            Internal Timesheets
            {stats.timesheets?.internalPending > 0 && (
              <span style={{
                marginLeft: '8px',
                backgroundColor: '#ef4444',
                color: 'white',
                borderRadius: '50%',
                padding: '2px 6px',
                fontSize: '11px'
              }}>
                {stats.timesheets.internalPending}
              </span>
            )}
          </button>

          {/* HIDE THESE TABS FOR PROPHECY OFFSHORE */}
          {!shouldHideTabs(company) && (
            <>
              <button
                className={`employee-details-tab-button ${activeView === 'externalTimesheets' ? 'active' : ''}`}
                onClick={() => setActiveView('externalTimesheets')}
              >
                <FileSpreadsheet size={16} />
                External Timesheets
                {stats.timesheets?.externalPending > 0 && (
                  <span style={{
                    marginLeft: '8px',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    borderRadius: '50%',
                    padding: '2px 6px',
                    fontSize: '11px'
                  }}>
                    {stats.timesheets.externalPending}
                  </span>
                )}
              </button>
              <button
                className={`employee-details-tab-button ${activeView === 'statement' ? 'active' : ''}`}
                onClick={() => setActiveView('statement')}
              >
                <BarChart3 size={16} />
                Payroll Statement
              </button>
            </>
          )}
          <button
            className={`employee-details-tab-button ${activeView === 'report' ? 'active' : ''}`}
            onClick={() => setActiveView('report')}
          >
            <FileText size={16} />
            Report
          </button>
          <button
            className={`employee-details-tab-button ${activeView === 'workDeploymentHistory' ? 'active' : ''}`}
            onClick={() => setActiveView('workDeploymentHistory')}
          >
            <Briefcase size={16} />
            Work Deployment History
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="employee-details-main-content">
        {/* Employee Details View */}
        {activeView === 'employeeDetails' && (
          <div className="employee-details-content-section">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: '24px', alignItems: 'start' }}>
              {/* LEFT COLUMN - Employee Details Card */}
              <div className="employee-details-card">
                <div className="employee-details-card-header">
                  <h3>Employee Details - {employee?.Name || `${employee?.FirstName || ''} ${employee?.LastName || ''}`.trim() || employee?.EmployeeCode}</h3>

                  {isEditing ? (
                    <div className="employee-details-action-buttons">
                      <button className="employee-details-btn employee-details-btn-success" onClick={handleSave}>
                        <Save size={16} />
                        Save
                      </button>
                      <button className="employee-details-btn employee-details-btn-danger" onClick={handleCancel}>
                        <X size={16} />
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="employee-details-action-buttons">
                      <button className="employee-details-btn employee-details-btn-primary" onClick={handleEdit}>
                        <Edit3 size={16} />
                        Edit
                      </button>
                      <button className="employee-details-btn employee-details-btn-danger" onClick={handleDelete}>
                        <Trash2 size={16} />
                        Delete
                      </button>
                    </div>
                  )}
                </div>

                <div className="employee-details-info-grid">
                  <div className="employee-details-employee-card">
                    <div className="employee-details-header-info">
                      <div className="employee-details-avatar">
                        {(employee?.Name || employee?.FirstName || employee?.EmployeeCode || 'E').charAt(0)}
                      </div>
                      <div>
                        {isEditing ? (
                          <input
                            name="Name"
                            value={editedEmployee.Name || ''}
                            onChange={handleInputChange}
                            className="employee-details-edit-input"
                          />
                        ) : (
                          <h4>{employee?.Name || `${employee?.FirstName || ''} ${employee?.LastName || ''}`.trim() || employee?.EmployeeCode || 'Employee'}</h4>
                        )}
                        {isEditing ? (
                          <input
                            name="Position"
                            value={editedEmployee.Position}
                            onChange={handleInputChange}
                            className="employee-details-edit-input"
                          />
                        ) : (
                          <p>{employee.Position}</p>
                        )}
                      </div>
                    </div>

                    <div className="employee-details-list">
                      <div className="employee-details-detail-row">
                        <span>Employee Code:</span>
                        <span>{employee.EmployeeCode || employee.employeeCode || 'N/A'}</span>
                      </div>
                      {/* 
                          <div className="employee-details-detail-row">
                            <span>Department:</span>
                            {isEditing ? (
                              <input
                                name="Department"
                                value={editedEmployee.Department}
                                onChange={handleInputChange}
                                className="employee-details-edit-input small"
                              />
                            ) : (
                              <span>{employee.Department}</span>
                            )}
                          </div>
                          */}
                      <div className="employee-details-detail-row">
                        <span>Email:</span>
                        {isEditing ? (
                          <input
                            name="Email"
                            value={editedEmployee.Email}
                            onChange={handleInputChange}
                            className="employee-details-edit-input medium"
                          />
                        ) : (
                          <span>{employee.Email}</span>
                        )}
                      </div>
                      <div className="employee-details-detail-row">
                        <span>Phone:</span>
                        {isEditing ? (
                          <input
                            name="Phone"
                            value={editedEmployee.Phone}
                            onChange={handleInputChange}
                            className="employee-details-edit-input medium"
                          />
                        ) : (
                          <span>{employee.Phone}</span>
                        )}
                      </div>
                      <div className="employee-details-detail-row">
                        <span>Hire Date:</span>
                        {isEditing ? (
                          <input
                            type="date"
                            name="HireDate"
                            value={editedEmployee.HireDate ? editedEmployee.HireDate.split('T')[0] : ''}
                            onChange={handleInputChange}
                            className="employee-details-edit-input small"
                          />
                        ) : (
                          <span>{employee.HireDate ?
                            (!isNaN(new Date(employee.HireDate).getTime()) ?
                              new Date(employee.HireDate).toLocaleDateString() :
                              String(employee.HireDate).split('T')[0])
                            : 'N/A'}</span>
                        )}
                      </div>
                      <div className="employee-details-detail-row">
                        <span>Street Address:</span>
                        {isEditing ? (
                          <input
                            name="StreetAddress"
                            value={editedEmployee.StreetAddress || ''}
                            onChange={handleInputChange}
                            className="employee-details-edit-input"
                            placeholder="Enter street address"
                          />
                        ) : (
                          <span>{employee.StreetAddress || 'N/A'}</span>
                        )}
                      </div>
                      <div className="employee-details-detail-row">
                        <span>Apartment, Suite, Unit, Building, or Floor:</span>
                        {isEditing ? (
                          <input
                            name="ApartmentSuite"
                            value={editedEmployee.ApartmentSuite || ''}
                            onChange={handleInputChange}
                            className="employee-details-edit-input"
                            placeholder="Enter apartment/suite (optional)"
                          />
                        ) : (
                          <span>{employee.ApartmentSuite || 'N/A'}</span>
                        )}
                      </div>
                      <div className="employee-details-detail-row">
                        <span>City:</span>
                        {isEditing ? (
                          <input
                            name="City"
                            value={editedEmployee.City || ''}
                            onChange={handleInputChange}
                            className="employee-details-edit-input"
                            placeholder="Enter city"
                          />
                        ) : (
                          <span>{employee.City || 'N/A'}</span>
                        )}
                      </div>
                      <div className="employee-details-detail-row">
                        <span>State:</span>
                        <CountryStateFieldForDetails
                          value={editedEmployee.State || ''}
                          onChange={(value) => setEditedEmployee(prev => ({ ...prev, State: value }))}
                          isEditing={isEditing}
                        />
                      </div>
                      <div className="employee-details-detail-row">
                        <span>Zip Code:</span>
                        {isEditing ? (
                          <input
                            name="ZipCode"
                            value={editedEmployee.ZipCode || ''}
                            onChange={handleInputChange}
                            className="employee-details-edit-input"
                            placeholder="Enter zip code"
                          />
                        ) : (
                          <span>{employee.ZipCode || 'N/A'}</span>
                        )}
                      </div>

                      {/* 
                          <div className="employee-details-detail-row">
                            <span>Employment Type:</span>
                            {isEditing ? (
                              <select
                                name="EmploymentType"
                                value={editedEmployee.EmploymentType}
                                onChange={handleInputChange}
                                className="employee-details-status-select"
                              >
                                <option value="Full-time">Full-time</option>
                                <option value="Part-time">Part-time</option>
                                <option value="Contract">Contract</option>
                              </select>
                            ) : (
                              <span>{employee.EmploymentType}</span>
                            )}
                          </div>
                          <div className="employee-details-detail-row">
                            <span>Status:</span>
                            {isEditing ? (
                              <select
                                name="Status"
                                value={editedEmployee.Status}
                                onChange={handleInputChange}
                                className="employee-details-status-select"
                              >
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                                <option value="On Leave">On Leave</option>
                              </select>
                            ) : (
                              <span className="employee-details-status-badge">{employee.Status}</span>
                            )}
                          </div>
                          */}

                      <div className="employee-details-detail-row">
                        <span>Supervisor:</span>
                        {isEditing ? (
                          <select
                            name="SupervisorEmployeeId"
                            value={editedEmployee.SupervisorEmployeeId || ''}
                            onChange={handleInputChange}
                            className="employee-details-edit-input"
                          >
                            <option value="">Select Supervisor</option>
                            {[...allEmployees].sort((a, b) => (a.Name || '').localeCompare(b.Name || '')).map(emp => (
                              <option key={emp.Id} value={emp.Id}>{emp.Name} ({emp.EmployeeCode || emp.EmployeeId})</option>
                            ))}
                          </select>
                        ) : (
                          <span>{employee.SupervisorName || 'N/A'}</span>
                        )}
                      </div>

                      <div className="employee-details-detail-row">
                        <span>Backup Supervisor:</span>
                        {isEditing ? (
                          <select
                            name="BackupSupervisorEmployeeId"
                            value={editedEmployee.BackupSupervisorEmployeeId || ''}
                            onChange={handleInputChange}
                            className="employee-details-edit-input"
                          >
                            <option value="">Select Backup Supervisor</option>
                            {[...allEmployees].sort((a, b) => (a.Name || '').localeCompare(b.Name || '')).map(emp => (
                              <option key={emp.Id} value={emp.Id}>{emp.Name} ({emp.EmployeeCode || emp.EmployeeId})</option>
                            ))}
                          </select>
                        ) : (
                          <span>{employee.BackupSupervisorName || 'N/A'}</span>
                        )}
                      </div>

                      <div className="employee-details-detail-row">
                        <span>Timesheet Required:</span>
                        {isEditing ? (
                          <input
                            type="checkbox"
                            name="TimesheetRequired"
                            checked={editedEmployee.TimesheetRequired}
                            onChange={(e) => setEditedEmployee(prev => ({ ...prev, TimesheetRequired: e.target.checked }))}
                          />
                        ) : (
                          <span>{employee.TimesheetRequired ? 'Yes' : 'No'}</span>
                        )}
                      </div>

                      <div className="employee-details-detail-row">
                        <span>Mailing Address:</span>
                        {isEditing ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <input
                                type="checkbox"
                                id="useMailingAddressDetail"
                                checked={useMailingAddress}
                                onChange={(e) => setUseMailingAddress(e.target.checked)}
                              />
                              <label htmlFor="useMailingAddressDetail" style={{ fontSize: '12px' }}>Different from Home</label>
                            </div>
                            {useMailingAddress && (
                              <>
                                <input
                                  name="MailingStreetAddress"
                                  value={editedEmployee.MailingStreetAddress || ''}
                                  onChange={handleInputChange}
                                  className="employee-details-edit-input"
                                  placeholder="Street"
                                />
                                <input
                                  name="MailingCity"
                                  value={editedEmployee.MailingCity || ''}
                                  onChange={handleInputChange}
                                  className="employee-details-edit-input"
                                  placeholder="City"
                                />
                                <input
                                  name="MailingZipCode"
                                  value={editedEmployee.MailingZipCode || ''}
                                  onChange={handleInputChange}
                                  className="employee-details-edit-input"
                                  placeholder="Zip Code"
                                />
                              </>
                            )}
                          </div>
                        ) : (
                          <span>{employee.MailingStreetAddress ? `${employee.MailingStreetAddress}, ${employee.MailingCity} ${employee.MailingZipCode}` : 'Same as Home'}</span>
                        )}
                      </div>

                      <div className="employee-details-detail-row">
                        <span>Emergency Contact:</span>
                        {isEditing ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                            <input
                              name="EmergencyContactName"
                              value={editedEmployee.EmergencyContactName || ''}
                              onChange={handleInputChange}
                              className="employee-details-edit-input"
                              placeholder="Name"
                            />
                            <input
                              name="EmergencyContactPhone"
                              value={editedEmployee.EmergencyContactPhone || ''}
                              onChange={handleInputChange}
                              className="employee-details-edit-input"
                              placeholder="Phone"
                            />
                          </div>
                        ) : (
                          <span>{employee.EmergencyContactName ? `${employee.EmergencyContactName} (${employee.EmergencyContactPhone})` : 'N/A'}</span>
                        )}
                      </div>

                      {/* Timesheet Editing Permission Row - Matching first image */}
                      <div className="employee-details-detail-row" style={{ marginTop: '12px', borderTop: '1px solid #f3f4f6', paddingTop: '12px' }}>
                        <span>Timesheet Editing:</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            color: permissionStatus.allowEditing ? '#10b981' : '#ef4444',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}>
                            {permissionStatus.allowEditing ? (
                              <>
                                <CheckCircle2 size={16} />
                                <span>Allowed</span>
                              </>
                            ) : (
                              <>
                                <XCircle size={16} />
                                <span>Locked</span>
                              </>
                            )}
                          </div>

                          <button
                            onClick={() => permissionStatus.allowEditing
                              ? handleRevokeEditingPermission(employee.EmployeeId)
                              : handleGrantEditingPermission(employee.EmployeeId)
                            }
                            style={{
                              backgroundColor: permissionStatus.allowEditing ? '#6b7280' : '#10b981',
                              color: 'white',
                              border: 'none',
                              padding: '4px 12px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '600',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {permissionStatus.allowEditing ? 'Revoke' : 'Allow'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT COLUMN - Payroll Info Panel */}
              <div style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '8px',
                padding: '20px',
                border: '1px solid #e9ecef',
                height: 'fit-content',
                position: 'sticky',
                top: '5px'
              }}>
                {/* Header with Title and Buttons */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    margin: 0,
                    color: '#1f2937'
                  }}>
                    Payroll Info
                  </h3>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    {isEditingPayroll ? (
                      <>
                        <button
                          onClick={handleSavePayrollInfo}
                          style={{
                            backgroundColor: '#188858',
                            color: 'white',
                            border: 'none',
                            padding: '6px 14px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <Save size={14} />
                          Save
                        </button>
                        <button
                          onClick={handleCancelPayrollEdit}
                          style={{
                            backgroundColor: '#6b7280',
                            color: 'white',
                            border: 'none',
                            padding: '6px 14px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <X size={14} />
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setIsEditingPayroll(true)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#188858',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '500',
                          padding: '0',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <Edit3 size={14} />
                        Edit
                      </button>
                    )}
                  </div>
                </div>

                {/* Pay Info Section */}
                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    marginBottom: '12px',
                    color: '#374151'
                  }}>
                    Pay info
                  </h4>

                  {/* Pay Type */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#1f2937'
                    }}>
                      Pay type<span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <select
                      name="payType"
                      value={payrollInfo.payType}
                      onChange={handlePayrollInputChange}
                      disabled={!isEditingPayroll}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '13px',
                        backgroundColor: isEditingPayroll ? 'white' : '#f9fafb',
                        cursor: isEditingPayroll ? 'pointer' : 'not-allowed',
                        color: '#1f2937'
                      }}
                    >
                      <option value="Hourly">Hourly</option>
                      <option value="Salary">Salary</option>
                      <option value="Contract">Contract</option>
                    </select>
                  </div>

                  {/* Basis of Pay */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#1f2937'
                    }}>
                      Basis of pay
                    </label>
                    <select
                      name="basisOfPay"
                      value={payrollInfo.basisOfPay}
                      onChange={handlePayrollInputChange}
                      disabled={!isEditingPayroll}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '13px',
                        backgroundColor: isEditingPayroll ? 'white' : '#f9fafb',
                        cursor: isEditingPayroll ? 'pointer' : 'not-allowed',
                        color: '#1f2937'
                      }}
                    >
                      <option value="Same as Pay Type">Same as Pay Type</option>
                      <option value="Custom">Custom</option>
                    </select>
                  </div>

                  {/* Pay Schedule */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#1f2937'
                    }}>
                      Pay schedule<span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <select
                      name="paySchedule"
                      value={payrollInfo.paySchedule}
                      onChange={handlePayrollInputChange}
                      disabled={!isEditingPayroll}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '13px',
                        backgroundColor: isEditingPayroll ? 'white' : '#f9fafb',
                        cursor: isEditingPayroll ? 'pointer' : 'not-allowed',
                        color: '#1f2937'
                      }}
                    >
                      <option value="Monthly">Monthly</option>
                      <option value="Semi-Monthly">Semi-Monthly</option>
                      <option value="Bi-Weekly">Bi-Weekly</option>
                      <option value="Weekly">Weekly</option>
                    </select>
                  </div>

                  {/* Employment Type */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#1f2937'
                    }}>
                      Employment type
                    </label>
                    <select
                      name="employmentType"
                      value={payrollInfo.employmentType || 'Full-time'}
                      onChange={handlePayrollInputChange}
                      disabled={!isEditingPayroll}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '13px',
                        backgroundColor: isEditingPayroll ? 'white' : '#f9fafb',
                        cursor: isEditingPayroll ? 'pointer' : 'not-allowed',
                        color: '#1f2937'
                      }}
                    >
                      <option value="Full-time">Full-time</option>
                      <option value="Part-time">Part-time</option>
                      <option value="Contract">Contract</option>
                    </select>
                  </div>

                  {/* Seasonal Employee */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#1f2937'
                    }}>
                      Seasonal employee
                    </label>
                    <select
                      name="seasonalEmployee"
                      value={payrollInfo.seasonalEmployee}
                      onChange={handlePayrollInputChange}
                      disabled={!isEditingPayroll}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '13px',
                        backgroundColor: isEditingPayroll ? 'white' : '#f9fafb',
                        cursor: isEditingPayroll ? 'pointer' : 'not-allowed',
                        color: '#1f2937'
                      }}
                    >
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  </div>

                  {/* Owner Officer */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#1f2937'
                    }}>
                      Owner Officer
                    </label>
                    <select
                      name="ownerOfficer"
                      value={payrollInfo.ownerOfficer}
                      onChange={handlePayrollInputChange}
                      disabled={!isEditingPayroll}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '13px',
                        backgroundColor: isEditingPayroll ? 'white' : '#f9fafb',
                        cursor: isEditingPayroll ? 'pointer' : 'not-allowed',
                        color: '#1f2937'
                      }}
                    >
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  </div>
                </div>

                {/* Divider */}
                <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '16px 0' }} />

                {/* Pay Rate Section */}
                <div>
                  <h4 style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    marginBottom: '12px',
                    color: '#374151'
                  }}>
                    Pay rate
                  </h4>

                  {/* Semi-Monthly Salary */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#1f2937'
                    }}>
                      Semi-Monthly salary<span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{
                        padding: '8px 10px',
                        backgroundColor: '#f3f4f6',
                        border: '1px solid #d1d5db',
                        borderRight: 'none',
                        borderRadius: '6px 0 0 6px',
                        color: '#6b7280',
                        fontWeight: '500',
                        fontSize: '13px'
                      }}>
                        $
                      </span>
                      <input
                        type="number"
                        name="semiMonthlySalary"
                        placeholder="0.00"
                        value={payrollInfo.semiMonthlySalary}
                        onChange={handlePayrollInputChange}
                        disabled={!isEditingPayroll}
                        step="0.01"
                        style={{
                          flex: 1,
                          padding: '8px 10px',
                          border: '1px solid #d1d5db',
                          borderLeft: 'none',
                          borderRadius: '0 6px 6px 0',
                          fontSize: '13px',
                          backgroundColor: isEditingPayroll ? 'white' : '#f9fafb',
                          cursor: isEditingPayroll ? 'auto' : 'not-allowed',
                          color: '#1f2937'
                        }}
                      />
                    </div>
                  </div>

                  {/* Hourly Rate */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#1f2937'
                    }}>
                      Hourly rate
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{
                        padding: '8px 10px',
                        backgroundColor: '#f3f4f6',
                        border: '1px solid #d1d5db',
                        borderRight: 'none',
                        borderRadius: '6px 0 0 6px',
                        color: '#6b7280',
                        fontWeight: '500',
                        fontSize: '13px'
                      }}>
                        $
                      </span>
                      <input
                        type="number"
                        name="hourlyRate"
                        placeholder="0.00"
                        value={payrollInfo.hourlyRate}
                        onChange={handlePayrollInputChange}
                        disabled={!isEditingPayroll}
                        step="0.01"
                        style={{
                          flex: 1,
                          padding: '8px 10px',
                          border: '1px solid #d1d5db',
                          borderLeft: 'none',
                          borderRadius: '0 6px 6px 0',
                          fontSize: '13px',
                          backgroundColor: isEditingPayroll ? 'white' : '#f9fafb',
                          cursor: isEditingPayroll ? 'auto' : 'not-allowed',
                          color: '#1f2937'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Internal Timesheets View */}
        {activeView === 'internalTimesheets' && (
          <div className="employee-details-content-section">
            <div className="employee-details-card">
              <h3>Timesheet Overview - {employee.Name}</h3>
              <div className="employee-details-stats-grid">
                <div className="employee-details-stat-card">
                  <span className="employee-details-stat-label">Pending</span>
                  <span className="employee-details-stat-value">{stats.timesheets?.pending || 0}</span>
                </div>
                <div className="employee-details-stat-card">
                  <span className="employee-details-stat-label">Approved</span>
                  <span className="employee-details-stat-value">{stats.timesheets?.approved || 0}</span>
                </div>
                <div className="employee-details-stat-card">
                  <span className="employee-details-stat-label">Rejected</span>
                  <span className="employee-details-stat-value">{stats.timesheets?.rejected || 0}</span>
                </div>
                <div className="employee-details-stat-card">
                  <span className="employee-details-stat-label">Total</span>
                  <span className="employee-details-stat-value">{stats.timesheets?.total || 0}</span>
                </div>
              </div>
            </div>

            <div className="employee-details-card">
              <div className="employee-details-card-header">
                <h3>Timesheets</h3>


                <div className="employee-details-filter-controls">
                  <div className="employee-details-search-container">
                    <Search size={18} />
                    <input
                      type="text"
                      placeholder="Search timesheets..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="Pending">Pending</option>
                    <option value="Approved">Approved</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </div>
              </div>

              {filteredTimesheets.length > 0 ? (
                <div className="employee-details-timesheet-list">
                  {filteredTimesheets.map((timesheet) => (
                    <div
                      key={timesheet.Id || timesheet.id}
                      className="employee-details-timesheet-item"
                      onClick={() => handleViewTimesheetTasks(timesheet.Id || timesheet.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="employee-details-timesheet-header">
                        <div>
                          <h4>Period: {new Date(timesheet.PeriodStart).toLocaleDateString()} - {new Date(timesheet.PeriodEnd).toLocaleDateString()}</h4>
                          <p>Submitted: {new Date(timesheet.SubmittedDate || timesheet.CreatedAt).toLocaleDateString()}</p>
                        </div>

                        <div className="employee-details-timesheet-status">
                          <span className={`employee-details-status-badge ${(timesheet.Status || timesheet.status)?.toLowerCase()}`}>
                            {timesheet.Status || timesheet.status}
                          </span>

                          <div className="employee-details-hours-display">
                            <Clock size={16} />
                            {timesheet.totalHours || timesheet.TotalHours || 0} hrs
                          </div>
                        </div>
                      </div>

                      {(timesheet.Notes || timesheet.notes) && (
                        <div className="employee-details-timesheet-notes">
                          <p><strong>Notes:</strong> {timesheet.Notes || timesheet.notes}</p>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="employee-details-timesheet-actions" style={{
                        display: 'flex',
                        gap: '8px',
                        marginTop: '12px',
                        paddingTop: '12px',
                        borderTop: '1px solid #e5e7eb',
                        flexWrap: 'wrap',
                        alignItems: 'center'
                      }}>
                        {/* View Tasks Button - Always Visible */}
                        <button
                          className="employee-details-action-btn primary"
                          onClick={(e) => { e.stopPropagation(); handleViewTimesheetTasks(timesheet.Id || timesheet.id); }}
                          title="View Tasks"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 16px',
                            backgroundColor: '#188858',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#16a34a'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#188858'}
                        >
                          <Eye size={16} />
                          View Tasks
                        </button>

                        {/* Approve Button - Only show for Pending timesheets */}
                        {(['pending', 'submitted'].includes((timesheet.status || timesheet.Status || '').toLowerCase())) && (
                          <button
                            className="employee-details-action-btn success"
                            onClick={(e) => { e.stopPropagation(); handleApproveInternalTimesheet(timesheet.Id || timesheet.id); }}
                            title="Approve"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '8px 16px',
                              backgroundColor: '#10b981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: '500',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
                          >
                            <CheckCircle2 size={16} />
                            Approve
                          </button>
                        )}

                        {/* Reject Button - Only show for Pending timesheets */}
                        {(['pending', 'submitted'].includes((timesheet.status || timesheet.Status || '').toLowerCase())) && (
                          <button
                            className="employee-details-action-btn warning"
                            onClick={(e) => { e.stopPropagation(); handleRejectInternalTimesheet(timesheet.Id || timesheet.id); }}
                            title="Reject"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '8px 16px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: '500',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}
                          >
                            <XCircle size={16} />
                            Reject
                          </button>
                        )}

                        {/* Delete Button - Show for all statuses, aligned to the right */}
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteInternalTimesheet(timesheet.Id || timesheet.id); }}
                          className="employee-details-action-btn danger"
                          title="Delete"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 16px',
                            backgroundColor: '#6b7280',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            marginLeft: 'auto',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4b5563'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6b7280'}
                        >
                          <Trash2 size={16} />
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="employee-details-empty-state">
                  <FileText size={48} />
                  <p>No timesheets found for this employee.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* External Timesheets View */}
        {/* External Timesheets View */}
        {activeView === 'externalTimesheets' && !shouldHideTabs(company) && (
          <div className="employee-details-content-section">
            <div className="employee-details-card">
              <div className="employee-details-card-header">
                <h3>External Timesheets - {employee.Name}</h3>
              </div>

              {/* Filter and Search Controls */}
              <div className="employee-details-filter-controls" style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                flexWrap: 'wrap',
                alignItems: 'center'
              }}>
                {/* Search Input */}
                <div className="employee-details-search-container" style={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: '1 1 250px',
                  minWidth: '250px',
                  gap: '8px',
                  padding: '8px 12px',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px'
                }}>
                  <Search size={18} style={{ color: '#6b7280' }} />
                  <input
                    type="text"
                    placeholder="Search by file name or period..."
                    value={externalSearchTerm}
                    onChange={(e) => setExternalSearchTerm(e.target.value)}
                    style={{
                      border: 'none',
                      outline: 'none',
                      flex: 1,
                      fontSize: '14px',
                      backgroundColor: 'transparent',
                      color: '#1f2937'
                    }}
                  />
                </div>

                {/* Status Filter */}
                <select
                  value={externalFilterStatus}
                  onChange={(e) => setExternalFilterStatus(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    fontSize: '14px',
                    cursor: 'pointer',
                    color: '#1f2937',
                    fontWeight: '500',
                    minWidth: '150px'
                  }}
                >
                  <option value="all">All Status</option>
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </select>

                {/* Period Filter */}
                <select
                  value={externalFilterPeriod}
                  onChange={(e) => setExternalFilterPeriod(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    fontSize: '14px',
                    cursor: 'pointer',
                    color: '#1f2937',
                    fontWeight: '500',
                    minWidth: '150px'
                  }}
                >
                  <option value="all">All Time</option>
                  <option value="1">Last 1 Month</option>
                  <option value="3">Last 3 Months</option>
                  <option value="6">Last 6 Months</option>
                  <option value="12">Last 12 Months</option>
                </select>

                {/* Clear Filters Button */}
                {(externalSearchTerm || externalFilterStatus !== 'all' || externalFilterPeriod !== 'all') && (
                  <button
                    onClick={() => {
                      setExternalSearchTerm('');
                      setExternalFilterStatus('all');
                      setExternalFilterPeriod('all');
                    }}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      backgroundColor: '#fff3cd',
                      fontSize: '14px',
                      cursor: 'pointer',
                      color: '#856404',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <X size={16} />
                    Clear Filters
                  </button>
                )}
              </div>

              {/* Results Info */}
              <div style={{
                marginBottom: '16px',
                padding: '0 16px',
                fontSize: '14px',
                color: '#6b7280'
              }}>
                Showing <strong>{filteredExternalTimesheets.length}</strong> of <strong>{externalTimesheets.length}</strong> timesheets
              </div>

              {filteredExternalTimesheets && filteredExternalTimesheets.length > 0 ? (
                <div className="employee-details-file-list">
                  <div className="employee-details-file-list-header">
                    <div>File Name</div>
                    <div>Upload Date</div>
                    <div>Period</div>
                    <div>Status</div>
                    <div>Actions</div>
                  </div>

                  {filteredExternalTimesheets.map((timesheet) => {
                    const FileIcon = getFileIcon(timesheet.FileName || 'file.pdf');
                    return (
                      <div key={timesheet.Id} className="employee-details-file-list-item">
                        <div className="employee-details-file-info">
                          <FileIcon size={18} />
                          <span>{timesheet.FileName || 'Unknown File'}</span>
                        </div>
                        <div>{timesheet.UploadDate ? new Date(timesheet.UploadDate).toLocaleDateString() : 'N/A'}</div>
                        <div>{timesheet.Period || 'N/A'}</div>
                        <div>
                          <span className={`employee-details-status-badge ${(timesheet.Status || 'Pending').toLowerCase()}`}>
                            {timesheet.Status || 'Pending'}
                          </span>
                        </div>
                        <div className="employee-details-file-actions">
                          <button
                            className="employee-details-icon-btn"
                            onClick={() => handleDownloadExternalTimesheet(timesheet.Id, timesheet.FileName)}
                            title="Download"
                          >
                            <Download size={16} />
                          </button>

                          {(['pending', 'submitted'].includes((timesheet.Status || '').toLowerCase()) || !timesheet.Status) && (
                            <>
                              <button
                                className="employee-details-icon-btn success"
                                onClick={() => handleApproveExternalTimesheet(timesheet.Id)}
                                title="Approve"
                              >
                                <CheckCircle2 size={16} />
                              </button>
                              <button
                                className="employee-details-icon-btn warning"
                                onClick={() => handleRejectExternalTimesheet(timesheet.Id)}
                                title="Reject"
                              >
                                <XCircle size={16} />
                              </button>
                            </>
                          )}

                          <button
                            onClick={() => deleteExternalTimesheet(timesheet.Id)}
                            className="employee-details-icon-btn danger"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="employee-details-empty-state">
                  <FileSpreadsheet size={48} />
                  <p>
                    {externalTimesheets.length === 0
                      ? 'No external timesheets found for this employee.'
                      : 'No timesheets match your filter criteria.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Statement View */}
        {activeView === 'statement' && !shouldHideTabs(company) && (
          <div className="employee-details-content-section">
            <div className="employee-details-card">
              <div className="employee-details-card-header">
                <h3>Payroll Statement - {employee.Name}</h3>

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {/* FILE UPLOAD BUTTON */}
                  <div style={{ position: 'relative' }}>
                    <label
                      htmlFor="statement-file-upload"
                      className="employee-details-btn employee-details-btn-primary"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: statementFileUpload.isUploading ? 'not-allowed' : 'pointer',
                        opacity: statementFileUpload.isUploading ? 0.6 : 1,
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <Upload size={16} />
                      {statementFileUpload.isUploading
                        ? `Uploading... ${statementFileUpload.progress}%`
                        : 'Import from File'}
                    </label>
                    <input
                      type="file"
                      id="statement-file-upload"
                      style={{ display: 'none' }}
                      onChange={handleStatementFileUpload}
                      accept=".csv,.xlsx,.xls"
                      disabled={statementFileUpload.isUploading}
                    />
                  </div>

                  {/* PROGRESS BAR */}
                  {statementFileUpload.isUploading && (
                    <div style={{
                      width: '200px',
                      height: '6px',
                      backgroundColor: '#e5e7eb',
                      borderRadius: '3px',
                      overflow: 'hidden',
                      alignSelf: 'center'
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${statementFileUpload.progress}%`,
                        backgroundColor: '#10b981',
                        transition: 'width 0.3s ease'
                      }}></div>
                    </div>
                  )}

                  {/* PAY STRUCTURE BUTTON */}
                  <button
                    onClick={handleOpenPayStructure}
                    className="employee-details-btn employee-details-btn-primary"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <BarChart3 size={16} />
                    Pay Structure
                  </button>

                  {/* BULK DELETE BUTTON */}
                  {selectedStatementIds.length > 0 && (
                    <button
                      onClick={handleBulkDeleteStatements}
                      className="employee-details-btn"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        backgroundColor: '#dc2626',
                        color: '#ffffff',
                        border: 'none',
                        cursor: 'pointer'
                      }}
                      title="Delete selected statements"
                    >
                      <Trash2 size={16} />
                      Delete Selected ({selectedStatementIds.length})
                    </button>
                  )}

                  {/* ADD NEW ROW BUTTON */}
                  <button
                    onClick={handleAddNewRow}
                    className={`employee-details-btn ${isAddingNewRow ? 'employee-details-btn-success' : 'employee-details-btn-primary'}`}
                    disabled={statementFileUpload.isUploading}
                  >
                    <Plus size={16} />
                    {isAddingNewRow ? 'Save New Row' : 'Add New Row'}
                  </button>
                </div>
              </div>

              {/* FILE UPLOAD INFO MESSAGE */}
              {!statementFileUpload.isUploading && (
                <div style={{
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #e0f2fe',
                  borderRadius: '6px',
                  padding: '12px 16px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px'
                }}>
                  <AlertCircle size={18} style={{ color: '#0369a1', flexShrink: 0, marginTop: '2px' }} />
                  <div style={{ fontSize: '13px', color: '#0369a1', lineHeight: '1.5' }}>
                    <strong>📄 Import from File:</strong> Upload a CSV or Excel file to automatically populate statement entries.
                    <br />
                    <small>Supported columns: Date, Description, Hours, Pay Rate, Credit, Debit, Balance</small>
                  </div>
                </div>
              )}

              {/* SEARCH & DATE FILTER CONTROLS */}
              <div className="employee-details-filter-controls" style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                flexWrap: 'wrap',
                alignItems: 'center'
              }}>
                {/* Search Input */}
                <div className="employee-details-search-container" style={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: '1 1 250px',
                  minWidth: '250px',
                  gap: '8px',
                  padding: '8px 12px',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px'
                }}>
                  <Search size={18} style={{ color: '#6b7280' }} />
                  <input
                    type="text"
                    placeholder="Search by description or period..."
                    value={statementSearchTerm}
                    onChange={(e) => setStatementSearchTerm(e.target.value)}
                    style={{
                      border: 'none',
                      outline: 'none',
                      flex: 1,
                      fontSize: '14px',
                      backgroundColor: 'transparent',
                      color: '#1f2937'
                    }}
                  />
                </div>

                {/* Start Date Filter */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  minWidth: '180px'
                }}>
                  <label style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#6b7280'
                  }}>
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={statementFilterStartDate}
                    onChange={(e) => setStatementFilterStartDate(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      fontSize: '14px',
                      cursor: 'pointer',
                      color: '#1f2937'
                    }}
                  />
                </div>

                {/* End Date Filter */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  minWidth: '180px'
                }}>
                  <label style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#6b7280'
                  }}>
                    End Date
                  </label>
                  <input
                    type="date"
                    value={statementFilterEndDate}
                    onChange={(e) => setStatementFilterEndDate(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      fontSize: '14px',
                      cursor: 'pointer',
                      color: '#1f2937'
                    }}
                  />
                </div>

                {/* Clear Filters Button */}
                {(statementSearchTerm || statementFilterStartDate || statementFilterEndDate) && (
                  <button
                    onClick={() => {
                      setStatementSearchTerm('');
                      setStatementFilterStartDate('');
                      setStatementFilterEndDate('');
                    }}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      backgroundColor: '#fff3cd',
                      fontSize: '14px',
                      cursor: 'pointer',
                      color: '#856404',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <X size={16} />
                    Clear Filters
                  </button>
                )}
              </div>

              {/* Results Info */}
              <div style={{
                marginBottom: '16px',
                padding: '0 16px',
                fontSize: '14px',
                color: '#6b7280'
              }}>
                Showing <strong>{sortedFilteredStatements.length > 0 ? statementIndexOfFirstRow + 1 : 0}–{Math.min(statementIndexOfLastRow, sortedFilteredStatements.length)}</strong> of <strong>{sortedFilteredStatements.length}</strong> statements {statements.length !== sortedFilteredStatements.length && `(filtered from ${statements.length} total)`}
              </div>

              <div className="employee-details-statement-info" style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '16px',
                padding: '12px 16px',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <div>
                  <span className="employee-details-info-label" style={{ fontWeight: 600, color: '#475569', marginRight: '6px' }}>Vendor:</span>
                  <span style={{ color: '#1e293b', fontWeight: 600 }}>{statementPlacement?.Vendor || statementPlacement?.vendor || 'Alku'}</span>
                </div>
                <div>
                  <span className="employee-details-info-label" style={{ fontWeight: 600, color: '#475569', marginRight: '6px' }}>Bill Rate:</span>
                  <span style={{ color: '#1e293b', fontWeight: 600 }}>${parseFloat(statementPlacement?.BillRate || statementPlacement?.billRate || 57).toFixed(2)}/hr</span>
                </div>
                <div>
                  <span className="employee-details-info-label" style={{ fontWeight: 600, color: '#475569', marginRight: '6px' }}>Candidate Rate:</span>
                  <span style={{ color: '#059669', fontWeight: 600 }}>${parseFloat(statementPlacement?.CandidateRate || statementPlacement?.candidateRate || 45.6).toFixed(2)}/hr</span>
                </div>
                <div>
                  <span className="employee-details-info-label" style={{ fontWeight: 600, color: '#475569', marginRight: '6px' }}>Job Start:</span>
                  <span style={{ color: '#1e293b' }}>{statementPlacement?.JobStartDate ? new Date(statementPlacement.JobStartDate).toLocaleDateString() : '07/20/2026'}</span>
                </div>
                <div>
                  <span className="employee-details-info-label" style={{ fontWeight: 600, color: '#475569', marginRight: '6px' }}>Pay Frequency:</span>
                  <span style={{ color: '#1e293b' }}>Monthly</span>
                </div>
                <div>
                  <span className="employee-details-info-label" style={{ fontWeight: 600, color: '#475569', marginRight: '6px' }}>Employee:</span>
                  <span style={{ color: '#1e293b', fontWeight: 600 }}>{employee.Name}</span>
                </div>
              </div>

              <div className="employee-details-table-container">
                <table className="employee-details-statement-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={paginatedStatements.length > 0 && paginatedStatements.every(stmt => selectedStatementIds.includes(stmt.Id))}
                          onChange={handleToggleSelectAllStatements}
                          title="Select all statements on this page"
                        />
                      </th>
                      <th
                        onClick={() => handleStatementSort('CheckDate')}
                        className="sortable-header"
                        title="Click to sort by Date"
                      >
                        <div className="header-cell-content">
                          <span>Date</span>
                          {renderStatementSortIcon('CheckDate')}
                        </div>
                      </th>
                      <th
                        onClick={() => handleStatementSort('Period')}
                        className="sortable-header"
                        title="Click to sort by Ref"
                      >
                        <div className="header-cell-content">
                          <span>Ref</span>
                          {renderStatementSortIcon('Period')}
                        </div>
                      </th>
                      <th
                        onClick={() => handleStatementSort('Description')}
                        className="sortable-header"
                        title="Click to sort by Description"
                      >
                        <div className="header-cell-content">
                          <span>Description</span>
                          {renderStatementSortIcon('Description')}
                        </div>
                      </th>
                      <th
                        onClick={() => handleStatementSort('Hours')}
                        className="sortable-header text-right"
                        title="Click to sort by Hours"
                      >
                        <div className="header-cell-content justify-end">
                          <span>Hours</span>
                          {renderStatementSortIcon('Hours')}
                        </div>
                      </th>
                      <th
                        onClick={() => handleStatementSort('PayRate')}
                        className="sortable-header text-right"
                        title="Click to sort by Pay Rate"
                      >
                        <div className="header-cell-content justify-end">
                          <span>Pay Rate</span>
                          {renderStatementSortIcon('PayRate')}
                        </div>
                      </th>
                      <th
                        onClick={() => handleStatementSort('Credit')}
                        className="sortable-header text-right"
                        title="Click to sort by Credit"
                      >
                        <div className="header-cell-content justify-end">
                          <span>Credit</span>
                          {renderStatementSortIcon('Credit')}
                        </div>
                      </th>
                      <th
                        onClick={() => handleStatementSort('Debit')}
                        className="sortable-header text-right"
                        title="Click to sort by Debit"
                      >
                        <div className="header-cell-content justify-end">
                          <span>Debit</span>
                          {renderStatementSortIcon('Debit')}
                        </div>
                      </th>
                      <th
                        onClick={() => handleStatementSort('Balance')}
                        className="sortable-header text-right"
                        title="Click to sort by Balance"
                      >
                        <div className="header-cell-content justify-end">
                          <span>Balance</span>
                          {renderStatementSortIcon('Balance')}
                        </div>
                      </th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isAddingNewRow && (
                      <tr className="employee-details-new-row">
                        <td style={{ textAlign: 'center' }}></td>
                        <td>
                          <input
                            type="date"
                            name="date"
                            value={newStatementRow.date}
                            onChange={handleNewRowInputChange}
                            placeholder="MM/DD/YYYY"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            name="retention"
                            value={newStatementRow.retention}
                            onChange={handleNewRowInputChange}
                            placeholder="Retention"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            name="description"
                            value={newStatementRow.description}
                            onChange={handleNewRowInputChange}
                            placeholder="Description"
                          />
                        </td>
                        <td className="text-right">
                          <input
                            type="number"
                            name="hours"
                            value={newStatementRow.hours}
                            onChange={handleNewRowInputChange}
                            placeholder="Hours"
                            step="0.01"
                          />
                        </td>
                        <td className="text-right">
                          <input
                            type="number"
                            name="payRate"
                            value={newStatementRow.payRate}
                            onChange={handleNewRowInputChange}
                            placeholder="Rate"
                            step="0.01"
                          />
                        </td>
                        <td className="text-right">
                          <input
                            type="text"
                            name="credit"
                            value={formatCurrency(newStatementRow.credit)}
                            onChange={handleNewRowInputChange}
                            placeholder="Credit"
                            disabled
                          />
                        </td>
                        <td className="text-right">
                          <input
                            type="number"
                            name="debit"
                            value={newStatementRow.debit}
                            onChange={handleNewRowInputChange}
                            placeholder="Debit"
                            step="0.01"
                          />
                        </td>
                        <td className="text-right">
                          <input
                            type="text"
                            name="balance"
                            value={formatCurrency(newStatementRow.balance)}
                            onChange={handleNewRowInputChange}
                            placeholder="Balance"
                            disabled
                          />
                        </td>
                        <td className="text-center">
                          <div className="employee-details-action-buttons">
                            <button
                              onClick={handleAddNewRow}
                              className="employee-details-icon-btn success"
                            >
                              <Save size={16} />
                            </button>
                            <button
                              onClick={handleCancelNewRow}
                              className="employee-details-icon-btn danger"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}

                    {paginatedStatements.map((statement) => (
                      <tr key={statement.Id} className={editingStatementId === statement.Id ? 'editing-row' : ''}>
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={selectedStatementIds.includes(statement.Id)}
                            onChange={() => handleToggleSelectStatement(statement.Id)}
                            disabled={editingStatementId === statement.Id}
                            title="Select statement for deletion"
                          />
                        </td>
                        <td>
                          {editingStatementId === statement.Id ? (
                            <input
                              type="date"
                              name="CheckDate"
                              value={editingStatement.CheckDate}
                              onChange={handleEditStatementInputChange}
                              style={{ width: '100%', padding: '4px' }}
                            />
                          ) : (
                            formatToMonthDateYear(statement.CheckDate)
                          )}
                        </td>
                        <td>
                          {editingStatementId === statement.Id ? (
                            <input
                              type="text"
                              name="Period"
                              value={editingStatement.Period}
                              onChange={handleEditStatementInputChange}
                              style={{ width: '100%', padding: '4px' }}
                            />
                          ) : (
                            statement.Period
                          )}
                        </td>
                        <td>
                          {editingStatementId === statement.Id ? (
                            <input
                              type="text"
                              name="Description"
                              value={editingStatement.Description}
                              onChange={handleEditStatementInputChange}
                              style={{ width: '100%', padding: '4px' }}
                            />
                          ) : (
                            statement.Description
                          )}
                        </td>
                        <td className="text-right">
                          {editingStatementId === statement.Id ? (
                            <input
                              type="number"
                              name="Hours"
                              value={editingStatement.Hours}
                              onChange={handleEditStatementInputChange}
                              step="0.01"
                              style={{ width: '100%', padding: '4px', textAlign: 'right' }}
                            />
                          ) : (
                            statement.Hours || 0
                          )}
                        </td>
                        <td className="text-right">
                          {editingStatementId === statement.Id ? (
                            <input
                              type="number"
                              name="PayRate"
                              value={editingStatement.PayRate}
                              onChange={handleEditStatementInputChange}
                              step="0.01"
                              style={{ width: '100%', padding: '4px', textAlign: 'right' }}
                            />
                          ) : (
                            statement.PayRate || 0
                          )}
                        </td>
                        <td className="text-right credit">
                          {editingStatementId === statement.Id ? (
                            <input
                              type="number"
                              name="Credit"
                              value={editingStatement.Credit}
                              onChange={handleEditStatementInputChange}
                              step="0.01"
                              disabled
                              style={{ width: '100%', padding: '4px', textAlign: 'right' }}
                            />
                          ) : (
                            statement.Credit || ''
                          )}
                        </td>
                        <td className="text-right debit">
                          {editingStatementId === statement.Id ? (
                            <input
                              type="number"
                              name="Debit"
                              value={editingStatement.Debit}
                              onChange={handleEditStatementInputChange}
                              step="0.01"
                              style={{ width: '100%', padding: '4px', textAlign: 'right' }}
                            />
                          ) : (
                            statement.Debit || ''
                          )}
                        </td>
                        <td className="text-right balance">
                          {editingStatementId === statement.Id ? (
                            <input
                              type="number"
                              name="Balance"
                              value={editingStatement.Balance}
                              onChange={handleEditStatementInputChange}
                              step="0.01"
                              disabled
                              style={{ width: '100%', padding: '4px', textAlign: 'right' }}
                            />
                          ) : (
                            statement.Balance || ''
                          )}
                        </td>
                        <td className="text-center">
                          <div className="employee-details-action-buttons" style={{ gap: '4px' }}>
                            {editingStatementId === statement.Id ? (
                              <>
                                <button
                                  onClick={() => handleSaveEditStatement(statement.Id)}
                                  className="employee-details-icon-btn success"
                                  title="Save"
                                >
                                  <Save size={16} />
                                </button>
                                <button
                                  onClick={handleCancelEditStatement}
                                  className="employee-details-icon-btn danger"
                                  title="Cancel"
                                >
                                  <X size={16} />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleEditStatement(statement)}
                                  className="employee-details-icon-btn primary"
                                  title="Edit"
                                >
                                  <Edit3 size={16} />
                                </button>
                                <button
                                  onClick={() => deleteStatement(statement.Id)}
                                  className="employee-details-icon-btn danger"
                                  title="Delete"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* STATEMENT PAGINATION CONTROLS */}
                {sortedFilteredStatements.length > 0 && (
                  <div className="employee-details-pagination">
                    {/* Rows per page selector */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b', fontSize: '13px' }}>
                      <span>Rows per page:</span>
                      <select
                        value={statementRowsPerPage}
                        onChange={(e) => {
                          setStatementRowsPerPage(Number(e.target.value));
                          setStatementCurrentPage(1);
                        }}
                        style={{
                          padding: '5px 8px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          backgroundColor: '#ffffff',
                          fontSize: '13px',
                          color: '#374151',
                          outline: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span style={{ marginLeft: '8px' }}>
                        Showing {statementIndexOfFirstRow + 1}–{Math.min(statementIndexOfLastRow, sortedFilteredStatements.length)} of {sortedFilteredStatements.length} statements
                      </span>
                    </div>

                    {/* Pagination buttons */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <button
                        type="button"
                        onClick={() => setStatementCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={statementCurrentPage === 1}
                        className="employee-details-page-btn"
                        style={{
                          backgroundColor: statementCurrentPage === 1 ? '#f3f4f6' : '#ffffff',
                          color: statementCurrentPage === 1 ? '#9ca3af' : '#374151',
                          cursor: statementCurrentPage === 1 ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <ChevronLeft size={16} />
                        Previous
                      </button>

                      {/* Numbered page buttons */}
                      {Array.from({ length: totalStatementPages }, (_, i) => i + 1)
                        .filter(page => {
                          return (
                            page === 1 ||
                            page === totalStatementPages ||
                            Math.abs(page - statementCurrentPage) <= 1
                          );
                        })
                        .reduce((acc, page, idx, arr) => {
                          if (idx > 0 && page - arr[idx - 1] > 1) {
                            acc.push('ellipsis-' + page);
                          }
                          acc.push(page);
                          return acc;
                        }, [])
                        .map((item) => {
                          if (typeof item === 'string') {
                            return <span key={item} style={{ padding: '0 4px', color: '#9ca3af' }}>...</span>;
                          }
                          const isCurrent = item === statementCurrentPage;
                          return (
                            <button
                              key={item}
                              type="button"
                              onClick={() => setStatementCurrentPage(item)}
                              className={`employee-details-page-number-btn ${isCurrent ? 'active' : ''}`}
                              style={{
                                border: isCurrent ? '1px solid #10b981' : '1px solid #d1d5db',
                                backgroundColor: isCurrent ? '#10b981' : '#ffffff',
                                color: isCurrent ? '#ffffff' : '#374151',
                                fontWeight: isCurrent ? 600 : 500
                              }}
                            >
                              {item}
                            </button>
                          );
                        })}

                      <button
                        type="button"
                        onClick={() => setStatementCurrentPage(prev => Math.min(prev + 1, totalStatementPages))}
                        disabled={statementCurrentPage === totalStatementPages}
                        className="employee-details-page-btn"
                        style={{
                          backgroundColor: statementCurrentPage === totalStatementPages ? '#f3f4f6' : '#ffffff',
                          color: statementCurrentPage === totalStatementPages ? '#9ca3af' : '#374151',
                          cursor: statementCurrentPage === totalStatementPages ? 'not-allowed' : 'pointer'
                        }}
                      >
                        Next
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Report View */}
        {activeView === 'report' && (
          <div className="employee-details-content-section">
            <div className="employee-details-card">
              <div className="employee-details-card-header">
                <h3>Reports - {employee.Name}</h3>

                <div>
                  <label htmlFor="report-upload" className="employee-details-btn employee-details-btn-primary">
                    <Plus size={16} />
                    Upload Report
                  </label>
                  <input
                    type="file"
                    id="report-upload"
                    style={{ display: 'none' }}
                    onChange={handleReportUpload}
                  />
                </div>
              </div>

              {reports.length > 0 ? (
                <div className="employee-details-file-list">
                  <div className="employee-details-file-list-header">
                    <div>File Name</div>
                    <div>Upload Date</div>
                    <div>Type</div>
                    <div>Description</div>
                    <div>Actions</div>
                  </div>

                  {reports.map((report) => {
                    const FileIcon = getFileIcon(report.FileName);
                    return (
                      <div key={report.Id} className="employee-details-file-list-item">
                        <div className="employee-details-file-info">
                          <FileIcon size={18} />
                          <span>{report.FileName}</span>
                        </div>
                        <div>{new Date(report.UploadDate).toLocaleDateString()}</div>
                        <div>{report.Type}</div>
                        <div>{report.Description}</div>
                        <div className="employee-details-file-actions">
                          <button className="employee-details-icon-btn">
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => deleteReport(report.Id)}
                            className="employee-details-icon-btn danger"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="employee-details-empty-state">
                  <FileText size={48} />
                  <p>No reports found for this employee.</p>
                </div>
              )}
            </div>
          </div>
        )}
        {showPayStructureModal && (
          <div className="pay-structure-modal">
            <div className="pay-structure-modal-content">
              {/* Header */}
              <div className="pay-structure-modal-header">
                <h2 style={{ margin: 0, color: '#1f2937', fontSize: '24px', fontWeight: '600' }}>
                  Pay Structure - {employee?.Name}
                </h2>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  {isEditingPayStructure ? (
                    <>
                      <button
                        onClick={handleSavePayStructure}
                        style={{
                          backgroundColor: '#188858',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}
                      >
                        <Save size={16} style={{ marginRight: '8px' }} />
                        Save
                      </button>
                      <button
                        onClick={handleCancelEditPayStructure}
                        style={{
                          backgroundColor: '#6b7280',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}
                      >
                        <X size={16} style={{ marginRight: '8px' }} />
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleEditPayStructure}
                      style={{
                        backgroundColor: '#188858',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      <Edit3 size={16} style={{ marginRight: '8px' }} />
                      Edit
                    </button>
                  )}
                  <button
                    onClick={handleClosePayStructure}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '24px',
                      cursor: 'pointer',
                      color: '#6b7280',
                      padding: '0',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Body - Scrollable Content */}
              <div className="pay-structure-modal-body">
                {/* Pay Structure Form */}
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{ marginBottom: '16px', color: '#374151' }}>Basic Configuration</h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '16px',
                    marginBottom: '24px'
                  }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                        LCA Amount
                      </label>
                      {isEditingPayStructure ? (
                        <input
                          type="number"
                          name="lca"
                          value={payStructureData.lca}
                          onChange={handlePayStructureInputChange}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px'
                          }}
                        />
                      ) : (
                        <div style={{
                          padding: '8px 12px',
                          backgroundColor: '#f9fafb',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb'
                        }}>
                          ${payStructureData.lca.toLocaleString()}
                        </div>
                      )}
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                        As per LCA Rate
                      </label>
                      <div style={{
                        padding: '8px 12px',
                        backgroundColor: '#f0f9ff',
                        borderRadius: '6px',
                        border: '1px solid #e0f2fe',
                        color: '#0369a1',
                        fontWeight: '600'
                      }}>
                        ${calculatePayStructureValues().asPerLCARate}
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                        Vendor Rate
                      </label>
                      {isEditingPayStructure ? (
                        <input
                          type="number"
                          name="vendorRate"
                          value={payStructureData.vendorRate}
                          onChange={handlePayStructureInputChange}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px'
                          }}
                        />
                      ) : (
                        <div style={{
                          padding: '8px 12px',
                          backgroundColor: '#f9fafb',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb'
                        }}>
                          ${payStructureData.vendorRate}
                        </div>
                      )}
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                        Payroll Mode
                      </label>
                      {isEditingPayStructure ? (
                        <div>
                          <select
                            name="payrollMode"
                            value={payStructureData.payrollMode}
                            onChange={handlePayStructureInputChange}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                          >
                            <option value="Payroll & Perdiem mode">Payroll & Perdiem mode (Recommended)</option>
                            <option value="80/20 Percentage Split">80/20 Percentage Split (80% / 20%)</option>
                            <option value="85/15 Percentage Split">85/15 Percentage Split (85% / 15%)</option>
                            <option value="Equals to 86.25%">Equals to 86.25% Percentage Split</option>
                            <option value="70/30 Percentage Split">70/30 Percentage Split (70% / 30%)</option>
                            <option value="Custom Percentage Split">Custom Percentage Split (%)</option>
                            <option value="Hourly Billing">Hourly Billing (Fixed Rate)</option>
                          </select>

                          {/* Customizable Percentage Split Field */}
                          {(payStructureData.payrollMode?.includes('Percentage') ||
                            payStructureData.payrollMode?.includes('80/20') ||
                            payStructureData.payrollMode?.includes('86.25') ||
                            payStructureData.payrollMode?.includes('70/30') ||
                            payStructureData.payrollMode?.includes('Custom')) && (
                            <div style={{
                              marginTop: '8px',
                              padding: '8px 10px',
                              backgroundColor: '#f0fdf4',
                              border: '1px solid #bbf7d0',
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '8px'
                            }}>
                              <span style={{ fontSize: '13px', fontWeight: 600, color: '#166534' }}>
                                Consultant %:
                              </span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <input
                                  type="number"
                                  step="0.25"
                                  min="1"
                                  max="100"
                                  name="percentageSplit"
                                  value={payStructureData.percentageSplit !== undefined ? payStructureData.percentageSplit : 80}
                                  onChange={handlePayStructureInputChange}
                                  style={{
                                    width: '75px',
                                    padding: '4px 6px',
                                    border: '1px solid #16a34a',
                                    borderRadius: '4px',
                                    fontSize: '13px',
                                    fontWeight: '700',
                                    color: '#15803d',
                                    textAlign: 'right'
                                  }}
                                />
                                <span style={{ fontWeight: 700, color: '#166534' }}>%</span>
                              </div>
                              <span style={{ fontSize: '12px', color: '#15803d', fontWeight: 500 }}>
                                (Company: {(100 - (parseFloat(payStructureData.percentageSplit) || 80)).toFixed(2).replace(/\.00$/, '')}%)
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{
                          padding: '8px 12px',
                          backgroundColor: '#f9fafb',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb',
                          fontWeight: '600',
                          color: '#0369a1'
                        }}>
                          {payStructureData.payrollMode}
                          {(payStructureData.payrollMode?.includes('Percentage') ||
                            payStructureData.payrollMode?.includes('80/20') ||
                            payStructureData.payrollMode?.includes('86.25') ||
                            payStructureData.payrollMode?.includes('Custom')) && (
                            <span style={{ marginLeft: '8px', color: '#059669', fontSize: '12px', fontWeight: 700 }}>
                              ({payStructureData.percentageSplit || 80}% / {(100 - (parseFloat(payStructureData.percentageSplit) || 80)).toFixed(2).replace(/\.00$/, '')}%)
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                        Biweekly Hours
                      </label>
                      {isEditingPayStructure ? (
                        <input
                          type="number"
                          name="biweeklyHours"
                          value={payStructureData.biweeklyHours}
                          onChange={handlePayStructureInputChange}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px'
                          }}
                        />
                      ) : (
                        <div style={{
                          padding: '8px 12px',
                          backgroundColor: '#f9fafb',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb'
                        }}>
                          {payStructureData.biweeklyHours} hours
                        </div>
                      )}
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                        Perdiem Amount (Biweekly)
                      </label>
                      {isEditingPayStructure ? (
                        <input
                          type="number"
                          name="perdiemAmount"
                          value={payStructureData.perdiemAmount !== undefined ? payStructureData.perdiemAmount : 1600}
                          onChange={handlePayStructureInputChange}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px'
                          }}
                        />
                      ) : (
                        <div style={{
                          padding: '8px 12px',
                          backgroundColor: '#fef3c7',
                          borderRadius: '6px',
                          border: '1px solid #fde68a',
                          color: '#92400e',
                          fontWeight: '600'
                        }}>
                          ${(payStructureData.perdiemAmount !== undefined ? payStructureData.perdiemAmount : 1600).toLocaleString()} (${calculatePayStructureValues().perdiemRate}/hr)
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Comparative Pay Structure Table matching Anice_Acct Stmt.xlsx Sheet 2 */}
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{ marginBottom: '16px', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <BarChart3 size={18} style={{ color: '#0369a1' }} />
                    Pay Structure Comparison (Excel Blueprint)
                  </h3>
                  <div style={{
                    overflowX: 'auto',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                  }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #cbd5e1' }}>
                          <th style={{ padding: '12px 14px', textAlign: 'left', color: '#334155', fontWeight: 600 }}>Specification / Component</th>
                          <th style={{ padding: '12px 14px', textAlign: 'right', color: '#334155', fontWeight: 600 }}>
                            {calculatePayStructureValues().mode1.name}
                          </th>
                          <th style={{ padding: '12px 14px', textAlign: 'right', color: '#0369a1', fontWeight: 600, backgroundColor: '#f0f9ff' }}>
                            Mode 2: Payroll & Perdiem ★
                          </th>
                          <th style={{ padding: '12px 14px', textAlign: 'right', color: '#334155', fontWeight: 600 }}>Mode 3: Equals to 86.25%</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '10px 14px', fontWeight: 500, color: '#475569' }}>Consultant Hourly Rate</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: '#1e293b' }}>${calculatePayStructureValues().mode1.rate}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600, color: '#0369a1', backgroundColor: '#f0f9ff' }}>${calculatePayStructureValues().mode2.rate}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: '#1e293b' }}>${calculatePayStructureValues().mode3.rate}</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '10px 14px', fontWeight: 500, color: '#475569' }}>Biweekly Gross ({calculatePayStructureValues().biweeklyHours} hrs)</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: '#1e293b' }}>${calculatePayStructureValues().mode1.biweeklyGross}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: '#1e293b', backgroundColor: '#f0f9ff' }}>${calculatePayStructureValues().mode2.biweeklyGross}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: '#1e293b' }}>${calculatePayStructureValues().mode3.biweeklyGross}</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '10px 14px', fontWeight: 500, color: '#475569' }}>ADP Payroll Gross</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: '#1e293b' }}>${calculatePayStructureValues().mode1.adpPayroll}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: '#1e293b', backgroundColor: '#f0f9ff' }}>${calculatePayStructureValues().mode2.adpPayroll}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: '#1e293b' }}>${calculatePayStructureValues().mode3.adpPayroll}</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '10px 14px', fontWeight: 500, color: '#475569' }}>Estimated Taxable Net Pay</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: '#1e293b' }}>${calculatePayStructureValues().mode1.netPay}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: '#1e293b', backgroundColor: '#f0f9ff' }}>${calculatePayStructureValues().mode2.netPay}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: '#1e293b' }}>${calculatePayStructureValues().mode3.netPay}</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '10px 14px', fontWeight: 500, color: '#475569' }}>Perdiem Amount (Tax-Free)</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: '#94a3b8' }}>$0.00</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600, color: '#16a34a', backgroundColor: '#f0f9ff' }}>
                            ${calculatePayStructureValues().mode2.perdiem} (${calculatePayStructureValues().mode2.perdiemRate}/hr)
                          </td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: '#94a3b8' }}>$0.00</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '10px 14px', fontWeight: 500, color: '#475569' }}>Effective Combined Hourly Rate</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: '#1e293b' }}>${calculatePayStructureValues().mode1.rate}/hr</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600, color: '#0369a1', backgroundColor: '#f0f9ff' }}>${calculatePayStructureValues().mode2.totalRate}/hr</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: '#1e293b' }}>${calculatePayStructureValues().mode3.rate}/hr</td>
                        </tr>
                        <tr style={{ backgroundColor: '#f8fafc', borderTop: '2px solid #cbd5e1' }}>
                          <td style={{ padding: '12px 14px', fontWeight: 700, color: '#0f172a' }}>TOTAL NET TAKE-HOME</td>
                          <td style={{ padding: '12px 14px', textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>${calculatePayStructureValues().mode1.totalNetPay}</td>
                          <td style={{ padding: '12px 14px', textAlign: 'right', fontWeight: 700, color: '#15803d', backgroundColor: '#dcfce7', fontSize: '14px' }}>
                            ${calculatePayStructureValues().mode2.totalNetPay}
                          </td>
                          <td style={{ padding: '12px 14px', textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>${calculatePayStructureValues().mode3.totalNetPay}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Summary */}
                <div style={{
                  padding: '16px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <h4 style={{ marginBottom: '8px', color: '#374151', fontSize: '14px', fontWeight: 600 }}>Active Mode Summary ({payStructureData.payrollMode})</h4>
                  <p style={{ color: '#0f172a', fontSize: '15px', lineHeight: '1.5', margin: '0 0 6px 0', fontWeight: 600 }}>
                    Estimated Take-Home Pay:{' '}
                    <span style={{ color: '#16a34a', fontSize: '16px' }}>
                      ${(payStructureData.payrollMode?.includes('Percentage') ||
                          payStructureData.payrollMode?.includes('80/20') ||
                          payStructureData.payrollMode?.includes('Custom'))
                        ? calculatePayStructureValues().mode1.totalNetPay
                        : payStructureData.payrollMode?.includes('86.25%')
                        ? calculatePayStructureValues().mode3.totalNetPay
                        : calculatePayStructureValues().mode2.totalNetPay}
                    </span>
                  </p>
                  <p style={{ color: '#64748b', fontSize: '13px', lineHeight: '1.5', margin: 0 }}>
                    Calculations follow the standard immigration & billing formulas from <code>Anice_Acct Stmt.xlsx</code> (LCA annual benchmark with 1,980 hours annual divisor and tax-exempt perdiem split).
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Work Deployment History View */}
        {activeView === 'workDeploymentHistory' && (
          <div className="employee-details-content-section">
            <div style={{
              background: '#ffffff',
              borderRadius: '12px',
              padding: '24px 28px',
              border: '1px solid #e2e8f0',
              width: '100%'
            }}>
              {/* Top Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px',
                marginBottom: '20px',
                paddingBottom: '16px',
                borderBottom: '1px solid #e2e8f0'
              }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Briefcase size={20} style={{ color: '#3b5bfd' }} />
                    Work Deployment History
                    {(deployments?.length > 0 || workExperience?.length > 0) && (
                      <span style={{ fontSize: '0.88rem', fontWeight: 500, color: '#64748b' }}>
                        ({deployments?.length > 0 ? `${deployments.length} ${deployments.length === 1 ? 'deployment' : 'deployments'}` : ''}
                        {deployments?.length > 0 && workExperience?.length > 0 ? ', ' : ''}
                        {workExperience?.length > 0 ? `${workExperience.length} ${workExperience.length === 1 ? 'prior role' : 'prior roles'}` : ''})
                      </span>
                    )}
                  </h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '0.84rem', color: '#64748b' }}>
                    Track, manage, and document past roles, client deployments, and detailed project summaries.
                  </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={handleOpenAddDeploymentModal}
                    style={{
                      background: '#3b5bfd',
                      color: '#ffffff',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      boxShadow: '0 1px 3px rgba(15, 118, 110, 0.25)',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#115e59'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#3b5bfd'; }}
                  >
                    <Plus size={16} /> Add Deployment
                  </button>
                </div>
              </div>

              {/* Content Body */}
              {loadingWork ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
                  Loading work deployment history...
                </div>
              ) : isEditingWork ? (
                /* EDIT FORM MODE (Type all work) */
                <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                  {workFormData.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '30px', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                      <p style={{ color: '#64748b', margin: '0 0 12px 0', fontSize: '0.9rem' }}>No roles in form. Click below to add a role.</p>
                      <button
                        type="button"
                        onClick={() => {
                          setWorkFormData([{ JobTitle: '', Company: '', StartDate: '', EndDate: '', IsCurrent: false, Description: '' }]);
                        }}
                        style={{
                          background: '#3b5bfd',
                          color: '#ffffff',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        <Plus size={15} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Add First Role
                      </button>
                    </div>
                  ) : (
                    workFormData.map((item, idx) => (
                      <div
                        key={idx}
                        style={{
                          background: '#f8fafc',
                          borderRadius: '10px',
                          padding: '20px 24px',
                          border: '1px solid #cbd5e1',
                          borderLeft: '4px solid #3b5bfd',
                          position: 'relative'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#3b5bfd', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Role #{idx + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const updated = workFormData.filter((_, i) => i !== idx);
                              setWorkFormData(updated);
                            }}
                            style={{
                              background: '#fee2e2',
                              border: '1px solid #fca5a5',
                              color: '#dc2626',
                              borderRadius: '6px',
                              padding: '4px 10px',
                              fontSize: '0.78rem',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            title="Remove role"
                          >
                            <Trash2 size={13} /> Remove
                          </button>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px', marginBottom: '14px' }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                              Role Name / Job Title *
                            </label>
                            <input
                              type="text"
                              value={item.JobTitle || ''}
                              onChange={(e) => {
                                const updated = [...workFormData];
                                updated[idx] = { ...updated[idx], JobTitle: e.target.value };
                                setWorkFormData(updated);
                              }}
                              placeholder="e.g. SAP MM Lead"
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                fontSize: '0.88rem',
                                outline: 'none'
                              }}
                            />
                          </div>

                          <div>
                            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                              Company / Client Name *
                            </label>
                            <input
                              type="text"
                              value={item.Company || ''}
                              onChange={(e) => {
                                const updated = [...workFormData];
                                updated[idx] = { ...updated[idx], Company: e.target.value };
                                setWorkFormData(updated);
                              }}
                              placeholder="e.g. Apple Inc / Entrans"
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                fontSize: '0.88rem',
                                outline: 'none'
                              }}
                            />
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px', alignItems: 'center', marginBottom: '14px' }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                              Start Date
                            </label>
                            <input
                              type="month"
                              value={item.StartDate ? item.StartDate.substring(0, 7) : ''}
                              onChange={(e) => {
                                const updated = [...workFormData];
                                updated[idx] = { ...updated[idx], StartDate: e.target.value ? `${e.target.value}-01` : '' };
                                setWorkFormData(updated);
                              }}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                fontSize: '0.85rem'
                              }}
                            />
                          </div>

                          <div>
                            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                              End Date
                            </label>
                            <input
                              type="month"
                              value={item.EndDate && !item.IsCurrent ? item.EndDate.substring(0, 7) : ''}
                              disabled={item.IsCurrent}
                              onChange={(e) => {
                                const updated = [...workFormData];
                                updated[idx] = { ...updated[idx], EndDate: e.target.value ? `${e.target.value}-01` : '' };
                                setWorkFormData(updated);
                              }}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                fontSize: '0.85rem',
                                background: item.IsCurrent ? '#f1f5f9' : '#ffffff'
                              }}
                            />
                          </div>

                          <div style={{ paddingTop: '18px' }}>
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', fontWeight: 600, color: '#3b5bfd', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={Boolean(item.IsCurrent)}
                                onChange={(e) => {
                                  const updated = [...workFormData];
                                  updated[idx] = { ...updated[idx], IsCurrent: e.target.checked };
                                  setWorkFormData(updated);
                                }}
                              />
                              Current Role
                            </label>
                          </div>
                        </div>

                        <div>
                          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                            Detailed Summary *
                          </label>
                          <textarea
                            rows={5}
                            value={item.Description || ''}
                            onChange={(e) => {
                              const updated = [...workFormData];
                              updated[idx] = { ...updated[idx], Description: e.target.value };
                              setWorkFormData(updated);
                            }}
                            placeholder="Type detailed project responsibilities, environment, technical architecture, and accomplishments..."
                            style={{
                              width: '100%',
                              padding: '10px 12px',
                              borderRadius: '6px',
                              border: '1px solid #cbd5e1',
                              fontSize: '0.88rem',
                              lineHeight: 1.6,
                              fontFamily: 'inherit',
                              resize: 'vertical'
                            }}
                          />
                        </div>
                      </div>
                    ))
                  )}

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                    <button
                      type="button"
                      disabled={savingWork}
                      onClick={() => {
                        setIsEditingWork(false);
                        setWorkFormData([...workExperience]);
                      }}
                      style={{
                        background: '#f1f5f9',
                        border: '1px solid #cbd5e1',
                        color: '#475569',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={savingWork}
                      onClick={handleSaveWorkExperience}
                      style={{
                        background: '#3b5bfd',
                        border: 'none',
                        color: '#ffffff',
                        padding: '8px 20px',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <Save size={15} />
                      {savingWork ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              ) : (
                /* VIEW MODE - Live Deployments with Placement Pipeline from [hrm].[Deployment] */
                <div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {/* Live Client Deployments from Database Table [hrm].[Deployment] */}
                    {deployments && deployments.length > 0 ? (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#3b5bfd', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Client Deployments ({deployments.length})
                          </span>
                          <span style={{ height: '1px', flex: 1, background: '#e2e8f0' }} />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          {deployments.map((dep, dIdx) => (
                            <div
                              key={dep.deploymentId || dep.id || dIdx}
                              style={{
                                background: '#ffffff',
                                borderRadius: '10px',
                                padding: '20px 24px',
                                border: '1px solid #e2e8f0',
                                borderLeft: '4px solid #3b5bfd',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                              }}
                            >
                              <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                {/* Left Side: Role, Client, Date & Status, Placement Pipeline, Job Description */}
                                {(() => {
                                  const isEditingLeft = editingLeftDepId === (dep.deploymentId || dep.id);

                                  // Compute client nodes (End Client first, then Direct Client)
                                  const clientNodes = dep.clientSide && dep.clientSide.length > 0
                                    ? dep.clientSide
                                    : (dep.clientName ? [{ CompanyName: dep.clientName, DisplayName: dep.clientName, IsEndClient: false }] : []);

                                  // Compute supplier nodes
                                  const supplierNodes = dep.supplierSide && dep.supplierSide.length > 0
                                    ? dep.supplierSide
                                    : [];

                                  const candidateFirstName = (dep.candidateName && dep.candidateName !== 'Consultant')
                                    ? dep.candidateName.split(' ')[0]
                                    : (employee?.FirstName || (employee?.Name ? employee.Name.split(' ')[0] : 'Consultant'));

                                  const renderPipelineChain = () => (
                                    <div style={{
                                      background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
                                      border: '1px solid #cbd5e1',
                                      borderRadius: '10px',
                                      padding: '8px 14px',
                                      marginTop: '12px',
                                      marginBottom: '14px',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '5px',
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                                    }}>
                                      <div style={{
                                        fontSize: '0.68rem',
                                        fontWeight: 700,
                                        color: '#475569',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.06em'
                                      }}>
                                        PLACEMENT PIPELINE
                                      </div>

                                      <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                        gap: '6px'
                                      }}>
                                        {/* 1. Client-Side Nodes (End Client ★ -> Direct Client) */}
                                        {clientNodes.map((cn, idx) => (
                                          <React.Fragment key={cn.DeploymentChainId || idx}>
                                            <span 
                                              style={{
                                                fontSize: '0.76rem',
                                                fontWeight: 600,
                                                padding: '3px 8px',
                                                borderRadius: '6px',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '5px',
                                                whiteSpace: 'nowrap',
                                                background: cn.IsEndClient ? '#fef3c7' : '#dbeafe',
                                                color: cn.IsEndClient ? '#92400e' : '#1d4ed8',
                                                border: cn.IsEndClient ? '1px solid #fde68a' : '1px solid #bfdbfe'
                                              }}
                                              title={cn.IsEndClient ? 'End Client' : 'Direct Client / Intermediary'}
                                            >
                                              <Building2 size={13} />
                                              <span>{cn.DisplayName || cn.CompanyName}</span>
                                              {cn.IsEndClient && (
                                                <span style={{
                                                  fontSize: '0.65rem',
                                                  fontWeight: 700,
                                                  background: '#fde68a',
                                                  color: '#78350f',
                                                  padding: '1px 5px',
                                                  borderRadius: '4px',
                                                  letterSpacing: '0.02em',
                                                  lineHeight: 1.2
                                                }}>
                                                  End Client ★
                                                </span>
                                              )}
                                            </span>
                                            <ChevronLeft size={14} color="#94a3b8" style={{ flexShrink: 0 }} />
                                          </React.Fragment>
                                        ))}

                                        {/* 2. Internal Direct Agency: Prophecy (You) */}
                                        <span 
                                          style={{
                                            fontSize: '0.76rem',
                                            fontWeight: 700,
                                            padding: '3px 9px',
                                            borderRadius: '6px',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            whiteSpace: 'nowrap',
                                            background: '#3b5bfd',
                                            color: '#ffffff',
                                            boxShadow: '0 1px 2px rgba(15, 118, 110, 0.3)'
                                          }}
                                          title="Managing Agency (You)"
                                        >
                                          Prophecy (You)
                                        </span>
                                        <ChevronLeft size={14} color="#94a3b8" style={{ flexShrink: 0 }} />

                                        {/* 3. Supplier / Vendor Nodes */}
                                        {supplierNodes.map((sn, idx) => (
                                          <React.Fragment key={sn.DeploymentChainId || idx}>
                                            <span 
                                              style={{
                                                fontSize: '0.76rem',
                                                fontWeight: 600,
                                                padding: '3px 8px',
                                                borderRadius: '6px',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                whiteSpace: 'nowrap',
                                                background: '#eff6ff',
                                                color: '#1e40af',
                                                border: '1px solid #bfdbfe'
                                              }}
                                              title={sn.IsW2Employer ? 'W2 Employer' : 'Supplier / Vendor'}
                                            >
                                              <span>{sn.DisplayName || sn.CompanyName}</span>
                                            </span>
                                            <ChevronLeft size={14} color="#94a3b8" style={{ flexShrink: 0 }} />
                                          </React.Fragment>
                                        ))}

                                        {/* 4. Consultant / Candidate */}
                                        <span 
                                          style={{
                                            fontSize: '0.76rem',
                                            fontWeight: 600,
                                            padding: '3px 8px',
                                            borderRadius: '6px',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            whiteSpace: 'nowrap',
                                            background: '#ede9fe',
                                            color: '#6d28d9',
                                            border: '1px solid #ddd6fe'
                                          }}
                                          title="Consultant"
                                        >
                                          <User size={12} />
                                          <span>{candidateFirstName}</span>
                                        </span>
                                      </div>
                                    </div>
                                  );

                                  if (isEditingLeft) {
                                    return (
                                      <div style={{ flex: '1 1 500px', minWidth: '300px' }}>
                                        {/* Header with Cancel button */}
                                        <div style={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          marginBottom: '14px',
                                          paddingBottom: '10px',
                                          borderBottom: '1px solid #e2e8f0'
                                        }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Briefcase size={17} color="#3b5bfd" />
                                            <span style={{ fontSize: '0.92rem', fontWeight: 700, color: '#3b5bfd', letterSpacing: '0.03em' }}>
                                              EDIT DEPLOYMENT DETAILS
                                            </span>
                                          </div>
                                          <button
                                            type="button"
                                            className="onb-rate-cancel-btn"
                                            onClick={handleCancelEditLeft}
                                            style={{
                                              background: '#f1f5f9',
                                              color: '#64748b',
                                              border: '1px solid #cbd5e1',
                                              borderRadius: '4px',
                                              padding: '2px 8px',
                                              fontSize: '0.72rem',
                                              fontWeight: 600,
                                              cursor: 'pointer',
                                              transition: 'all 0.2s ease'
                                            }}
                                            title="Cancel Editing"
                                            onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.borderColor = '#fca5a5'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                                          >
                                            ✕ Cancel
                                          </button>
                                        </div>

                                        {/* Edit Form */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                          <div className="onb-rate-edit-field">
                                            <label style={{ fontSize: '0.74rem', fontWeight: 600, color: '#475569' }}>Placed Job Title</label>
                                            <input
                                              type="text"
                                              value={leftFormData.placedJobTitle}
                                              onChange={(e) => handleLeftFieldChange('placedJobTitle', e.target.value)}
                                              placeholder="e.g. Jr Business Analyst"
                                              style={{
                                                padding: '6px 10px',
                                                borderRadius: '6px',
                                                border: '1px solid #cbd5e1',
                                                fontSize: '0.84rem',
                                                color: '#0f172a',
                                                outline: 'none',
                                                background: '#ffffff',
                                                width: '100%',
                                                boxSizing: 'border-box'
                                              }}
                                            />
                                          </div>

                                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
                                            <div className="onb-rate-edit-field">
                                              <label style={{ fontSize: '0.74rem', fontWeight: 600, color: '#475569' }}>Start Date</label>
                                              <TypableDatePicker
                                                value={leftFormData.startDate}
                                                onChange={(val) => handleLeftFieldChange('startDate', val)}
                                                placeholder="YYYY-MM-DD or MM/DD/YYYY"
                                              />
                                            </div>

                                            <div className="onb-rate-edit-field">
                                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <label style={{ fontSize: '0.74rem', fontWeight: 600, color: '#475569' }}>End Date</label>
                                                <label style={{ fontSize: '0.7rem', color: '#3b5bfd', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                                  <input
                                                    type="checkbox"
                                                    checked={leftFormData.isCurrent}
                                                    onChange={(e) => {
                                                      const checked = e.target.checked;
                                                      setLeftFormData(prev => ({
                                                        ...prev,
                                                        isCurrent: checked,
                                                        endDate: checked ? '' : prev.endDate
                                                      }));
                                                    }}
                                                  />
                                                  Present
                                                </label>
                                              </div>
                                              <TypableDatePicker
                                                disabled={leftFormData.isCurrent}
                                                value={leftFormData.isCurrent ? '' : leftFormData.endDate}
                                                onChange={(val) => handleLeftFieldChange('endDate', val)}
                                                placeholder="YYYY-MM-DD or MM/DD/YYYY"
                                              />
                                            </div>

                                            <div className="onb-rate-edit-field">
                                              <label style={{ fontSize: '0.74rem', fontWeight: 600, color: '#475569' }}>Deployment Status</label>
                                              <select
                                                value={leftFormData.status}
                                                onChange={(e) => handleLeftFieldChange('status', e.target.value)}
                                                style={{
                                                  padding: '5px 8px',
                                                  borderRadius: '6px',
                                                  border: '1px solid #cbd5e1',
                                                  fontSize: '0.8rem',
                                                  color: '#0f172a',
                                                  outline: 'none',
                                                  background: '#ffffff',
                                                  width: '100%',
                                                  boxSizing: 'border-box'
                                                }}
                                              >
                                                <option value="ACTIVE">ACTIVE</option>
                                                <option value="COMPLETED">COMPLETED</option>
                                                <option value="TERMINATED">TERMINATED</option>
                                                <option value="ON_HOLD">ON_HOLD</option>
                                              </select>
                                            </div>
                                          </div>

                                          {/* Pipeline Chain */}
                                          {renderPipelineChain()}

                                          <div className="onb-rate-edit-field">
                                            <label style={{ fontSize: '0.74rem', fontWeight: 600, color: '#475569' }}>Project Description & Summary</label>
                                            <textarea
                                              rows={6}
                                              value={leftFormData.notes}
                                              onChange={(e) => handleLeftFieldChange('notes', e.target.value)}
                                              placeholder="Enter project summary or responsibilities..."
                                              style={{
                                                padding: '8px 10px',
                                                borderRadius: '6px',
                                                border: '1px solid #cbd5e1',
                                                fontSize: '0.84rem',
                                                color: '#0f172a',
                                                outline: 'none',
                                                background: '#ffffff',
                                                width: '100%',
                                                boxSizing: 'border-box',
                                                resize: 'vertical',
                                                fontFamily: 'inherit',
                                                lineHeight: 1.5
                                              }}
                                            />
                                          </div>

                                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '4px' }}>
                                            <button
                                              type="button"
                                              disabled={savingLeft}
                                              onClick={handleCancelEditLeft}
                                              style={{
                                                padding: '6px 14px',
                                                fontSize: '0.78rem',
                                                fontWeight: 600,
                                                borderRadius: '6px',
                                                border: '1px solid #cbd5e1',
                                                background: '#ffffff',
                                                color: '#475569',
                                                cursor: 'pointer'
                                              }}
                                            >
                                              Cancel
                                            </button>
                                            <button
                                              type="button"
                                              className="onb-rate-save-btn"
                                              disabled={savingLeft}
                                              onClick={() => handleSaveLeft(dep)}
                                              style={{
                                                padding: '6px 16px',
                                                background: '#3b5bfd',
                                                color: '#ffffff',
                                                border: 'none',
                                                borderRadius: '6px',
                                                fontSize: '0.78rem',
                                                fontWeight: 600,
                                                cursor: savingLeft ? 'not-allowed' : 'pointer',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '5px',
                                                transition: 'all 0.2s ease',
                                                opacity: savingLeft ? 0.7 : 1
                                              }}
                                            >
                                              <CheckCircle2 size={13} /> {savingLeft ? 'Saving...' : 'Save Deployment'}
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  }

                                  // View Mode:
                                  return (
                                    <div style={{ flex: '1 1 500px', minWidth: '300px' }}>
                                      <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        flexWrap: 'wrap',
                                        gap: '10px',
                                        marginBottom: '10px'
                                      }}>
                                        <div>
                                          <div style={{ fontWeight: 700, fontSize: '1.08rem', color: '#0f172a' }}>
                                            {dep.jobTitle || 'Deployed Consultant'}
                                          </div>
                                          <div style={{ fontSize: '0.88rem', color: '#3b5bfd', fontWeight: 600, marginTop: '2px' }}>
                                            {dep.clientName || dep.company || 'Client Deployment'}
                                          </div>
                                        </div>

                                        <div style={{
                                          fontSize: '0.84rem',
                                          color: '#64748b',
                                          fontWeight: 500,
                                          textAlign: 'right',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '8px',
                                          flexWrap: 'wrap'
                                        }}>
                                          <span>
                                            {formatMonthYear(dep.startDate)} — {dep.isCurrent
                                              ? <span style={{ color: '#3b5bfd', fontWeight: 700 }}>Present</span>
                                              : (formatMonthYear(dep.endDate) || 'N/A')}
                                          </span>
                                          <span style={{
                                            fontSize: '0.74rem',
                                            padding: '3px 10px',
                                            borderRadius: '20px',
                                            fontWeight: 700,
                                            background: dep.status === 'ACTIVE' ? '#ccfbf1' : '#f1f5f9',
                                            color: dep.status === 'ACTIVE' ? '#3b5bfd' : '#64748b',
                                            border: dep.status === 'ACTIVE' ? '1px solid #99f6e4' : '1px solid #cbd5e1'
                                          }}>
                                            {dep.status === 'ACTIVE' ? 'Active Deployment' : dep.status}
                                          </span>
                                          <button
                                            type="button"
                                            className="onb-rate-edit-btn"
                                            onClick={() => handleOpenEditDeploymentModal(dep)}
                                            style={{
                                              background: '#3b5bfd',
                                              color: '#ffffff',
                                              border: 'none',
                                              borderRadius: '5px',
                                              padding: '3px 10px',
                                              fontSize: '0.74rem',
                                              fontWeight: 600,
                                              cursor: 'pointer',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: '4px',
                                              transition: 'all 0.2s ease',
                                              boxShadow: '0 1px 2px rgba(15, 118, 110, 0.2)'
                                            }}
                                            title="Edit Deployment (All Details, Rates & Referrals)"
                                            onMouseEnter={(e) => { e.currentTarget.style.background = '#115e59'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.background = '#3b5bfd'; }}
                                          >
                                            <Edit3 size={13} /> Edit Deployment
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => handleDeleteDeployment(dep)}
                                            style={{
                                              background: '#fee2e2',
                                              color: '#dc2626',
                                              border: '1px solid #fca5a5',
                                              borderRadius: '5px',
                                              padding: '3px 7px',
                                              fontSize: '0.72rem',
                                              fontWeight: 600,
                                              cursor: 'pointer',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: '3px',
                                              transition: 'all 0.2s ease'
                                            }}
                                            title="Delete Deployment"
                                            onMouseEnter={(e) => { e.currentTarget.style.background = '#fecaca'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.background = '#fee2e2'; }}
                                          >
                                            <Trash2 size={13} />
                                          </button>
                                        </div>
                                      </div>

                                      {/* Deployment / Placement Pipeline Chain (Matching Image 2 - Read-Only) */}
                                      {renderPipelineChain()}

                                      {/* Project Description & Summary with Read More / Read Less */}
                                      {(() => {
                                        const descText = dep.description || dep.jobDescription || dep.notes || '';
                                        if (!descText) return null;

                                        const currentDepId = dep.deploymentId || dep.id;
                                        const isExpanded = expandedDescDepIds.includes(currentDepId);
                                        const isHtml = /<[a-z][\s\S]*>/i.test(descText);
                                        const plainText = descText.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
                                        const isLong = plainText.length > 180 || descText.length > 180 || (descText.match(/<li|•|\n/gi) || []).length > 4;

                                        return (
                                          <div style={{ marginTop: '10px' }}>
                                            <div
                                              style={{
                                                position: 'relative',
                                                maxHeight: (!isLong || isExpanded) ? 'none' : '200px',
                                                overflow: 'hidden',
                                                transition: 'max-height 0.3s ease'
                                              }}
                                            >
                                              {isHtml ? (
                                                <div
                                                  style={{
                                                    fontSize: '0.86rem',
                                                    color: '#475569',
                                                    lineHeight: 1.65,
                                                    margin: 0
                                                  }}
                                                  dangerouslySetInnerHTML={{ __html: descText }}
                                                />
                                              ) : (
                                                <p style={{
                                                  fontSize: '0.86rem',
                                                  color: '#475569',
                                                  lineHeight: 1.65,
                                                  margin: 0,
                                                  whiteSpace: 'pre-line'
                                                }}>
                                                  {descText}
                                                </p>
                                              )}

                                              {/* Gradient fade overlay when collapsed */}
                                              {isLong && !isExpanded && (
                                                <div
                                                  style={{
                                                    position: 'absolute',
                                                    bottom: 0,
                                                    left: 0,
                                                    right: 0,
                                                    height: '50px',
                                                    background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.95))',
                                                    pointerEvents: 'none'
                                                  }}
                                                />
                                              )}
                                            </div>

                                            {/* Read More / Read Less Button */}
                                            {isLong && (
                                              <button
                                                type="button"
                                                onClick={() => toggleExpandDesc(currentDepId)}
                                                style={{
                                                  background: '#f0fdfa',
                                                  border: '1px solid #99f6e4',
                                                  borderRadius: '6px',
                                                  padding: '4px 12px',
                                                  color: '#3b5bfd',
                                                  fontSize: '0.78rem',
                                                  fontWeight: 650,
                                                  cursor: 'pointer',
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  gap: '5px',
                                                  marginTop: '6px',
                                                  transition: 'all 0.15s ease'
                                                }}
                                                onMouseEnter={(e) => { e.currentTarget.style.background = '#ccfbf1'; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.background = '#f0fdfa'; }}
                                              >
                                                {isExpanded ? (
                                                  <>Read Less <ChevronUp size={14} /></>
                                                ) : (
                                                  <>Read More <ChevronDown size={14} /></>
                                                )}
                                              </button>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  );
                                })()}

                                {/* Right Side: Rate & Referral Details Card (Matching Image 4 for W2, Image 5 for C2C) */}
                                {(() => {
                                  const currentDepId = dep.deploymentId || dep.id;
                                  const isEditingThisCard = editingRateDepId === currentDepId;
                                  const empType = (dep.employmentType || dep.metadata?.employmentType || employee?.EmployeeType || 'W2').toUpperCase();
                                  const isW2 = !isC2CType(empType);

                                  return (
                                    <div
                                      className="onb-rates-referral-card"
                                      style={{
                                        width: '320px',
                                        minWidth: '290px',
                                        flexShrink: 0,
                                        background: '#ffffff',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '10px',
                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                                        overflow: 'hidden'
                                      }}
                                    >
                                      {/* Header with Edit / Cancel */}
                                      <div
                                        className="onb-rates-card-header"
                                        style={{
                                          background: 'linear-gradient(135deg, #f0fdfa, #f8fafc)',
                                          padding: '10px 14px',
                                          borderBottom: '1px solid #e2e8f0',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                          gap: '8px'
                                        }}
                                      >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                          <DollarSign size={16} color="#3b5bfd" />
                                          <h4
                                            style={{
                                              margin: 0,
                                              fontSize: '0.82rem',
                                              fontWeight: 700,
                                              color: '#3b5bfd',
                                              letterSpacing: '0.04em'
                                            }}
                                          >
                                            RATE & REFERRAL DETAILS
                                          </h4>
                                        </div>
                                        {!isEditingThisCard ? (
                                          <button
                                            type="button"
                                            className="onb-rate-edit-btn"
                                            onClick={() => handleStartEditRates(dep)}
                                            style={{
                                              background: '#f0fdf4',
                                              color: '#15803d',
                                              border: '1px solid #bbf7d0',
                                              borderRadius: '4px',
                                              padding: '2px 8px',
                                              fontSize: '0.72rem',
                                              fontWeight: 600,
                                              cursor: 'pointer',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: '3px',
                                              transition: 'all 0.2s ease'
                                            }}
                                            title="Edit Rate & Referral Details"
                                            onMouseEnter={(e) => { e.currentTarget.style.background = '#15803d'; e.currentTarget.style.color = '#ffffff'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.background = '#f0fdf4'; e.currentTarget.style.color = '#15803d'; }}
                                          >
                                            <Edit2 size={12} /> Edit
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            className="onb-rate-cancel-btn"
                                            onClick={handleCancelEditRates}
                                            style={{
                                              background: '#f1f5f9',
                                              color: '#64748b',
                                              border: '1px solid #cbd5e1',
                                              borderRadius: '4px',
                                              padding: '2px 8px',
                                              fontSize: '0.72rem',
                                              fontWeight: 600,
                                              cursor: 'pointer',
                                              transition: 'all 0.2s ease'
                                            }}
                                            title="Cancel Editing"
                                            onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.borderColor = '#fca5a5'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                                          >
                                            ✕ Cancel
                                          </button>
                                        )}
                                      </div>

                                      {/* Body: Edit Mode OR View Mode */}
                                      {isEditingThisCard ? (
                                        <div
                                          className="onb-rates-edit-form"
                                          style={{
                                            padding: '12px 14px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '8px'
                                          }}
                                        >
                                          <div className="onb-rate-edit-field">
                                            <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569' }}>
                                              Client Rate ($/hr)
                                            </label>
                                            <input
                                              type="number"
                                              step="0.01"
                                              value={rateFormData.clientRate}
                                              onChange={(e) => handleRateFieldChange('clientRate', e.target.value, dep)}
                                              placeholder="0.00"
                                            />
                                          </div>
                                          <div className="onb-rate-edit-field">
                                            <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569' }}>
                                              VMS (%)
                                            </label>
                                            <input
                                              type="number"
                                              step="0.01"
                                              value={rateFormData.vmsPct}
                                              onChange={(e) => handleRateFieldChange('vmsPct', e.target.value, dep)}
                                              placeholder="0.00"
                                            />
                                          </div>
                                          <div className="onb-rate-edit-field">
                                            <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569' }}>
                                              Rate After VMS ($/hr)
                                            </label>
                                            <input
                                              type="number"
                                              step="0.01"
                                              value={rateFormData.rateAfterVms}
                                              onChange={(e) => handleRateFieldChange('rateAfterVms', e.target.value, dep)}
                                              placeholder="0.00"
                                            />
                                          </div>

                                          <div className="onb-rates-divider" style={{ height: '1px', background: '#e2e8f0', margin: '2px 0' }} />

                                          {!isW2 && (
                                            <>
                                              <div className="onb-rate-edit-field">
                                                <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569' }}>
                                                  Supplier Rate ($/hr)
                                                </label>
                                                <input
                                                  type="number"
                                                  step="0.01"
                                                  value={rateFormData.supplierRate}
                                                  onChange={(e) => handleRateFieldChange('supplierRate', e.target.value, dep)}
                                                  placeholder="0.00"
                                                />
                                              </div>
                                              <div className="onb-rate-edit-field">
                                                <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569' }}>
                                                  Supplier VMS (%)
                                                </label>
                                                <input
                                                  type="number"
                                                  step="0.01"
                                                  value={rateFormData.supplierVmsPct}
                                                  onChange={(e) => handleRateFieldChange('supplierVmsPct', e.target.value, dep)}
                                                  placeholder="0.00"
                                                />
                                              </div>
                                              <div className="onb-rate-edit-field">
                                                <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569' }}>
                                                  Supplier Rate After VMS ($/hr)
                                                </label>
                                                <input
                                                  type="number"
                                                  step="0.01"
                                                  value={rateFormData.supplierRateAfterVms}
                                                  onChange={(e) => handleRateFieldChange('supplierRateAfterVms', e.target.value, dep)}
                                                  placeholder="0.00"
                                                />
                                              </div>
                                              <div className="onb-rate-edit-field">
                                                <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569' }}>
                                                  Supplier Net Terms (Days)
                                                </label>
                                                <input
                                                  type="number"
                                                  value={rateFormData.supplierNetTermsDays || ''}
                                                  onChange={(e) => handleRateFieldChange('supplierNetTermsDays', e.target.value, dep)}
                                                  placeholder="e.g. 30, 45, 60"
                                                />
                                              </div>
                                              <div className="onb-rates-divider" style={{ height: '1px', background: '#e2e8f0', margin: '2px 0' }} />
                                            </>
                                          )}

                                          <div className="onb-rate-edit-field">
                                            <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569' }}>
                                              Candidate Compliance Pay Rate ($/hr)
                                            </label>
                                            <input
                                              type="number"
                                              step="0.01"
                                              value={rateFormData.compliancePayRate}
                                              onChange={(e) => handleRateFieldChange('compliancePayRate', e.target.value, dep)}
                                              placeholder="0.00"
                                            />
                                          </div>
                                          <div className="onb-rate-edit-field highlight" style={{ background: '#f0fdf4', padding: '6px 8px', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                                            <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#15803d' }}>
                                              Candidate Actual Pay Rate ($/hr)
                                            </label>
                                            <input
                                              type="number"
                                              step="0.01"
                                              value={rateFormData.actualPayRate}
                                              onChange={(e) => handleRateFieldChange('actualPayRate', e.target.value, dep)}
                                              placeholder="0.00"
                                            />
                                          </div>

                                          <div className="onb-rates-divider" style={{ height: '1px', background: '#e2e8f0', margin: '2px 0' }} />

                                          {/* Multi-assign Referral Source */}
                                          <div className="onb-rate-edit-field" style={{ position: 'relative' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                              <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569' }}>
                                                Referral Source(s)
                                              </label>
                                              <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>Assign multiple with individual fees</span>
                                            </div>

                                            <div style={{ position: 'relative', marginBottom: '8px' }}>
                                              <div className="onb-referral-chips-input-container">
                                                <input
                                                  type="text"
                                                  value={rateReferralSearchText}
                                                  onChange={(e) => {
                                                    setRateReferralSearchText(e.target.value);
                                                    setShowRateReferralDropdown(true);
                                                  }}
                                                  onClick={() => setShowRateReferralDropdown(true)}
                                                  onBlur={() => setTimeout(() => setShowRateReferralDropdown(false), 200)}
                                                  onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && rateReferralSearchText.trim()) {
                                                      e.preventDefault();
                                                      handleAddReferralSource(rateReferralSearchText.trim());
                                                    }
                                                  }}
                                                  placeholder="+ Search company or add referral..."
                                                  autoComplete="off"
                                                />
                                              </div>

                                              {showRateReferralDropdown && (
                                                <div className="onb-referral-dropdown-menu">
                                                  {rateReferralSearchText.trim() && !getFilteredCrmCompanies(rateReferralSearchText).some(c => c.displayName.toLowerCase() === rateReferralSearchText.trim().toLowerCase()) && (
                                                    <div
                                                      className="onb-referral-dropdown-item custom-add"
                                                      onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        handleAddReferralSource(rateReferralSearchText.trim());
                                                      }}
                                                    >
                                                      <span style={{ fontWeight: 600, color: '#3b5bfd' }}>+ Add "{rateReferralSearchText.trim()}"</span>
                                                    </div>
                                                  )}
                                                  {getFilteredCrmCompanies(rateReferralSearchText).map((clientObj, index) => {
                                                    const isSelected = (rateFormData.referrals || []).some(r => (r.displayName || '').toLowerCase() === clientObj.displayName.toLowerCase());
                                                    return (
                                                      <div
                                                        key={index}
                                                        className={`onb-referral-dropdown-item ${isSelected ? 'selected' : ''}`}
                                                        onMouseDown={(e) => {
                                                          e.preventDefault();
                                                          if (!isSelected) {
                                                            handleAddReferralSource(clientObj);
                                                          }
                                                        }}
                                                      >
                                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                          <span className="onb-ref-item-title">{clientObj.displayName}</span>
                                                          {clientObj.companyName && (
                                                            <span className="onb-ref-item-sub">{clientObj.companyName}</span>
                                                          )}
                                                        </div>
                                                        {isSelected && <span className="onb-ref-selected-check">✓</span>}
                                                      </div>
                                                    );
                                                  })}
                                                </div>
                                              )}
                                            </div>

                                            {/* Referral Cards */}
                                            {(rateFormData.referrals || []).length > 0 && (
                                              <div className="onb-referral-cards-container">
                                                {rateFormData.referrals.map((refItem, rIdx) => (
                                                  <div key={rIdx} className="onb-referral-item-card">
                                                    <div className="onb-referral-item-card-header">
                                                      <div className="onb-referral-item-title-wrap">
                                                        <UserCheck size={12} color="#3b5bfd" />
                                                        <span className="onb-referral-item-name">{refItem.displayName}</span>
                                                        {refItem.companyName && refItem.companyName !== refItem.displayName && (
                                                          <span className="onb-referral-item-sub">({refItem.companyName})</span>
                                                        )}
                                                      </div>
                                                      <button
                                                        type="button"
                                                        className="onb-referral-remove-btn"
                                                        onClick={() => handleRemoveReferralSource(rIdx)}
                                                        title={`Remove ${refItem.displayName}`}
                                                      >
                                                        ✕ Remove
                                                      </button>
                                                    </div>

                                                    <div className="onb-referral-item-fields-grid">
                                                      <div className="onb-referral-input-wrapper">
                                                        <label>Fee / Rate ($)</label>
                                                        <input
                                                          type="number"
                                                          step="0.01"
                                                          placeholder="0.00"
                                                          value={refItem.referralFee}
                                                          onChange={(e) => handleReferralItemChange(rIdx, 'referralFee', e.target.value)}
                                                        />
                                                      </div>
                                                      <div className="onb-referral-input-wrapper">
                                                        <label>Net Terms (Days)</label>
                                                        <input
                                                          type="number"
                                                          placeholder="e.g. 30, 45, 60"
                                                          value={refItem.referralNetTermsDays || ''}
                                                          onChange={(e) => handleReferralItemChange(rIdx, 'referralNetTermsDays', e.target.value)}
                                                        />
                                                      </div>
                                                      <div className="onb-referral-input-wrapper">
                                                        <label>Referrer Notes</label>
                                                        <input
                                                          type="text"
                                                          placeholder="e.g. Terms..."
                                                          value={refItem.notes}
                                                          onChange={(e) => handleReferralItemChange(rIdx, 'notes', e.target.value)}
                                                        />
                                                      </div>
                                                    </div>
                                                  </div>
                                                ))}

                                                <div className="onb-referral-total-summary">
                                                  <span className="onb-referral-total-label">Total Referral Cost:</span>
                                                  <span className="onb-referral-total-val">
                                                    ${Number(rateFormData.referralFee || 0).toFixed(2)}/hr
                                                  </span>
                                                </div>
                                              </div>
                                            )}
                                          </div>

                                          {/* Expenses Field in Edit Mode */}
                                          <div className="onb-rate-edit-field" style={{ marginTop: '6px' }}>
                                            <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569' }}>Expenses ($/hr)</label>
                                            <input
                                              type="number"
                                              step="0.01"
                                              value={rateFormData.expenses}
                                              onChange={(e) => handleRateFieldChange('expenses', e.target.value, dep)}
                                              placeholder="0.00"
                                            />
                                          </div>

                                          {/* Auto-computed Profit Margin Preview - Moved immediately before Notes */}
                                          <div className="onb-rate-edit-field highlight" style={{ background: '#f0fdf4', padding: '6px 10px', borderRadius: '6px', border: '1px solid #bbf7d0', marginTop: '6px', marginBottom: '6px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                              <label style={{ color: '#166534', fontWeight: 700, margin: 0, fontSize: '0.72rem' }}>
                                                Profit Margin ($/hr) — Auto Computed
                                              </label>
                                              <span style={{ fontSize: '0.66rem', color: '#15803d', fontWeight: 600 }}>
                                                {isW2 ? '(W2 Net: Rate After VMS - Pay Rate - expenses - Referrals)' : '(C2C Net: Rate After VMS - Supplier Rate After VMS - expenses - Referrals)'}
                                              </span>
                                            </div>
                                            <div style={{ fontSize: '1rem', fontWeight: 800, color: '#15803d', marginTop: '3px' }}>
                                              ${rateFormData.margin != null && rateFormData.margin !== '' ? Number(rateFormData.margin).toFixed(2) : '0.00'}
                                            </div>
                                          </div>

                                          <div className="onb-rates-divider" style={{ height: '1px', background: '#e2e8f0', margin: '2px 0' }} />

                                          {/* Notes in Edit Mode */}
                                          <div className="onb-rate-edit-field">
                                            <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569' }}>
                                              General Placement & Rate Notes
                                            </label>
                                            <textarea
                                              rows={3}
                                              className="onb-rate-notes-textarea"
                                              value={rateFormData.notes}
                                              onChange={(e) => handleRateFieldChange('notes', e.target.value, dep)}
                                              placeholder="Add general placement notes or rate comments..."
                                            />
                                          </div>

                                          <div className="onb-rates-edit-actions" style={{ marginTop: '6px' }}>
                                            <button
                                              type="button"
                                              className="onb-rate-save-btn"
                                              disabled={savingRates}
                                              onClick={() => handleSaveRates(dep)}
                                              style={{
                                                width: '100%',
                                                padding: '7px 12px',
                                                background: '#3b5bfd',
                                                color: '#ffffff',
                                                border: 'none',
                                                borderRadius: '6px',
                                                fontSize: '0.78rem',
                                                fontWeight: 600,
                                                cursor: savingRates ? 'not-allowed' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '5px',
                                                transition: 'all 0.2s ease',
                                                opacity: savingRates ? 0.7 : 1
                                              }}
                                            >
                                              <CheckCircle2 size={13} /> {savingRates ? 'Saving...' : 'Save Details'}
                                            </button>
                                          </div>
                                        </div>
                                      ) : (
                                        /* VIEW MODE - Matching Image 4 (W2) and Image 5 (C2C) */
                                        <div
                                          className="onb-rates-breakdown-list"
                                          style={{
                                            padding: '12px 14px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '9px',
                                            fontSize: '0.82rem'
                                          }}
                                        >
                                          <div className="onb-rate-item">
                                            <span className="onb-rate-label">Client Rate</span>
                                            <span className="onb-rate-val">
                                              {dep.clientRate != null ? `$${Number(dep.clientRate).toFixed(2)}/hr` : (dep.billRate != null ? `$${Number(dep.billRate).toFixed(2)}/hr` : '-')}
                                            </span>
                                          </div>

                                          <div className="onb-rate-item">
                                            <span className="onb-rate-label">VMS %</span>
                                            <span className="onb-rate-val">
                                              {dep.vmsPct != null && dep.vmsPct !== '' ? `${dep.vmsPct}%` : '0%'}
                                            </span>
                                          </div>

                                          <div className="onb-rate-item">
                                            <span className="onb-rate-label">Rate After VMS</span>
                                            <span className="onb-rate-val">
                                              {dep.rateAfterVms != null ? `$${Number(dep.rateAfterVms).toFixed(2)}/hr` : '-'}
                                            </span>
                                          </div>

                                          <div className="onb-rates-divider" style={{ height: '1px', background: '#f1f5f9', margin: '2px 0' }}></div>

                                          {!isW2 && (
                                            <>
                                              <div className="onb-rate-item">
                                                <span className="onb-rate-label">Supplier Rate</span>
                                                <span className="onb-rate-val">
                                                  {dep.supplierRate != null ? `$${Number(dep.supplierRate).toFixed(2)}/hr` : (dep.payRate != null ? `$${Number(dep.payRate).toFixed(2)}/hr` : '-')}
                                                </span>
                                              </div>

                                              <div className="onb-rate-item">
                                                <span className="onb-rate-label">Supplier VMS %</span>
                                                <span className="onb-rate-val">
                                                  {dep.supplierVmsPct != null && dep.supplierVmsPct !== '' ? `${dep.supplierVmsPct}%` : '0%'}
                                                </span>
                                              </div>

                                              <div className="onb-rate-item">
                                                <span className="onb-rate-label">Supplier Rate After VMS</span>
                                                <span className="onb-rate-val" style={{ color: '#3b5bfd', fontWeight: 650 }}>
                                                  {(() => {
                                                    const sAfter = dep.supplierRateAfterVms ?? (dep.supplierRate && dep.supplierVmsPct ? (dep.supplierRate * (1 - dep.supplierVmsPct / 100)) : null);
                                                    return sAfter ? `$${Number(sAfter).toFixed(2)}/hr` : '-';
                                                  })()}
                                                </span>
                                              </div>

                                              <div className="onb-rate-item">
                                                <span className="onb-rate-label">Supplier Net Terms</span>
                                                <span className="onb-rate-val">
                                                  {dep.supplierNetTermsDays ? `${dep.supplierNetTermsDays} Days` : '-'}
                                                </span>
                                              </div>

                                              <div className="onb-rates-divider" style={{ height: '1px', background: '#f1f5f9', margin: '2px 0' }}></div>
                                            </>
                                          )}

                                          <div className="onb-rate-item">
                                            <span className="onb-rate-label">Candidate Compliance Pay Rate</span>
                                            <span className="onb-rate-val">
                                              {dep.compliancePayRate != null ? `$${Number(dep.compliancePayRate).toFixed(2)}/hr` : '-'}
                                            </span>
                                          </div>

                                          <div className="onb-rate-item highlight" style={{ background: '#f0fdf4', padding: '4px 6px', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                                            <span className="onb-rate-label" style={{ color: '#15803d', fontWeight: 600 }}>Candidate Actual Pay Rate</span>
                                            <span className="onb-rate-val highlight" style={{ color: '#15803d', fontWeight: 700 }}>
                                              {(() => {
                                                if (dep.actualPayRate != null && dep.actualPayRate !== '') {
                                                  return `$${Number(dep.actualPayRate).toFixed(2)}/hr`;
                                                }
                                                if (isW2 && dep.payRate != null) {
                                                  return `$${Number(dep.payRate).toFixed(2)}/hr`;
                                                }
                                                return isW2 ? '-' : '$0.00/hr';
                                              })()}
                                            </span>
                                          </div>

                                          <div className="onb-rates-divider" style={{ height: '1px', background: '#f1f5f9', margin: '2px 0' }}></div>

                                          {/* Referral Breakdown */}
                                          {(() => {
                                            const refsList = Array.isArray(dep.referrals) && dep.referrals.length > 0
                                              ? dep.referrals
                                              : null;

                                            const assignedList = (dep.referralSource || '')
                                              .split(',')
                                              .map(s => s.trim())
                                              .filter(Boolean);

                                            const hasStructuredRefs = refsList && refsList.length > 0;
                                            const hasAssignedList = assignedList.length > 0;
                                            const hasAnyReferral = hasStructuredRefs || hasAssignedList;

                                            if (!hasAnyReferral) {
                                              return (
                                                <div className="onb-rate-item">
                                                  <span className="onb-rate-label">Referral Breakdown</span>
                                                  <span className="onb-rate-val">None</span>
                                                </div>
                                              );
                                            }

                                            const totalRefFee = hasStructuredRefs
                                              ? refsList.reduce((sum, r) => sum + (parseFloat(r.ReferralFee ?? r.referralFee) || 0), 0)
                                              : (parseFloat(dep.referralFee) || 0);

                                            return (
                                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%' }}>
                                                <div className="onb-rate-item" style={{ marginBottom: 0 }}>
                                                  <span className="onb-rate-label">Referral Breakdown</span>
                                                  <span className="onb-rate-val" style={{ color: '#3b5bfd', fontWeight: 700 }}>
                                                    {totalRefFee > 0 ? `$${totalRefFee.toFixed(2)}/hr` : (hasAssignedList ? 'Assigned' : '-')}
                                                  </span>
                                                </div>

                                                {hasStructuredRefs ? (
                                                  <div className="onb-referral-view-list" style={{ marginTop: 0 }}>
                                                    {refsList.map((ref, rIdx) => (
                                                      <div key={rIdx} className="onb-referral-view-row">
                                                        <div className="onb-referral-view-header">
                                                          <span className="onb-referral-view-name">
                                                            <UserCheck size={12} color="#3b5bfd" />
                                                            {ref.DisplayName || ref.CompanyName || ref.displayName}
                                                            {(ref.ReferralNetTermsDays != null && ref.ReferralNetTermsDays !== '') || (ref.referralNetTermsDays != null && ref.referralNetTermsDays !== '') ? (
                                                              <span className="onb-referral-terms-tag">
                                                                Net Terms: {ref.ReferralNetTermsDays || ref.referralNetTermsDays} Days
                                                              </span>
                                                            ) : null}
                                                          </span>
                                                          <span className="onb-referral-view-fee">
                                                            {(ref.ReferralFee != null && ref.ReferralFee !== '') || (ref.referralFee != null && ref.referralFee !== '')
                                                              ? `$${Number(ref.ReferralFee ?? ref.referralFee).toFixed(2)}/hr`
                                                              : '-'}
                                                          </span>
                                                        </div>
                                                        {(ref.Notes || ref.notes) && (
                                                          <div className="onb-referral-view-notes">
                                                            "{ref.Notes || ref.notes}"
                                                          </div>
                                                        )}
                                                      </div>
                                                    ))}
                                                    {refsList.length > 1 && (
                                                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', background: '#f0fdfa', borderRadius: '4px', marginTop: '2px', border: '1px solid #ccfbf1' }}>
                                                        <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#3b5bfd' }}>Total Fee:</span>
                                                        <span style={{ fontSize: '0.74rem', fontWeight: 700, color: '#3b5bfd' }}>
                                                          ${totalRefFee.toFixed(2)}/hr
                                                        </span>
                                                      </div>
                                                    )}
                                                  </div>
                                                ) : (
                                                  <div className="onb-referral-chips-wrap" style={{ justifyContent: 'flex-start', maxWidth: '100%', marginTop: '2px' }}>
                                                    {assignedList.map((refItem, rIdx) => (
                                                      <span key={rIdx} className="onb-referral-pill">
                                                        <UserCheck size={11} />
                                                        <span>{refItem}</span>
                                                      </span>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })()}

                                          {/* Expenses Row in View Mode */}
                                          <div className="onb-rate-item">
                                            <span className="onb-rate-label">Expenses</span>
                                            <span className="onb-rate-val">
                                              {dep.expenses != null && dep.expenses !== '' && Number(dep.expenses) > 0 
                                                ? `${Number(dep.expenses).toFixed(2)}/hr` 
                                                : (dep.Expenses != null && dep.Expenses !== '' && Number(dep.Expenses) > 0 ? `${Number(dep.Expenses).toFixed(2)}/hr` : '-')}
                                            </span>
                                          </div>

                                          {/* Profit Margin in View Mode - Placed immediately before Notes */}
                                          <div className="onb-rate-item highlight" style={{ background: '#f0fdf4', borderRadius: '6px', padding: '6px 8px', marginTop: '6px', marginBottom: '6px', border: '1px solid #bbf7d0' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                              <span className="onb-rate-label" style={{ color: '#166534', fontWeight: 700 }}>Profit Margin</span>
                                              <span style={{ fontSize: '0.68rem', color: '#15803d' }}>
                                                {isW2 ? '(W2 Net: Rate After VMS - Pay Rate - expenses - Referrals)' : '(C2C Net: Rate After VMS - Supplier Rate After VMS - expenses - Referrals)'}
                                              </span>
                                            </div>
                                            <span className="onb-rate-val highlight" style={{ color: '#15803d', fontWeight: 800, fontSize: '0.94rem' }}>
                                              {(() => {
                                                const sAfter = dep.supplierRateAfterVms ?? (dep.supplierRate && dep.supplierVmsPct ? (dep.supplierRate * (1 - dep.supplierVmsPct / 100)) : null);
                                                const calcM = calculateMargin(
                                                  dep.rateAfterVms,
                                                  sAfter,
                                                  dep.actualPayRate ?? (isW2 ? dep.payRate : '0.00'),
                                                  empType,
                                                  dep.referrals,
                                                  dep.referralFee,
                                                  dep.expenses != null ? dep.expenses : (dep.Expenses != null ? dep.Expenses : 0)
                                                );
                                                if (calcM != null && calcM !== '') {
                                                  return `${Number(calcM).toFixed(2)}/hr`;
                                                }
                                                return (dep.margin != null && dep.margin !== '') ? `${Number(dep.margin).toFixed(2)}/hr` : '-';
                                              })()}
                                            </span>
                                          </div>

                                          {/* Dedicated Notes Section in View Mode */}
                                          <div className="onb-rates-divider" style={{ height: '1px', background: '#f1f5f9', margin: '2px 0' }}></div>
                                          <div className="onb-rate-notes-box">
                                            <div className="onb-rate-notes-title">
                                              <FileText size={12} color="#3b5bfd" />
                                              <span>Notes</span>
                                            </div>
                                            <div className="onb-rate-notes-body">
                                              {(dep.rateNotes || dep.notes) && String(dep.rateNotes || dep.notes).trim() ? (
                                                dep.rateNotes || dep.notes
                                              ) : (
                                                <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>No notes added</span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div style={{
                        padding: '48px 24px',
                        textAlign: 'center',
                        background: '#ffffff',
                        borderRadius: '12px',
                        border: '1px dashed #cbd5e1'
                      }}>
                        <Briefcase size={38} color="#94a3b8" style={{ marginBottom: '10px' }} />
                        <h4 style={{ fontSize: '1.02rem', fontWeight: 700, color: '#1e293b', margin: '0 0 6px 0' }}>
                          No Client Deployments Found
                        </h4>
                        <p style={{ fontSize: '0.86rem', color: '#64748b', margin: '0 0 16px 0', maxWidth: '460px', marginLeft: 'auto', marginRight: 'auto' }}>
                          No active or past client deployments are recorded in the HRM Deployment table for this employee.
                        </p>
                        <button
                          type="button"
                          onClick={handleOpenAddDeploymentModal}
                          style={{
                            background: '#3b5bfd',
                            color: '#ffffff',
                            border: 'none',
                            padding: '8px 18px',
                            borderRadius: '6px',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 1px 3px rgba(15, 118, 110, 0.25)'
                          }}
                        >
                          <Plus size={16} /> Add First Deployment
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Full Add / Edit Deployment Modal */}
        {showDeploymentModal && (
          <div className="pay-structure-modal" style={{ zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)' }}>
            <div
              className="pay-structure-modal-content"
              style={{
                maxWidth: '860px',
                width: '92%',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '14px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                overflow: 'hidden',
                background: '#ffffff',
                border: '1px solid #cbd5e1'
              }}
            >
              {/* Modal Header */}
              <div
                className="pay-structure-modal-header"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: 'linear-gradient(135deg, #f0fdfa, #f8fafc)',
                  padding: '18px 26px',
                  borderBottom: '1px solid #e2e8f0'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    background: '#ccfbf1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#3b5bfd'
                  }}>
                    <Briefcase size={20} />
                  </div>
                  <div>
                    <h2 style={{ margin: 0, color: '#0f172a', fontSize: '1.22rem', fontWeight: 700 }}>
                      {isEditingDeployment ? 'Edit Deployment Details' : 'Add New Client Deployment'}
                    </h2>
                    <p style={{ margin: '2px 0 0 0', fontSize: '0.8rem', color: '#64748b' }}>
                      {employee?.FirstName || employee?.Name ? `Employee: ${employee.FirstName || employee.Name}` : 'Configure deployment role, placement chain, rates & referral details.'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDeploymentModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '22px',
                    cursor: 'pointer',
                    color: '#64748b',
                    padding: '4px 8px',
                    borderRadius: '6px'
                  }}
                  title="Close"
                >
                  ✕
                </button>
              </div>

              {/* Modal Body (Scrollable) */}
              <div
                className="pay-structure-modal-body"
                style={{
                  padding: '24px 28px',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '24px'
                }}
              >
                {/* 1. Placement & Client Details Section */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                    <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#3b5bfd', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      1. Placement & Organization Pipeline
                    </span>
                    <span style={{ height: '1px', flex: 1, background: '#e2e8f0' }} />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                        Placed Job Title / Role *
                      </label>
                      <input
                        type="text"
                        value={deploymentModalData.placedJobTitle}
                        onChange={(e) => handleDeploymentModalChange('placedJobTitle', e.target.value)}
                        placeholder="e.g. Lead Cloud Architect / SAP MM Lead"
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '0.85rem',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>

                    {/* Direct Client Autocomplete */}
                    <div style={{ position: 'relative' }}>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                        Direct Client / Account *
                      </label>
                      <input
                        type="text"
                        value={deploymentModalData.clientName}
                        onChange={(e) => {
                          handleDeploymentModalChange('clientName', e.target.value);
                          setShowClientDropdown(true);
                        }}
                        onFocus={() => setShowClientDropdown(true)}
                        onBlur={() => setTimeout(() => setShowClientDropdown(false), 220)}
                        placeholder="Select or enter Client company"
                        autoComplete="off"
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '0.85rem',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                      {showClientDropdown && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          zIndex: 1050,
                          maxHeight: '190px',
                          overflowY: 'auto',
                          backgroundColor: '#ffffff',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)',
                          marginTop: '2px'
                        }}>
                          {getFilteredCrmCompanies(deploymentModalData.clientName).length > 0 ? (
                            getFilteredCrmCompanies(deploymentModalData.clientName).map((c, idx) => (
                              <div
                                key={c.id || idx}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setDeploymentModalData(prev => ({
                                    ...prev,
                                    clientId: c.id,
                                    clientName: c.displayName
                                  }));
                                  setShowClientDropdown(false);
                                }}
                                style={{
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f1f5f9',
                                  fontSize: '0.82rem',
                                  display: 'flex',
                                  justifyContent: 'space-between'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                              >
                                <span style={{ fontWeight: 600, color: '#1f2937' }}>{c.displayName}</span>
                                {c.companyName && <span style={{ color: '#64748b', fontSize: '0.75rem' }}>{c.companyName}</span>}
                              </div>
                            ))
                          ) : (
                            <div style={{ padding: '8px 12px', fontSize: '0.78rem', color: '#94a3b8' }}>
                              Type to enter client name
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* End Client Autocomplete (Optional) */}
                    <div style={{ position: 'relative' }}>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                        End Client (Optional)
                      </label>
                      <input
                        type="text"
                        value={deploymentModalData.endClientName}
                        onChange={(e) => {
                          handleDeploymentModalChange('endClientName', e.target.value);
                          setShowEndClientDropdown(true);
                        }}
                        onFocus={() => setShowEndClientDropdown(true)}
                        onBlur={() => setTimeout(() => setShowEndClientDropdown(false), 220)}
                        placeholder="e.g. Apple, Walmart (if different from Direct Client)"
                        autoComplete="off"
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '0.85rem',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                      {showEndClientDropdown && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          zIndex: 1050,
                          maxHeight: '190px',
                          overflowY: 'auto',
                          backgroundColor: '#ffffff',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)',
                          marginTop: '2px'
                        }}>
                          {getFilteredCrmCompanies(deploymentModalData.endClientName).length > 0 ? (
                            getFilteredCrmCompanies(deploymentModalData.endClientName).map((c, idx) => (
                              <div
                                key={c.id || idx}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setDeploymentModalData(prev => ({
                                    ...prev,
                                    endClientId: c.id,
                                    endClientName: c.displayName
                                  }));
                                  setShowEndClientDropdown(false);
                                }}
                                style={{
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f1f5f9',
                                  fontSize: '0.82rem',
                                  display: 'flex',
                                  justifyContent: 'space-between'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                              >
                                <span style={{ fontWeight: 600, color: '#1f2937' }}>{c.displayName}</span>
                                {c.companyName && <span style={{ color: '#64748b', fontSize: '0.75rem' }}>{c.companyName}</span>}
                              </div>
                            ))
                          ) : (
                            <div style={{ padding: '8px 12px', fontSize: '0.78rem', color: '#94a3b8' }}>
                              Type to enter end client
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Supplier / Vendor (Optional) */}
                    <div style={{ position: 'relative' }}>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                        Supplier / Vendor (Optional)
                      </label>
                      <input
                        type="text"
                        value={deploymentModalData.supplierName}
                        onChange={(e) => {
                          handleDeploymentModalChange('supplierName', e.target.value);
                          setShowSupplierDropdown(true);
                        }}
                        onFocus={() => setShowSupplierDropdown(true)}
                        onBlur={() => setTimeout(() => setShowSupplierDropdown(false), 220)}
                        placeholder="e.g. Staffing Supplier / C2C Employer"
                        autoComplete="off"
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '0.85rem',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                      {showSupplierDropdown && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          zIndex: 1050,
                          maxHeight: '190px',
                          overflowY: 'auto',
                          backgroundColor: '#ffffff',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)',
                          marginTop: '2px'
                        }}>
                          {getFilteredCrmCompanies(deploymentModalData.supplierName).length > 0 ? (
                            getFilteredCrmCompanies(deploymentModalData.supplierName).map((c, idx) => (
                              <div
                                key={c.id || idx}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setDeploymentModalData(prev => ({
                                    ...prev,
                                    supplierId: c.id,
                                    supplierName: c.displayName
                                  }));
                                  setShowSupplierDropdown(false);
                                }}
                                style={{
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f1f5f9',
                                  fontSize: '0.82rem',
                                  display: 'flex',
                                  justifyContent: 'space-between'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                              >
                                <span style={{ fontWeight: 600, color: '#1f2937' }}>{c.displayName}</span>
                                {c.companyName && <span style={{ color: '#64748b', fontSize: '0.75rem' }}>{c.companyName}</span>}
                              </div>
                            ))
                          ) : (
                            <div style={{ padding: '8px 12px', fontSize: '0.78rem', color: '#94a3b8' }}>
                              Type to enter supplier name
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* 2. Timeline & Status Section */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                    <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#3b5bfd', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      2. Timeline & Deployment Status
                    </span>
                    <span style={{ height: '1px', flex: 1, background: '#e2e8f0' }} />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                        Start Date *
                      </label>
                      <TypableDatePicker
                        value={deploymentModalData.startDate}
                        onChange={(val) => handleDeploymentModalChange('startDate', val)}
                        placeholder="YYYY-MM-DD or MM/DD/YYYY"
                      />
                    </div>

                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#334155' }}>
                          End Date
                        </label>
                        <label style={{ fontSize: '0.75rem', color: '#3b5bfd', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <input
                            type="checkbox"
                            checked={deploymentModalData.isCurrent}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setDeploymentModalData(prev => ({
                                ...prev,
                                isCurrent: checked,
                                endDate: checked ? '' : prev.endDate
                              }));
                            }}
                          />
                          Present
                        </label>
                      </div>
                      <TypableDatePicker
                        disabled={deploymentModalData.isCurrent}
                        value={deploymentModalData.isCurrent ? '' : deploymentModalData.endDate}
                        onChange={(val) => handleDeploymentModalChange('endDate', val)}
                        placeholder="YYYY-MM-DD or MM/DD/YYYY"
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                        Deployment Status
                      </label>
                      <select
                        value={deploymentModalData.status}
                        onChange={(e) => handleDeploymentModalChange('status', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '7px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '0.84rem',
                          outline: 'none',
                          background: '#ffffff',
                          boxSizing: 'border-box'
                        }}
                      >
                        <option value="ACTIVE">ACTIVE</option>
                        <option value="COMPLETED">COMPLETED</option>
                        <option value="TERMINATED">TERMINATED</option>
                        <option value="ON_HOLD">ON_HOLD</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* 3. Rate & Commercial Structure */}
                <div style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  padding: '18px 20px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                    <DollarSign size={18} color="#3b5bfd" />
                    <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#3b5bfd', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      3. Rate & Commercial Details
                    </span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px', marginBottom: '14px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                        Client Rate ($/hr)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={deploymentModalData.clientRate}
                        onChange={(e) => handleDeploymentModalChange('clientRate', e.target.value)}
                        placeholder="0.00"
                        style={{
                          width: '100%',
                          padding: '6px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '0.84rem',
                          outline: 'none',
                          background: '#ffffff',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                        VMS (%)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={deploymentModalData.vmsPct}
                        onChange={(e) => handleDeploymentModalChange('vmsPct', e.target.value)}
                        placeholder="0.00"
                        style={{
                          width: '100%',
                          padding: '6px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '0.84rem',
                          outline: 'none',
                          background: '#ffffff',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                        Rate After VMS ($/hr)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={deploymentModalData.rateAfterVms}
                        onChange={(e) => handleDeploymentModalChange('rateAfterVms', e.target.value)}
                        placeholder="0.00"
                        style={{
                          width: '100%',
                          padding: '6px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '0.84rem',
                          outline: 'none',
                          background: '#f1f5f9',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px', marginBottom: '14px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                        Supplier Rate ($/hr)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={deploymentModalData.supplierRate}
                        onChange={(e) => handleDeploymentModalChange('supplierRate', e.target.value)}
                        placeholder="0.00"
                        style={{
                          width: '100%',
                          padding: '6px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '0.84rem',
                          outline: 'none',
                          background: '#ffffff',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                        Supplier VMS (%)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={deploymentModalData.supplierVmsPct}
                        onChange={(e) => handleDeploymentModalChange('supplierVmsPct', e.target.value)}
                        placeholder="0.00"
                        style={{
                          width: '100%',
                          padding: '6px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '0.84rem',
                          outline: 'none',
                          background: '#ffffff',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                        Compliance Pay Rate ($/hr)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={deploymentModalData.compliancePayRate}
                        onChange={(e) => handleDeploymentModalChange('compliancePayRate', e.target.value)}
                        placeholder="0.00"
                        style={{
                          width: '100%',
                          padding: '6px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '0.84rem',
                          outline: 'none',
                          background: '#ffffff',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>

                  {/* Actual Pay Rate Highlighted */}
                  <div style={{
                    background: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderRadius: '8px',
                    padding: '10px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px'
                  }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#15803d' }}>
                        Actual Pay Rate ($/hr) *
                      </label>
                      <span style={{ fontSize: '0.72rem', color: '#16a34a' }}>Net pay rate directly received by consultant / candidate</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      value={deploymentModalData.actualPayRate}
                      onChange={(e) => handleDeploymentModalChange('actualPayRate', e.target.value)}
                      placeholder="0.00"
                      style={{
                        width: '140px',
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: '1px solid #86efac',
                        fontSize: '0.9rem',
                        fontWeight: 700,
                        color: '#15803d',
                        outline: 'none',
                        background: '#ffffff',
                        textAlign: 'right'
                      }}
                    />
                  </div>

                  {/* Rate & Commercial Notes Field */}
                  <div style={{ marginTop: '14px' }}>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#334155', marginBottom: '5px' }}>
                      Rate & Commercial Notes
                    </label>
                    <textarea
                      rows={2}
                      value={deploymentModalData.rateNotes || ''}
                      onChange={(e) => handleDeploymentModalChange('rateNotes', e.target.value)}
                      placeholder="Add notes regarding rates, billing terms, discounts, or commercial exceptions..."
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '0.84rem',
                        outline: 'none',
                        background: '#ffffff',
                        boxSizing: 'border-box',
                        resize: 'vertical',
                        fontFamily: 'inherit'
                      }}
                    />
                  </div>
                </div>

                {/* 4. Referral Details */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                    <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#3b5bfd', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      4. Referral Source & Fee
                    </span>
                    <span style={{ height: '1px', flex: 1, background: '#e2e8f0' }} />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
                    {/* Referral Source Autocomplete */}
                    <div style={{ position: 'relative' }}>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                        Referral Source (Company / Individual)
                      </label>
                      <input
                        type="text"
                        value={deploymentModalData.referralSource}
                        onChange={(e) => {
                          handleDeploymentModalChange('referralSource', e.target.value);
                          setShowModalReferralDropdown(true);
                        }}
                        onFocus={() => setShowModalReferralDropdown(true)}
                        onBlur={() => setTimeout(() => setShowModalReferralDropdown(false), 220)}
                        placeholder="Search CRM companies or type person/agency"
                        autoComplete="off"
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '0.85rem',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                      {showModalReferralDropdown && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          zIndex: 1050,
                          maxHeight: '190px',
                          overflowY: 'auto',
                          backgroundColor: '#ffffff',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)',
                          marginTop: '2px'
                        }}>
                          {getFilteredCrmCompanies(deploymentModalData.referralSource).length > 0 ? (
                            getFilteredCrmCompanies(deploymentModalData.referralSource).map((c, idx) => (
                              <div
                                key={c.id || idx}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setDeploymentModalData(prev => ({ ...prev, referralSource: c.displayName }));
                                  setShowModalReferralDropdown(false);
                                }}
                                style={{
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f1f5f9',
                                  fontSize: '0.82rem',
                                  display: 'flex',
                                  justifyContent: 'space-between'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                              >
                                <span style={{ fontWeight: 600, color: '#1f2937' }}>{c.displayName}</span>
                                {c.companyName && <span style={{ color: '#64748b', fontSize: '0.75rem' }}>{c.companyName}</span>}
                              </div>
                            ))
                          ) : (
                            <div style={{ padding: '8px 12px', fontSize: '0.78rem', color: '#94a3b8' }}>
                              Type to enter custom referral source
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                        Referral Fee ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={deploymentModalData.referralFee}
                        onChange={(e) => handleDeploymentModalChange('referralFee', e.target.value)}
                        placeholder="0.00"
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '0.85rem',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* 5. Project Description & Summary */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                    <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#3b5bfd', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      5. Project Description & Summary
                    </span>
                    <span style={{ height: '1px', flex: 1, background: '#e2e8f0' }} />
                  </div>

                  <textarea
                    rows={4}
                    value={deploymentModalData.notes}
                    onChange={(e) => handleDeploymentModalChange('notes', e.target.value)}
                    placeholder="Enter project summary, technical environment, deliverables, or responsibilities..."
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      fontSize: '0.85rem',
                      lineHeight: 1.5,
                      fontFamily: 'inherit',
                      boxSizing: 'border-box',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>

              {/* Modal Footer */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '16px 26px',
                  borderTop: '1px solid #e2e8f0',
                  background: '#f8fafc'
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowDeploymentModal(false)}
                  disabled={savingDeploymentModal}
                  style={{
                    padding: '8px 18px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    color: '#475569',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveDeploymentModal}
                  disabled={savingDeploymentModal}
                  style={{
                    padding: '8px 22px',
                    borderRadius: '6px',
                    border: 'none',
                    background: '#3b5bfd',
                    color: '#ffffff',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    cursor: savingDeploymentModal ? 'not-allowed' : 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    opacity: savingDeploymentModal ? 0.75 : 1,
                    boxShadow: '0 1px 3px rgba(15, 118, 110, 0.3)'
                  }}
                >
                  <CheckCircle2 size={16} />
                  {savingDeploymentModal
                    ? 'Saving...'
                    : (isEditingDeployment ? 'Save Deployment Changes' : 'Create Deployment')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeDetailsPage;